import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Login from './Login';
import AddTopicModal from './AddTopicModal';
import EditTopicModal from './EditTopicModal';
import ConfirmModal from './ConfirmModal';
import NewConceptModal from './NewConceptModal';
import SelectKnowledgeLevelModal from './SelectKnowledgeLevelModal';
import RateConceptModal from './RateConceptModal';
import UserProfileModal from './UserProfileModal';
import SettingsModal from './SettingsModal';
import SessionTakeoverModal from './components/SessionTakeoverModal';
import ReviewQuestionModal from './ReviewQuestionModal';
import GuidedTutorial from './components/GuidedTutorial';
import ResponsiveWarningModal from './ResponsiveWarningModal';
import FeedbackModal from './FeedbackModal';
import TutorialMode from './components/TutorialMode';
import TutorialButton from './components/TutorialButton';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import { generateDummyData } from './utils/tutorialDummyData';
import { getUserSession, clearUserSession } from './utils/cookies';
import { getTranslation } from './languages';
import { API_ENDPOINTS, REVIEW_SCHEDULE } from './config';
import { v4 as uuidv4 } from 'uuid';
import sessionManager from './utils/sessionManager';
import { renderMarkdown } from './utils/markdownUtils';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
    DragOverlay,
    useDndContext,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers';


import { Plus, Send, User, Bot, BookOpen, MessageSquarePlus, Filter, MoreVertical, Trash2, LogOut, Settings, Heart, Clock, Sparkles, CheckCircle, Folder, Edit2, X, Check, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { fetchWithRetry, RateLimitError, setRateLimitStatus, clearRateLimitStatus } from './utils/rateLimitHandler';
import { SSEConnectionManager } from './utils/sseConnection';
import ConnectionStatus from './components/ConnectionStatus';

/**
 * --- CHANGE LOG ---
 * - Added `userId` to the state to manage the current user session.
 * - All API calls are now made through a new `authenticatedFetch` helper function which automatically adds the `X-User-ID` header.
 * - The `onLoginSuccess` handler now receives and stores the `userId`.
 * - The entire application is now driven by the logged-in user's data.
 * - Added description parameter to handleAddTopic function to support detailed user goals.
 */

// Lazy load Tone.js only when needed
const loadToneJS = () => {
    return new Promise((resolve, reject) => {
        if (window.Tone) {
            resolve(window.Tone);
            return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/tone/14.8.49/Tone.js';
        script.onload = () => resolve(window.Tone);
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const playSound = async (type) => {
    if (type === 'newConcept') {
        // Try to play the audio file first
        try {
            const audio = new Audio('/tip_ready.wav');
            audio.volume = 0.5;
            
            // Request user permission for notifications if not already granted
            if (Notification.permission === 'default') {
                Notification.requestPermission();
            }
            
            // Show notification if permission is granted
            if (Notification.permission === 'granted') {
                new Notification('New Concept Ready!', {
                    body: 'A new learning concept has been generated for you.',
                    icon: '/logo192.png',
                    silent: false // This will play the system notification sound
                });
            } else {
            }
            
            // Try to play the audio file
            audio.play().catch(async (error) => {
                // Fallback to Web Audio API if file fails
                try {
                    const Tone = await loadToneJS();
                    await Tone.start();
                    const synth = new Tone.Synth().toDestination();
                    const now = Tone.now();
                    synth.triggerAttackRelease("E5", "16n", now);
                    synth.triggerAttackRelease("G5", "16n", now + 0.1);
                    synth.triggerAttackRelease("C6", "16n", now + 0.2);
                } catch (toneError) { 
 
                }
            });
        } catch (error) {
        }
    } else if (type === 'newMessage') {
        try {
            const Tone = await loadToneJS();
            await Tone.start();
            const synth = new Tone.Synth().toDestination();
            synth.triggerAttackRelease("C5", "8n", Tone.now());
        } catch (error) { }
    }
};

const statusOptions = [
    { value: 'easy', label: 'tooEasy' }, 
    { value: 'known', label: 'alreadyKnown' },
    { value: 'partial', label: 'partiallyKnow' }, 
    { value: 'new', label: 'newToMe' },
    { value: 'hard', label: 'tooHard' }, 
    { value: 'skip', label: 'notWhatLookingFor' },
    { type: 'separator' },
    { value: 'hidden', label: 'showHiddenTips' },
];

// Droppable Group Component
function DroppableGroup({ groupId, children, customGroupedTopics, t }) {
    // Determine if the active item belongs to this group
    const { active, over } = useDndContext();
    let activeItemBelongsToThisGroup = false;
    if (active && !active.data.current?.type) {
        // It's a topic being dragged
        const group = customGroupedTopics?.find(g => g.id === groupId);
        if (group) {
            activeItemBelongsToThisGroup = group.topics.some(t => t.id === active.id);
        }
    }
    
    // Register as droppable - allow dropping from any group including same group
    const { setNodeRef, isOver } = useDroppable({
        id: groupId,
        disabled: false // Always enable to allow reordering within same group
    });
    
    // Check if we're hovering over this group in various ways
    let isGroupOver = isOver;
    
    // Also check if we're over a topic that belongs to this group (only for different group)
    if (!isGroupOver && over && over.id.toString().startsWith('topic_') && !activeItemBelongsToThisGroup) {
        const group = customGroupedTopics?.find(g => g.id === groupId);
        if (group && group.topics.some(t => t.id === over.id)) {
            isGroupOver = true;
        }
    }
    
    // Only show drop indicator when dragging a topic from a different group
    const showDropIndicator = isGroupOver && active && !active.data.current?.type && !activeItemBelongsToThisGroup;
    
    
    return (
        <div 
            ref={setNodeRef}
            className="relative"
            data-group-id={groupId}
        >
            {/* Invisible overlay that captures drop events across the entire group area */}
            {showDropIndicator && (
                <div className="absolute inset-0 z-20 pointer-events-none">
                    <div className="h-full w-full bg-sky-100/40 border-2 border-dashed border-sky-400 rounded-lg animate-pulse" />
                </div>
            )}
            
            {/* Content wrapper */}
            <div className={`relative min-h-[100px] p-2 rounded-lg transition-all duration-200 ${
                showDropIndicator ? 'bg-sky-50/50' : ''
            }`}>
                {children}
                
                {/* Empty state */}
                {React.Children.count(children) === 0 && (
                    <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
                        {showDropIndicator ? (
                            <span className="text-sky-600 font-medium">{t('dropTopicHere')}</span>
                        ) : (
                            <span>{t('noTopicsInGroup')}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Group Header Component
function GroupHeader({ group, children, attributes, listeners }) {
    return (
        <div 
            className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm group cursor-move"
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}

// Sortable Group Component for dragging entire groups
function SortableGroupContainer({ 
    group, 
    groupIndex,
    editingGroupId,
    editingGroupName,
    setEditingGroupName,
    handleUpdateTopicGroup,
    setEditingGroupId,
    groupDropdownOpen,
    setGroupDropdownOpen,
    groupDropdownRef,
    t,
    setConfirmModalState,
    handleDeleteTopicGroup,
    toggleGroupDropdown,
    dropdownPositions,
    tutorialTarget,
    children
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
        active,
        over,
    } = useSortable({ 
        id: `group-sort-${group.id}`,
        data: {
            type: 'group',
            group: group
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 50 : 'auto',
    };
    
    // Check if another group is being dragged over this one
    const isOverlay = over?.id === `group-sort-${group.id}` && active?.id !== `group-sort-${group.id}`;

    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            className={`${groupIndex > 0 ? 'mt-4' : ''} ${isDragging ? 'relative shadow-2xl rounded-lg' : ''} ${isOverlay ? 'border-t-4 border-sky-400' : ''}`}
            data-tutorial-target={tutorialTarget}
        >
            <GroupHeader group={group} attributes={attributes} listeners={listeners}>
                <div
                    onContextMenu={(e) => {
                        e.preventDefault();
                        toggleGroupDropdown(group.id, e);
                    }}
                >
                    {editingGroupId === group.id ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={editingGroupName}
                                onChange={(e) => setEditingGroupName(e.target.value)}
                                onKeyDown={(e) => {
                                    // Stop propagation to prevent drag-and-drop from intercepting keyboard events
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                        handleUpdateTopicGroup(group.id, editingGroupName);
                                    } else if (e.key === 'Escape') {
                                        setEditingGroupId(null);
                                        setEditingGroupName('');
                                    }
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-400"
                                autoFocus
                            />
                            <button
                                onClick={() => handleUpdateTopicGroup(group.id, editingGroupName)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                                <Check size={14} />
                            </button>
                            <button
                                onClick={() => {
                                    setEditingGroupId(null);
                                    setEditingGroupName('');
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Folder size={16} className="text-sky-500" />
                            {group.name}
                            <span className="text-xs font-normal text-gray-500">({group.topics.length} {group.topics.length === 1 ? t('topicSingular') : t('topicPlural')})</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGroupDropdown(group.id, e);
                                }}
                                className="ml-auto opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200"
                            >
                                <MoreVertical size={14} />
                            </button>
                        </h3>
                    )}
                    {groupDropdownOpen === group.id && (
                        <div ref={groupDropdownRef} className="absolute right-4 z-[100] w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1" style={{ ...(dropdownPositions[`group-${group.id}`] || { top: '32px', bottom: 'auto' }) }}>
                            <button
                                onClick={() => {
                                    setEditingGroupId(group.id);
                                    setEditingGroupName(group.name);
                                    setGroupDropdownOpen(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                            >
                                <Edit2 size={14} />
                                {t('rename') || 'Rename'}
                            </button>
                            <button
                                onClick={() => {
                                    setConfirmModalState({
                                        isOpen: true,
                                        title: t('deleteGroupTitle') || 'Delete Group',
                                        message: t('deleteGroupMessage') || `Are you sure you want to delete the group "${group.name}"? Topics will be moved to Ungrouped.`,
                                        onConfirm: () => {
                                            handleDeleteTopicGroup(group.id);
                                            setConfirmModalState({ isOpen: false });
                                        },
                                        confirmText: t('delete') || 'Delete',
                                        cancelText: t('cancel') || 'Cancel'
                                    });
                                    setGroupDropdownOpen(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                                <Trash2 size={14} />
                                {t('delete') || 'Delete'}
                            </button>
                        </div>
                    )}
                </div>
            </GroupHeader>
            {children}
        </div>
    );
}

// Sortable Topic Item Component
function SortableTopicItem({ topic, isDeleting, isGenerating, isDisabled, hasUnreadMark, 
                           topicHasReviewDue, selectedTopicId, setSelectedTopicId, 
                           toggleTopicDropdown, topicDropdownOpen, topicDropdownRef,
                           handleUpdateTopicFavorite, handleOpenEditModal, t, 
                           setConfirmModalState, handleDeleteTopic, uiLanguage,
                           setTopicDropdownOpen, dropdownPositions }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
        isSorting,
        over,
        active,
    } = useSortable({
        id: topic.id,
        animateLayoutChanges: () => true // Always animate layout changes
    });
    
    // Debug logging
    React.useEffect(() => {
        if (isDragging || isSorting || transform) {
        }
    }, [isDragging, isSorting, transform, over, active, topic.id]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 200ms ease',
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto', // Higher z-index when dragging to show above other elements
    };
    
    // Additional debug for transform
    if (transform?.y || transform?.x) {
    }


    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            className={`group relative ${isDragging ? 'cursor-grabbing' : ''}`}
            data-sortable-id={topic.id}
        >
            {/* Unread mark in upper left */}
            {hasUnreadMark && (
                <div className="absolute top-1 left-3.5 w-2 h-2 bg-sky-500 rounded-full z-[5]"></div>
            )}
            {/* Review due orange dot */}
            {topicHasReviewDue(topic.id) && (
                <div className="absolute top-1 left-6 w-2 h-2 bg-orange-500 rounded-full z-[5]"></div>
            )}
            <div
                onClick={() => !isDisabled && setSelectedTopicId(topic.id)}
                onContextMenu={(e) => {
                    if (!isDisabled) {
                        e.preventDefault();
                        toggleTopicDropdown(topic.id, e);
                    }
                }}
                className={`p-3 m-2 rounded-lg cursor-pointer flex justify-between items-center transition-colors duration-150 ${selectedTopicId === topic.id ? 'bg-sky-100 text-sky-800' : 'hover:bg-gray-100'} ${isDisabled ? 'opacity-60 pointer-events-none cursor-not-allowed grayscale' : ''}`}
                style={isDisabled ? { position: 'relative' } : {}}
                data-tutorial-target={topic.id === 'dummy-topic-1' ? 'topic-card-dummy-1' : 'topic-card'}
            >
                <div className="flex items-center flex-grow" {...attributes} {...listeners}>
                    <span className={`${selectedTopicId === topic.id ? 'font-semibold' : ''}`}>{topic.name}</span>
                    {topic.isFavorite && (
                        <Heart size={14} className="fill-current text-red-500 ml-2 flex-shrink-0" />
                    )}
                </div>
                {isDisabled ? (
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin ml-2"></div>
                ) : (
                    <button onClick={(e) => toggleTopicDropdown(topic.id, e)} className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-gray-200 text-gray-500">
                        <MoreVertical size={16} />
                    </button>
                )}
            </div>
            {topicDropdownOpen === topic.id && (
                <div 
                    ref={topicDropdownRef}
                    className="absolute right-0 z-30 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
                    style={{ ...(dropdownPositions[`topic-${topic.id}`] || { top: '32px', bottom: 'auto' }) }}
                    onMouseEnter={() => {}}
                >
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateTopicFavorite(topic.id, !topic.isFavorite);
                            setTopicDropdownOpen(null);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                    >
                        <Heart size={14} className={topic.isFavorite ? "fill-current text-red-500" : ""} />
                        {topic.isFavorite ? t('unfavoriteTopic') : t('favoriteTopic')}
                    </button>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(topic);
                            setTopicDropdownOpen(null);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                    >
                        <Settings size={14} />
                        {t('editTopic')}
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            // Use the unified translation key for the title
                            setConfirmModalState({
                                isOpen: true,
                                title: t('deleteConfirmTitle'),
                                message:
                                    uiLanguage === 'zh-CN' ? (
                                        <>
                                            确定要删除主题 <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{topic.name}</span> 吗？此操作无法撤销。
                                        </>
                                    ) : uiLanguage === 'ja' ? (
                                        <>
                                            トピック <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{topic.name}</span> を削除してもよろしいですか？この操作は元に戻せません。
                                        </>
                                    ) : (
                                        <>
                                            Are you sure you want to delete the topic <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{topic.name}</span>? This action cannot be undone.
                                        </>
                                    ),
                                confirmText: t('delete'),
                                cancelText: t('cancel'),
                                onConfirm: () => {
                                    setConfirmModalState((prev) => ({ ...prev, isOpen: false }));
                                    handleDeleteTopic(topic.id);
                                },
                                onCancel: () => setConfirmModalState((prev) => ({ ...prev, isOpen: false })),
                            });
                            setTopicDropdownOpen(null);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                        <Trash2 size={14} />
                        {t('delete')}
                    </button>
                </div>
            )}
        </div>
    );
}

function App() {
    // Get tutorial context
    const tutorialContext = useTutorial();
    const isTutorialMode = tutorialContext?.isTutorialMode || false;
    
    // Detect system language
    const detectSystemLanguage = () => {
        const systemLang = navigator.language || navigator.userLanguage;
        const supportedLanguages = ['en', 'zh-CN', 'ja'];
        
        // Check for exact match
        if (supportedLanguages.includes(systemLang)) {
            return systemLang;
        }
        
        // Check for language code match (e.g., 'zh' for 'zh-CN')
        const langCode = systemLang.split('-')[0];
        if (langCode === 'zh') return 'zh-CN';
        if (langCode === 'ja') return 'ja';
        
        // Default to English
        return 'en';
    };

    // Session State
    const [userId, setUserId] = useState(null);
    const [username, setUsername] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [uiLanguage, setUILanguage] = useState(detectSystemLanguage());
    const [googleUserData, setGoogleUserData] = useState(null);
    const [showSessionTakeoverModal, setShowSessionTakeoverModal] = useState(false);
    const [pendingSession, setPendingSession] = useState(null);
    
    // UI State
    const [loadingMessage, setLoadingMessage] = useState("Loading Data...");
    const [verificationMessage, setVerificationMessage] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [isAddTopicModalOpen, setIsAddTopicModalOpen] = useState(false);
    const [isEditTopicModalOpen, setIsEditTopicModalOpen] = useState(false);
    const [isNewConceptModalOpen, setIsNewConceptModalOpen] = useState(false);
    const [isSelectKnowledgeLevelModalOpen, setIsSelectKnowledgeLevelModalOpen] = useState(false);
    const [isRateConceptModalOpen, setIsRateConceptModalOpen] = useState(false);
    const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isReviewQuestionModalOpen, setIsReviewQuestionModalOpen] = useState(false);
    const [showGuidedTutorial, setShowGuidedTutorial] = useState(false);
    const [showResponsiveWarning, setShowResponsiveWarning] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isTestDeployment, setIsTestDeployment] = useState(false);
    const [reviewModalInitialSlide, setReviewModalInitialSlide] = useState(0);
    const [isGenerateReviewModalOpen, setIsGenerateReviewModalOpen] = useState(false);
    const [selectedReviewDay, setSelectedReviewDay] = useState(1);
    const [isGeneratingReviewQuestion, setIsGeneratingReviewQuestion] = useState(false);
    const [confirmModalState, setConfirmModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {}, confirmText: 'Confirm', cancelText: 'Cancel' });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [conceptDropdownOpen, setConceptDropdownOpen] = useState(null);
    const [topicDropdownOpen, setTopicDropdownOpen] = useState(null);
    const [dropdownPositions, setDropdownPositions] = useState({});
    const [deleteConfirmModalState, setDeleteConfirmModalState] = useState({ isOpen: false, tipId: null, tipTitle: '', onConfirm: () => {} });

    // Data State
    const [realAppData, setRealAppData] = useState({ topics: [], tips: {}, conversations: {}, topicGroups: [] });
    const [selectedTopicId, setSelectedTopicId] = useState(null);
    const [selectedTipId, setSelectedTipId] = useState(null);
    
    // Store pre-tutorial selections to restore them when exiting tutorial
    const preTutorialSelections = useRef({ topicId: null, tipId: null });
    
    // Save current selections when entering tutorial mode
    useEffect(() => {
        if (isTutorialMode) {
            // Just entered tutorial mode - save current selections
            if (preTutorialSelections.current.topicId === null && preTutorialSelections.current.tipId === null) {
                preTutorialSelections.current = {
                    topicId: selectedTopicId,
                    tipId: selectedTipId
                };
            }
        } else {
            // Just exited tutorial mode - restore previous selections
            if (preTutorialSelections.current.topicId !== null || preTutorialSelections.current.tipId !== null) {
                setSelectedTopicId(preTutorialSelections.current.topicId);
                setSelectedTipId(preTutorialSelections.current.tipId);
                // Clear the saved selections
                preTutorialSelections.current = { topicId: null, tipId: null };
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTutorialMode]); // Only depend on tutorial mode change
    
    // Use dummy data when in tutorial mode
    const dummyData = React.useMemo(() => {
        if (isTutorialMode) {
            const data = generateDummyData(uiLanguage);
            return {
                topics: data.topics,
                tips: {
                    'dummy-topic-1': data.tips.filter(t => t.topicId === 'dummy-topic-1'),
                    'dummy-topic-2': data.tips.filter(t => t.topicId === 'dummy-topic-2'),
                    'dummy-topic-3': data.tips.filter(t => t.topicId === 'dummy-topic-3')
                },
                conversations: (() => {
                    // Find the conversation objects and extract their messages
                    const conv1 = data.conversations.find(c => c.tipId === 'dummy-tip-1');
                    const conv2 = data.conversations.find(c => c.tipId === 'dummy-tip-2');
                    
                    return {
                        'dummy-tip-1': conv1 ? conv1.messages : [],
                        'dummy-tip-2': conv2 ? conv2.messages : []
                    };
                })(),
                topicGroups: data.topicGroups || [],
                reviewQuestions: data.reviewQuestions || []
            };
        }
        return null;
    }, [isTutorialMode, uiLanguage]);
    
    const appData = isTutorialMode && dummyData ? dummyData : realAppData;
    const setAppData = React.useMemo(() => {
        return isTutorialMode ? () => {} : setRealAppData;
    }, [isTutorialMode]);
    const [newlyGeneratedConcept, setNewlyGeneratedConcept] = useState(null);
    const [conceptForKnowledgeLevel, setConceptForKnowledgeLevel] = useState(null);
    const [statusFilters, setStatusFilters] = useState([]);
    const [topicFilters, setTopicFilters] = useState([]);
    const [reviewDueFilter, setReviewDueFilter] = useState(false);
    const [topicReviewDueFilter, setTopicReviewDueFilter] = useState(false);
    const [topicScheduledFilter, setTopicScheduledFilter] = useState(null); // 'scheduled', 'unscheduled', or null
    const [isTopicFilterOpen, setIsTopicFilterOpen] = useState(false);
    const [topicToEdit, setTopicToEdit] = useState(null);
    const [lastTipUpdate, setLastTipUpdate] = useState(0);
    
    // Topic Groups State
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [groupDropdownOpen, setGroupDropdownOpen] = useState(null);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState('');
    const [tipsForSelectedTopic, setTipsForSelectedTopic] = useState([]);
    
    // Drag and Drop State
    const [activeItem, setActiveItem] = useState(null);

    // Unread Feature State
    const [unreadConcepts, setUnreadConcepts] = useState(new Set());
    const [unreadTopics, setUnreadTopics] = useState(new Set());
    // Track which tips have already shown the follow-up modal
    const [shownFollowUpModalFor, setShownFollowUpModalFor] = useState(new Set());

    // Interaction State
    const [userInput, setUserInput] = useState('');
    const [chattingTipId, setChattingTipId] = useState(null); // Track which tip is currently chatting

    // Three-State Feature State
    const [mainAreaState, setMainAreaStateRaw] = useState('tip-only'); // 'tip-only', 'full', 'chat-only'
    const [hasConversationHistory, setHasConversationHistory] = useState(false);
    
    // Protected setMainAreaState that prevents changes during tutorial mode
    const setMainAreaState = useCallback((newState) => {
        // During tutorial mode, always keep it in 'full' view
        if (isTutorialMode) {
            setMainAreaStateRaw('full');
        } else {
            setMainAreaStateRaw(newState);
        }
    }, [isTutorialMode]);

    // Request Tracking State
    const [pendingRequests, setPendingRequests] = useState(new Set());
    const [forceRefetch, setForceRefetch] = useState(0);

    // Refs
    const conversationEndRef = useRef(null);
    const prevTopicIdRef = useRef();
    const filterRef = useRef(null);
    const conceptDropdownRef = useRef(null);
    const topicDropdownRef = useRef(null);
    const groupDropdownRef = useRef(null);
    const topicFilterRef = useRef(null);
    const topicFilterButtonRef = useRef(null);
    const selectedTopicIdRef = useRef(selectedTopicId);
    const sseConnectionRef = useRef(null);
    const reviewChatUpdateCallbackRef = useRef(null);
    const chatInputRef = useRef(null);
    const conceptContentAreaRef = useRef();
    const conceptContentAreaRef2 = useRef();
    const conversationAreaRef = useRef();

    // The 6 statuses that count as "read" - wrapped in useMemo to prevent recreation
    const READ_KNOWLEDGE_STATUSES = useMemo(() => new Set(['easy', 'known', 'partial', 'new', 'hard', 'skip']), []);

    // Helper function to safely check if a topic exists
    const topicExists = useCallback((topicId) => {
        return appData.topics.some(topic => topic.id === topicId);
    }, [appData.topics]);

    // Track server start time
    const [serverStartTime, setServerStartTime] = useState(null);
    const [notification, setNotification] = useState(null);
    
    // Connection status management
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [connectionStatusData, setConnectionStatusData] = useState(null);
    
    // Slow chat notification state
    const [showSlowChatNotification, setShowSlowChatNotification] = useState(false);
    const slowChatTimeoutRef = useRef(null);

    // Simple notification system
    const showNotification = useCallback((message, type = 'info') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    }, []);

    // Translation helper
    const t = useCallback((key, params = {}) => getTranslation(key, uiLanguage, params), [uiLanguage]);

    // Check server health and detect restarts
    const checkServerHealth = useCallback(async () => {
        try {
            const response = await fetch('/api/health');
            if (response.ok) {
                const data = await response.json();
                const newServerStartTime = data.serverStartTime;
                
                // Set test deployment flag
                if (data.isTestDeployment !== undefined) {
                    setIsTestDeployment(data.isTestDeployment);
                }
                
                // If we have a previous start time and it's different, server restarted
                if (serverStartTime && newServerStartTime !== serverStartTime) {
                    
                    // Force refresh data to get updated generation flags
                    setForceRefetch(prev => prev + 1);
                    
                    // Show notification to user
                    showNotification('Server restarted. Refreshing data...', 'info');
                }
                
                setServerStartTime(newServerStartTime);
            }
        } catch (error) {
        }
    }, [serverStartTime, showNotification]);

    // Helper for making authenticated API calls
    const authenticatedFetch = useCallback(async (url, options = {}) => {
        if (!userId) {
            throw new Error(t('userNotAuthenticated'));
        }
        
        // Get session token from localStorage or cookies
        const sessionId = localStorage.getItem('sessionId') || getUserSession()?.sessionId;
        
        const headers = {
            ...options.headers,
            'Content-Type': 'application/json',
            'X-User-ID': userId, // Keep for backward compatibility
            'X-User-Timezone': new Date().getTimezoneOffset().toString(),
        };
        
        // Add Authorization header if session token exists
        if (sessionId) {
            headers['Authorization'] = `Bearer ${sessionId}`;
        }
        
        // Add timeout for long-running requests (4 minutes to account for backend retries)
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 240000); // 4 minutes
        
        try {
            // Use fetchWithRetry to handle rate limiting
            return await fetchWithRetry(
                async () => {
                    const response = await fetch(url, { 
                        ...options, 
                        headers,
                        signal: controller.signal 
                    });
                    
                    // Handle session expiration
                    if (response.status === 401) {
                        const data = await response.json();
                        if (data.error === 'Invalid or expired session') {
                            // Clear session and redirect to login
                            clearUserSession();
                        setUserId(null);
                        setUsername(null);
                        window.location.reload();
                    }
                }
                
                // Handle rate limiting
                if (response.status === 429) {
                    const retryAfter = response.headers.get('Retry-After');
                    setRateLimitStatus({
                        isLimited: true,
                        retryAfter,
                        message: 'Too many requests. Please wait a moment.'
                    });
                    
                    // Show user-friendly message
                    showNotification(t('rateLimitError'), 'error');
                }
                
                // On successful response, check server health and clear rate limit status
                if (response.ok) {
                    checkServerHealth();
                    clearRateLimitStatus();
                }
                
                return response;
            },
            {
                onRetry: ({ attempt, delay, maxRetries }) => {
                    setLoadingMessage(t('retryingRequest', { seconds: Math.round(delay/1000) }));
                }
            }
        ).catch(error => {
            // If all retries failed, show error to user
            if (error instanceof RateLimitError) {
                showNotification(t('rateLimitExceeded'), 'error');
            } else {
                // If fetch fails (e.g., network error), check if server is down
                
                // Check server health to detect if it restarted
                setTimeout(() => checkServerHealth(), 1000);
            }
            
            throw error;
        });
        } finally {
            clearTimeout(timeout);
        }
    }, [userId, checkServerHealth, t, showNotification]);
    
    // Function to dismiss slow chat notification permanently
    const handleDismissSlowChatNotification = useCallback(async () => {
        setShowSlowChatNotification(false);
        clearTimeout(slowChatTimeoutRef.current);
        
        // Update user profile to hide notification permanently
        try {
            await authenticatedFetch('/api/dismiss-slow-chat-notification', {
                method: 'POST'
            });
            
            // Update local user profile
            setUserProfile(prev => ({
                ...prev,
                hideSlowChatNotification: true
            }));
        } catch (error) {
            console.error('Failed to dismiss notification permanently:', error);
        }
    }, [authenticatedFetch]);

    // Helper to check if a tip is in a READ state
    const isTipRead = useCallback((tip) => {
        return tip && READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus);
    }, [READ_KNOWLEDGE_STATUSES]);

    // Helper to check if a topic has any review-due concepts
    const topicHasReviewDue = useCallback((topicId) => {
        // Handle tutorial mode
        if (isTutorialMode) {
            // All tutorial topics should show review due marker
            return topicId === 'dummy-topic-1' || topicId === 'dummy-topic-2' || topicId === 'dummy-topic-3';
        }
        const tips = appData.tips[topicId] || [];
        return tips.some(tip => tip.reviewIsDue);
    }, [appData.tips, isTutorialMode]);

    // Setup unread topics for tutorial mode and ensure full view
    useEffect(() => {
        if (isTutorialMode) {
            // In tutorial mode, ALL topics should show unread markers
            const tutorialUnreadTopics = new Set(['dummy-topic-1', 'dummy-topic-2', 'dummy-topic-3']);
            setUnreadTopics(tutorialUnreadTopics);
            
            // ALL tips should show as unread in tutorial mode
            const tutorialUnreadConcepts = new Set(['dummy-tip-1', 'dummy-tip-2', 'dummy-tip-3']);
            setUnreadConcepts(tutorialUnreadConcepts);
            
            // ALWAYS set to full view mode during tutorial
            setMainAreaState('full');
            
            // Set default selections to ensure content is visible
            if (!selectedTopicId) {
                setSelectedTopicId('dummy-topic-1');
            }
            if (!selectedTipId) {
                setSelectedTipId('dummy-tip-2'); // Use dummy-tip-2 as it has read status
            }
        }
    }, [isTutorialMode, selectedTopicId, selectedTipId, setMainAreaState]);

    // Handle tutorial step changes - specifically for tip-content and ai-tutor steps
    useEffect(() => {
        if (isTutorialMode && tutorialContext) {
            const currentStepData = tutorialContext.getCurrentStepData?.();
            
            // When we're on the new-concept-modal step, show the modal
            if (currentStepData?.id === 'new-concept-modal') {
                // Ensure we have a topic and tip selected
                if (!selectedTopicId) {
                    setSelectedTopicId('dummy-topic-1');
                }
                if (!selectedTipId) {
                    setSelectedTipId('dummy-tip-1');
                }
                // Show the new concept modal with the dummy tip
                const dummyTip = dummyData?.tips['dummy-topic-1']?.find(t => t.id === 'dummy-tip-1');
                if (dummyTip) {
                    setNewlyGeneratedConcept(dummyTip);
                    setIsNewConceptModalOpen(true);
                }
            } else {
                // Close the modal when leaving this step
                if (isNewConceptModalOpen && newlyGeneratedConcept?.id === 'dummy-tip-1') {
                    setIsNewConceptModalOpen(false);
                    setNewlyGeneratedConcept(null);
                }
            }
            
            // When we're on the tip-content or ai-tutor steps, specific selections may be needed
            if (currentStepData?.id === 'tip-content' || currentStepData?.id === 'ai-tutor' || currentStepData?.id === 'quick-actions') {
                // For these steps, use dummy-tip-1 which is unread
                if (!selectedTopicId) {
                    setSelectedTopicId('dummy-topic-1');
                }
                if (!selectedTipId || selectedTipId !== 'dummy-tip-1') {
                    setSelectedTipId('dummy-tip-1');
                }
            }
            
            // When we're on the tip-actions step, ensure we select a read tip (dummy-tip-2)
            if (currentStepData?.id === 'tip-actions' || currentStepData?.id === 'review-tracking') {
                // Select dummy-topic-1 and dummy-tip-2 (which has knowledgeStatus='partial' so it's read)
                if (selectedTopicId !== 'dummy-topic-1') {
                    setSelectedTopicId('dummy-topic-1');
                }
                if (selectedTipId !== 'dummy-tip-2') {
                    setSelectedTipId('dummy-tip-2');
                }
            }
            
            // When we're on the review-modal step, open the ReviewQuestionModal
            if (currentStepData?.id === 'review-modal') {
                // Ensure we have the right tip selected
                if (selectedTopicId !== 'dummy-topic-1') {
                    setSelectedTopicId('dummy-topic-1');
                }
                if (selectedTipId !== 'dummy-tip-2') {
                    setSelectedTipId('dummy-tip-2');
                }
                // Set initial slide to Review 2 (index 1) for tutorial
                setReviewModalInitialSlide(1);
                // Open the review modal if not already open
                if (!isReviewQuestionModalOpen) {
                    setIsReviewQuestionModalOpen(true);
                }
            } else {
                // Close the review modal when leaving this step (only if it's the tutorial review modal)
                if (isReviewQuestionModalOpen && isTutorialMode) {
                    setIsReviewQuestionModalOpen(false);
                }
            }
            
            // When we're on the scheduled-generation or ai-learning-summary steps, open the EditTopicModal
            if (currentStepData?.id === 'scheduled-generation' || currentStepData?.id === 'ai-learning-summary') {
                // Ensure we have the right topic selected
                if (selectedTopicId !== 'dummy-topic-1') {
                    setSelectedTopicId('dummy-topic-1');
                }
                
                // Find the dummy topic to edit
                const dummyTopic = dummyData?.topics?.find(t => t.id === 'dummy-topic-1');
                if (dummyTopic) {
                    // Only open the modal if it's not already open with the correct topic
                    if (!isEditTopicModalOpen || topicToEdit?.id !== 'dummy-topic-1') {
                        setTopicToEdit(dummyTopic);
                        setIsEditTopicModalOpen(true);
                    }
                }
            } else {
                // Close the edit modal when leaving these steps (only if it's the tutorial edit modal)
                if (isEditTopicModalOpen && topicToEdit?.id === 'dummy-topic-1' && isTutorialMode) {
                    setIsEditTopicModalOpen(false);
                    setTopicToEdit(null);
                }
            }
        }
    }, [isTutorialMode, tutorialContext, tutorialContext?.currentStep, selectedTopicId, selectedTipId, dummyData, isNewConceptModalOpen, newlyGeneratedConcept, isReviewQuestionModalOpen, isEditTopicModalOpen, topicToEdit]);

    // Register modal cleanup callbacks when tutorial is active
    useEffect(() => {
        if (isTutorialMode && tutorialContext?.registerModalCleanup) {
            // Create cleanup function for all tutorial-related modals
            const cleanupModals = () => {
                // Close new concept modal if it's showing a tutorial tip
                if (isNewConceptModalOpen && newlyGeneratedConcept?.id === 'dummy-tip-1') {
                    setIsNewConceptModalOpen(false);
                    setNewlyGeneratedConcept(null);
                }
                
                // Close review question modal if it was opened by tutorial
                if (isReviewQuestionModalOpen) {
                    setIsReviewQuestionModalOpen(false);
                }
                
                // Close edit topic modal if it's showing tutorial topic
                if (isEditTopicModalOpen && topicToEdit?.id === 'dummy-topic-1') {
                    setIsEditTopicModalOpen(false);
                    setTopicToEdit(null);
                }
            };

            // Register the cleanup function
            tutorialContext.registerModalCleanup(cleanupModals);

            // Unregister on cleanup
            return () => {
                if (tutorialContext?.unregisterModalCleanup) {
                    tutorialContext.unregisterModalCleanup(cleanupModals);
                }
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTutorialMode, tutorialContext]); // Removed modal states to prevent infinite loop

    // Request tracking helpers
    const addPendingRequest = useCallback((requestId) => {
        setPendingRequests(prev => new Set([...prev, requestId]));
    }, []);

    const removePendingRequest = useCallback((requestId) => {
        setPendingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
        });
    }, []);

    const isRequestPending = useCallback((requestId) => {
        return pendingRequests.has(requestId);
    }, [pendingRequests]);

    const generateRequestId = useCallback((type, context) => {
        return `${type}-${context}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    // Check if any requests are pending
    const hasPendingRequests = useCallback(() => {
        return pendingRequests.size > 0;
    }, [pendingRequests]);

    // Check if a tip request is pending for a specific topic
    const hasPendingTipRequestForTopic = useCallback((topicId) => {
        if (!topicId) return false;
        // First check the database flag from topic data
        const topic = appData?.topics?.find(t => t.id === topicId);
        if (topic?.isGeneratingTip) {
            return true;
        }
        // Fallback to client-side state for immediate UI updates
        return Array.from(pendingRequests).some(id => id.startsWith(`new-tip-${topicId}-`));
    }, [pendingRequests, appData?.topics]);

    // Check if a specific tip is currently chatting
    const isTipChatting = useCallback((tipId) => {
        // Check local state first
        if (chattingTipId === tipId) {
            return true;
        }
        
        // Check database persistence flag
        if (tipId && appData?.tips) {
            // appData.tips is an object with topicId as keys, so we need to search through all topics
            for (const topicId in appData.tips) {
                const tipsInTopic = appData.tips[topicId] || [];
                const tip = tipsInTopic.find(t => t.id === tipId);
                if (tip && appData?.topics) {
                    const topic = appData.topics.find(t => t.id === tip.topicId);
                    return topic?.isGeneratingResponse || false;
                }
            }
        }
        
        return false;
    }, [chattingTipId, appData?.tips, appData?.topics]);

    // Check if a learning summary request is pending for a specific topic
    const hasPendingSummaryRequestForTopic = useCallback((topicId) => {
        if (!topicId) return false;
        // First check the database flag from topic data
        const topic = appData?.topics?.find(t => t.id === topicId);
        if (topic?.isGeneratingSummary) {
            return true;
        }
        // Fallback to client-side state for immediate UI updates
        return Array.from(pendingRequests).some(id => id.startsWith(`learning-summary-${topicId}-`));
    }, [pendingRequests, appData?.topics]);

    // eslint-disable-next-line no-unused-vars
    const hasPendingChatRequests = useCallback(() => {
        return Array.from(pendingRequests).some(id => id.startsWith('chat-'));
    }, [pendingRequests]);

    // Update document title based on selected language
    useEffect(() => {
        document.title = getTranslation('appTitle', uiLanguage);
    }, [uiLanguage]);

    // Note: renderMarkdown is now imported from utils/markdownUtils.js
    // We create a wrapper to maintain the same function signature
    const renderMarkdownWrapper = (content, useWhiteText = false) => {
        return renderMarkdown(content, { useWhiteText });
    };

    // Derived State
    const topics = useMemo(() => {
        const topicsList = appData.topics || [];
        // Sort topics by display_order if it exists, otherwise maintain original order
        return [...topicsList].sort((a, b) => {
            if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
                return a.displayOrder - b.displayOrder;
            }
            return 0;
        });
    }, [appData.topics]);
    
    const conversation = useMemo(() => 
        selectedTipId ? (appData.conversations[selectedTipId] || []) : [], 
        [selectedTipId, appData.conversations]
    );
    
    const filteredTopics = useMemo(() => {
        // Filter out any undefined or null topics first
        const validTopics = topics.filter(topic => topic && topic.id);
        
        // Apply topic filters
        if (topicFilters.includes('favorites')) {
            return validTopics.filter(topic => topic.isFavorite);
        }
        
        // Apply review due filter for topics
        if (topicReviewDueFilter) {
            return validTopics.filter(topic => {
                const tips = appData.tips[topic.id] || [];
                return tips.some(tip => tip.reviewIsDue);
            });
        }
        
        // Apply scheduled filter for topics
        if (topicScheduledFilter === 'scheduled') {
            return validTopics.filter(topic => topic.isScheduled);
        } else if (topicScheduledFilter === 'unscheduled') {
            return validTopics.filter(topic => !topic.isScheduled);
        }
        
        return validTopics;
    }, [topics, topicFilters, topicReviewDueFilter, topicScheduledFilter, appData.tips]);
    
    // Helper function to get frequency label
    const getFrequencyLabel = (frequencyValue, frequencyUnit) => {
        if (frequencyUnit === 1) { // Daily
            return frequencyValue === 1 ? t('daily') : t('everyNDays', { count: frequencyValue });
        } else if (frequencyUnit === 2) { // Weekly
            return frequencyValue === 1 ? t('weekly') : t('everyNWeeks', { count: frequencyValue });
        } else if (frequencyUnit === 3) { // Monthly
            return frequencyValue === 1 ? t('monthly') : t('everyNMonths', { count: frequencyValue });
        }
        return '';
    };
    
    // Group filtered topics by schedule frequency when showing scheduled topics
    const groupedTopics = useMemo(() => {
        if (topicScheduledFilter !== 'scheduled') {
            return null;
        }
        
        const groups = {};
        
        filteredTopics.forEach(topic => {
            // Calculate frequency in days
            let frequencyInDays;
            if (topic.scheduleFrequencyUnit === 1) { // Daily
                frequencyInDays = topic.scheduleFrequencyValue;
            } else if (topic.scheduleFrequencyUnit === 2) { // Weekly
                frequencyInDays = topic.scheduleFrequencyValue * 7;
            } else if (topic.scheduleFrequencyUnit === 3) { // Monthly
                frequencyInDays = topic.scheduleFrequencyValue * 30;
            }
            
            // Create group key
            const groupKey = `${topic.scheduleFrequencyValue}-${topic.scheduleFrequencyUnit}`;
            
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    frequencyInDays,
                    frequencyValue: topic.scheduleFrequencyValue,
                    frequencyUnit: topic.scheduleFrequencyUnit,
                    topics: []
                };
            }
            
            groups[groupKey].topics.push(topic);
        });
        
        // Sort groups by frequency (most frequent first)
        const sortedGroups = Object.values(groups).sort((a, b) => a.frequencyInDays - b.frequencyInDays);
        
        return sortedGroups;
    }, [filteredTopics, topicScheduledFilter]);
    
    // Group filtered topics by custom groups when no scheduling filter is active
    const customGroupedTopics = useMemo(() => {
        if (topicScheduledFilter !== null) {
            return null;
        }
        
        const topicGroups = appData.topicGroups || [];
        const grouped = {};
        
        // Initialize groups
        topicGroups.forEach(group => {
            grouped[group.id] = {
                ...group,
                topics: []
            };
        });
        
        // Add "Ungrouped" for topics without a group
        grouped['ungrouped'] = {
            id: 'ungrouped',
            name: t('ungrouped') || 'Ungrouped',
            displayOrder: Number.MAX_SAFE_INTEGER,
            topics: []
        };
        
        // Distribute topics into groups
        filteredTopics.forEach(topic => {
            if (topic.groupId && grouped[topic.groupId]) {
                grouped[topic.groupId].topics.push(topic);
            } else {
                grouped['ungrouped'].topics.push(topic);
            }
        });
        
        // Sort groups by display order, filter out empty groups if filters are active
        const hasActiveFilters = topicFilters.length > 0 || topicReviewDueFilter;
        const sortedGroups = Object.values(grouped)
            .filter(group => !hasActiveFilters || group.topics.length > 0)
            .sort((a, b) => a.displayOrder - b.displayOrder);
        
        return sortedGroups;
    }, [filteredTopics, topicScheduledFilter, appData.topicGroups, topicFilters, topicReviewDueFilter, t]);
    
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            // Disable keyboard sensor when an input is focused
            // This prevents space bar from triggering drag-and-drop while typing
            isDisabled: () => {
                const activeElement = document.activeElement;
                return activeElement && (
                    activeElement.tagName === 'INPUT' || 
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.contentEditable === 'true'
                );
            }
        })
    );
    
    // Custom collision detection that prioritizes topic items over group containers
    const customCollisionDetection = useCallback((args) => {
        const { active, droppableContainers, pointerCoordinates } = args;
        
        // If dragging a group, use more sensitive detection
        if (active.data.current?.type === 'group') {
            // Find all sortable group containers
            const candidates = [];
            
            droppableContainers.forEach((container) => {
                if (container.id.toString().startsWith('group-sort-')) {
                    const rect = container.rect.current;
                    if (rect) {
                        candidates.push({
                            id: container.id,
                            rect,
                            // Calculate vertical distance from pointer to container
                            distance: Math.abs(pointerCoordinates.y - (rect.top + rect.height / 2))
                        });
                    }
                }
            });
            
            // Sort by distance and return the closest one
            candidates.sort((a, b) => a.distance - b.distance);
            
            // Return the closest container if it's within a reasonable range (150px)
            if (candidates.length > 0 && candidates[0].distance < 150) {
                return [{ id: candidates[0].id }];
            }
            
            // Otherwise use default closest center
            return closestCenter(args);
        }
        
        // For topics, use closestCenter but filter out group-sort containers
        const collisions = closestCenter(args);
        if (!collisions || collisions.length === 0) return collisions;
        
        // Filter out group-sort containers
        const filteredCollisions = collisions.filter(collision =>
            !collision.id?.toString().startsWith('group-sort-')
        );
        
        return filteredCollisions;
    }, []);
    
    // Handle drag start
    const handleDragStart = (event) => {
        const { active } = event;
        
        // Store the active item data
        if (active.data.current?.type === 'group') {
            setActiveItem({
                type: 'group',
                data: active.data.current.group
            });
        } else {
            // It's a topic
            const topic = filteredTopics.find(t => t.id === active.id) || 
                         customGroupedTopics?.flatMap(g => g.topics).find(t => t.id === active.id);
            setActiveItem({
                type: 'topic',
                data: topic
            });
        }
    };
    
    // Handle drag end
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        // Clear active item
        setActiveItem(null);
        
        if (!over) {
            return;
        }
        
        // Check if we're dragging a group
        if (active.data.current?.type === 'group') {
            // Handle group reordering
            const activeGroupId = active.data.current.group.id;
            const overGroupId = over.data.current?.group?.id || over.id.replace('group-sort-', '');
            
            if (activeGroupId === overGroupId) return;
            
            const oldIndex = customGroupedTopics.findIndex(g => g.id === activeGroupId);
            const newIndex = customGroupedTopics.findIndex(g => g.id === overGroupId);
            
            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedGroups = arrayMove(customGroupedTopics, oldIndex, newIndex);
                
                // Update display orders
                const groupOrders = reorderedGroups
                    .filter(g => g.id !== 'ungrouped')
                    .map((group, index) => ({
                        id: group.id,
                        displayOrder: index + 1
                    }));
                
                // Update local state immediately for smooth UX
                const updatedGroups = appData.topicGroups.map(group => {
                    const order = groupOrders.find(o => o.id === group.id);
                    return order ? { ...group, displayOrder: order.displayOrder } : group;
                });
                
                // Sort by display order
                updatedGroups.sort((a, b) => a.displayOrder - b.displayOrder);
                
                setAppData(prev => ({
                    ...prev,
                    topicGroups: updatedGroups
                }));
                
                // Update server
                try {
                    const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_GROUPS_REORDER, {
                        method: 'PUT',
                        body: JSON.stringify({ groupOrders })
                    });
                    if (!response.ok) {
                        const error = await response.json();
                        // Revert on error by fetching fresh data
                        fetchData();
                        throw new Error(error.error || 'Failed to reorder groups');
                    }
                    // Don't fetch fresh data - let SSE handle sync with other tabs
                } catch (error) {
                    setNotification({
                        type: 'error',
                        message: t('failedToReorderGroups') || 'Failed to reorder groups'
                    });
                }
            }
            return;
        }
        
        // Handle custom groups drag-and-drop
        if (topicScheduledFilter === null && customGroupedTopics) {
            const activeTopic = topics.find(t => t.id === active.id);
            if (!activeTopic) return;
            
            // Find which group the dragged topic belongs to
            let sourceGroup = null;
            let sourceIndex = -1;
            
            for (const group of customGroupedTopics) {
                const index = group.topics.findIndex(t => t.id === active.id);
                if (index !== -1) {
                    sourceGroup = group;
                    sourceIndex = index;
                    break;
                }
            }
            
            if (!sourceGroup) return;
            
            // Find the target - it could be a topic (for reordering) or a group container
            let targetGroup = null;
            let targetIndex = -1;
            
            // First check if we're dropping on a topic
            for (const group of customGroupedTopics) {
                const index = group.topics.findIndex(t => t.id === over.id);
                if (index !== -1) {
                    targetGroup = group;
                    targetIndex = index;
                    break;
                }
            }
            
            // If not dropping on a topic, check if dropping on a group container
            if (!targetGroup) {
                // Check if the over.id matches a group id
                targetGroup = customGroupedTopics.find(g => g.id === over.id);
                if (targetGroup) {
                    targetIndex = targetGroup.topics.length; // Add to end of group
                }
            }
            
            if (!targetGroup) return;
            
            // Don't do anything if dropping on the same position
            if (sourceGroup.id === targetGroup.id && sourceIndex === targetIndex) {
                return;
            }
            
            // If moving to a different group, update the topic's groupId
            if (sourceGroup.id !== targetGroup.id) {
                const newGroupId = targetGroup.id === 'ungrouped' ? null : targetGroup.id;
                await handleAssignTopicToGroup(active.id, newGroupId);
            } else {
                // Reordering within the same group
                const newTopics = arrayMove(sourceGroup.topics, sourceIndex, targetIndex);
                
                // Update display orders
                const updates = newTopics.map((topic, index) => ({
                    id: topic.id,
                    displayOrder: index + 1
                }));
                
                // Update local state
                const updatedTopics = topics.map(topic => {
                    const update = updates.find(u => u.id === topic.id);
                    return update ? { ...topic, displayOrder: update.displayOrder } : topic;
                });
                
                setAppData(prev => ({
                    ...prev,
                    topics: updatedTopics
                }));
                
                // Update server
                try {
                    await authenticatedFetch(API_ENDPOINTS.TOPICS_REORDER, {
                        method: 'PUT',
                        body: JSON.stringify({ updates })
                    });
                } catch (error) {
                }
            }
            return;
        }
        
        // Handle scheduled groups drag-and-drop
        if (topicScheduledFilter === 'scheduled' && groupedTopics) {
            // Find the group containing the active item
            let activeGroup = null;
            let activeGroupIndex = -1;
            let overGroup = null;
            let overGroupIndex = -1;
            
            for (let i = 0; i < groupedTopics.length; i++) {
                const group = groupedTopics[i];
                const activeIndex = group.topics.findIndex(t => t.id === active.id);
                const overIndex = group.topics.findIndex(t => t.id === over.id);
                
                if (activeIndex !== -1) {
                    activeGroup = group;
                    activeGroupIndex = activeIndex;
                }
                if (overIndex !== -1) {
                    overGroup = group;
                    overGroupIndex = overIndex;
                }
            }
            
            // Only allow reordering within the same group
            if (activeGroup === overGroup && activeGroup !== null) {
                const newGroupTopics = arrayMove(activeGroup.topics, activeGroupIndex, overGroupIndex);
                
                // Update display orders for all topics in the group
                const updates = newGroupTopics.map((topic, index) => ({
                    id: topic.id,
                    displayOrder: topic.displayOrder // Keep existing order for now
                }));
                
                // Update the app data with new order
                const updatedTopics = topics.map(topic => {
                    const update = updates.find(u => u.id === topic.id);
                    if (update) {
                        const groupIndex = newGroupTopics.findIndex(t => t.id === topic.id);
                        return { ...topic, displayOrder: groupIndex + 1 };
                    }
                    return topic;
                });
                
                setAppData(prev => ({
                    ...prev,
                    topics: updatedTopics
                }));
                
                // Update server
                try {
                    await authenticatedFetch(API_ENDPOINTS.TOPICS_REORDER, {
                        method: 'PUT',
                        body: JSON.stringify({ updates })
                    });
                } catch (error) {
                }
            }
            return; // Exit early for grouped view
        }
        
        // Original logic for non-grouped view
        const oldIndex = filteredTopics.findIndex(topic => topic.id === active.id);
        const newIndex = filteredTopics.findIndex(topic => topic.id === over.id);
        
        // Update local state immediately for smooth UX
        const newFilteredTopics = arrayMove(filteredTopics, oldIndex, newIndex);
        
        // Create a new topics array with updated display orders
        let updatedTopics;
        let topicOrderMap = {};
        
        if (filteredTopics.length === topics.length) {
            // No filtering - just reorder all topics based on the new order
            newFilteredTopics.forEach((topic, index) => {
                topicOrderMap[topic.id] = index + 1;
            });
            
            updatedTopics = topics.map(topic => ({
                ...topic,
                displayOrder: topicOrderMap[topic.id] || topic.displayOrder || 999999
            }));
        } else {
            // Filtering is active - only update the filtered topics
            alert(t('reorderingWhileFilteringNotSupported'));
            return;
        }
        
        setAppData(prev => ({ ...prev, topics: updatedTopics }));
        
        // Create updates array for the server - only send topics that had their order changed
        const updates = newFilteredTopics.map((topic, index) => ({
            id: topic.id,
            displayOrder: index + 1
        }));
        
        // Send update to server
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPICS_REORDER, {
                method: 'PUT',
                body: JSON.stringify({ updates })
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to reorder topics');
            }
        } catch (error) {
        }
    };
    
    // Update tipsForSelectedTopic whenever relevant data changes
    useEffect(() => {
        if (!selectedTopicId) {
            setTipsForSelectedTopic([]);
            return;
        }
        
        const allTips = appData.tips[selectedTopicId] || [];
        
        // Filter out any undefined or null tips first
        const validTips = allTips.filter(tip => tip && tip.id);
        
        // Apply review due filter first if active
        let filteredTips = validTips;
        if (reviewDueFilter) {
            filteredTips = filteredTips.filter(tip => tip.reviewIsDue);
        }
        
        // Highly optimized sorting function for concepts - O(n log n) with scoring system
        const sortConcepts = (tips) => {
            // Pre-calculate read status set lookup for O(1) access
            const readStatusSet = READ_KNOWLEDGE_STATUSES;
            
            // Pre-calculate timestamps to avoid repeated Date parsing
            const tipsWithScores = tips.map(tip => {
                const isUnread = !readStatusSet.has(tip.knowledgeStatus);
                const reviewDue = tip.reviewIsDue || false;
                
                // Calculate priority score: review due (3), unread (2), normal (1)
                let priorityScore = 1;
                if (reviewDue) priorityScore = 3;
                else if (isUnread) priorityScore = 2;
                
                // Parse timestamp once per tip
                const timestamp = tip.createdAt ? 
                    (typeof tip.createdAt === 'number' ? tip.createdAt : new Date(tip.createdAt).getTime()) : 0;
                
                return { tip, priorityScore, timestamp };
            });
            
            // Sort by priority score first, then by timestamp
            tipsWithScores.sort((a, b) => {
                // Compare priority scores first
                if (a.priorityScore !== b.priorityScore) {
                    return b.priorityScore - a.priorityScore; // Higher priority first
                }
                
                // If same priority, sort by timestamp (newest first)
                return b.timestamp - a.timestamp;
            });
            
            // Extract just the tips in sorted order
            return tipsWithScores.map(item => item.tip);
        };
        
        // Handle hidden tips filter
        if (statusFilters.includes('hidden')) {
            // Show only hidden tips
            const hiddenTips = filteredTips.filter(tip => tip.isHidden);
            const result = sortConcepts(hiddenTips);
            setTipsForSelectedTopic(result);
        } else if (statusFilters.includes('favorites')) {
            // Show only favorited tips
            const favoritedTips = filteredTips.filter(tip => tip.isFavorite);
            const result = sortConcepts(favoritedTips);
            setTipsForSelectedTopic(result);
        } else {
            // Show all tips except hidden ones (unless hidden filter is active)
            const visibleTips = filteredTips.filter(tip => !tip.isHidden);
            const statusFilteredTips = statusFilters.length === 0 ? visibleTips : visibleTips.filter(tip => 
                tip.knowledgeStatus === null || statusFilters.includes(tip.knowledgeStatus)
            );
            const result = sortConcepts(statusFilteredTips);
            setTipsForSelectedTopic(result);
        }
    }, [selectedTopicId, appData.tips, statusFilters, reviewDueFilter, lastTipUpdate, READ_KNOWLEDGE_STATUSES]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedTip = tipsForSelectedTopic.find(t => t.id === selectedTipId);
    
    // Debug logging for selectedTip changes
    useEffect(() => {
        if (selectedTip) {
        }
    }, [selectedTip]);

    // No longer fetch review questions count when tip is selected
    // The modal will handle loading the questions

    // Effects
    useEffect(() => {
        // Check for email verification token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const verificationToken = urlParams.get('token');
        
        if (verificationToken && window.location.pathname === '/verify-email') {
            // Prevent double verification attempts
            const verificationAttempted = sessionStorage.getItem(`verification_${verificationToken}`);
            if (verificationAttempted) {
                console.log('[Email Verification] Already attempted this token, skipping...');
                return;
            }
            
            // Mark this token as attempted
            sessionStorage.setItem(`verification_${verificationToken}`, 'true');
            
            // Handle email verification
            const verifyEmail = async () => {
                setLoadingMessage('Verifying your email...');
                try {
                    const response = await fetch(`${API_ENDPOINTS.VERIFY_EMAIL || '/api/verify-email'}?token=${verificationToken}`);
                    const data = await response.json();
                    
                    if (response.ok && data.success) {
                        setVerificationMessage(data.message || 'Email verified successfully! You can now log in.');
                        setVerificationError('');
                        // Clear the URL parameters
                        window.history.replaceState({}, document.title, '/');
                        // Clear the verification attempt marker after success
                        sessionStorage.removeItem(`verification_${verificationToken}`);
                    } else {
                        setVerificationError(data.error || 'Email verification failed. The link may have expired.');
                        setVerificationMessage('');
                        // If it's a real error (not already verified), allow retry
                        if (!data.alreadyVerified) {
                            sessionStorage.removeItem(`verification_${verificationToken}`);
                        }
                    }
                } catch (error) {
                    console.error('Email verification error:', error);
                    setVerificationError('Failed to verify email. Please try again.');
                    setVerificationMessage('');
                    // Allow retry on network errors
                    sessionStorage.removeItem(`verification_${verificationToken}`);
                } finally {
                    setLoadingMessage('');
                }
            };
            
            verifyEmail();
            return; // Don't check for session when verifying email
        }
        
        // Check for existing session on app load
        const session = getUserSession();
        if (session) {
            // Check if sessionId is invalid
            if (!session.sessionId || session.sessionId === 'undefined') {
                clearUserSession();
                return;
            }
            
            // Validate session with backend
            const validateSession = async () => {
                try {
                    const headers = {
                        'X-User-ID': session.userId,
                        'Content-Type': 'application/json',
                    };
                    
                    // Add Authorization header if session token exists
                    if (session.sessionId) {
                        headers['Authorization'] = `Bearer ${session.sessionId}`;
                    }
                    
                    const response = await fetch(API_ENDPOINTS.VALIDATE_SESSION, { headers });
                    const data = await response.json();
                    
                    if (response.ok && data.valid) {
                        // Store session token if returned
                        if (data.sessionId) {
                            localStorage.setItem('sessionId', data.sessionId);
                            localStorage.setItem('sessionExpiresAt', data.expiresAt);
                        }
                        // Use handleLoginSuccess to check for existing sessions
                        handleLoginSuccess(session.userId);
                    } else {
                        // Invalid session, clear cookies
                        clearUserSession();
                    }
                } catch (error) {
                    // Don't fallback to legacy auth if sessionId validation fails
                    clearUserSession();
                }
            };
            validateSession();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Check window size and show responsive warning
    useEffect(() => {
        const checkWindowSize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            
            // Check if user has already dismissed the warning permanently
            const hideWarning = localStorage.getItem('hideResponsiveWarning');
            if (hideWarning === 'true') {
                return;
            }
            
            // Show warning if:
            // - Width is less than 1650px (minimum recommended width)
            // - OR height is less than 800px (minimum recommended height)
            // - AND user is logged in (userId exists)
            if (userId && (width < 1650 || height < 800)) {
                setShowResponsiveWarning(true);
            }
        };
        
        // Check on mount and when user logs in
        if (userId) {
            checkWindowSize();
        }
        
        // Check on window resize
        const handleResize = () => {
            checkWindowSize();
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [userId]);

    // Periodic server health check
    useEffect(() => {
        if (userId) {
            // Initial health check
            checkServerHealth();
            
            // Set up periodic health check every 30 seconds
            const intervalId = setInterval(() => {
                checkServerHealth();
            }, 30000);
            
            return () => clearInterval(intervalId);
        }
    }, [userId, checkServerHealth]);

    // Define fetchData as a standalone function
    const fetchData = useCallback(async () => {
        if (!userId) return;
        
        // Skip fetching if in tutorial mode
        if (isTutorialMode) {
            console.log('📚 Skipping fetchData - Tutorial mode is active');
            return;
        }
        
        setLoadingMessage(t('loadingJourney'));
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.DATA);
            const data = await response.json();
            
            
            
            // Check for pending messages and add error messages for them
            const conversationsWithPending = { ...data.conversations };
            Object.keys(conversationsWithPending).forEach(tipId => {
                const tipConversation = conversationsWithPending[tipId] || [];
                // Check if the last user message has is_pending_response flag
                for (let i = tipConversation.length - 1; i >= 0; i--) {
                    const msg = tipConversation[i];
                    if (msg.role === 'user' && msg.is_pending_response) {
                        // Add an error message after this pending user message
                        const errorMessage = {
                            role: 'bot',
                            text: t('previousMessageFailed') || 'Failed to get response for your previous message. You can retry or send a new message.',
                            timestamp: Date.now(),
                            isError: true,
                            retryable: true,
                            originalUserMessage: msg.text,
                            messageId: `pending_error_${Date.now()}`
                        };
                        // Insert error message after the pending user message
                        conversationsWithPending[tipId] = [
                            ...tipConversation.slice(0, i + 1),
                            errorMessage,
                            ...tipConversation.slice(i + 1)
                        ];
                        break; // Only handle the last pending message per tip
                    }
                }
            });
            
            setAppData({ ...data, conversations: conversationsWithPending });
            
            // Initialize unread concepts from loaded data
            const unreadConceptIds = new Set();
            const unreadTopicIds = new Set();
            
            
            // Check all tips across all topics for unread status
            Object.values(data.tips || {}).forEach(tipsForTopic => {
                tipsForTopic.forEach(tip => {
                    // A tip is unread if its knowledgeStatus is not one of the 6 read statuses
                    if (!READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus)) {
                        unreadConceptIds.add(tip.id);
                        unreadTopicIds.add(tip.topicId);
                    } else {
                    }
                });
            });
            
            
            setUnreadConcepts(unreadConceptIds);
            setUnreadTopics(unreadTopicIds);
            
        } catch (error) {
        } finally {
            setLoadingMessage(''); // Clear loading message
        }
    }, [userId, authenticatedFetch, t, READ_KNOWLEDGE_STATUSES, setAppData, isTutorialMode]);

    // Main data loading effect
    useEffect(() => {
        if (userId && !isTutorialMode) {
            fetchData();
        }
    }, [userId, uiLanguage, fetchData, forceRefetch, isTutorialMode]);

    // Effect to select a topic after data has been loaded
    useEffect(() => {
        // This logic should not run if topics are not yet loaded.
        if (!appData.topics) return; 
    
        // Select the first topic if none is selected, but preserve temp topic selection
        if (filteredTopics.length > 0 && !selectedTopicId) {
            const firstTopicId = filteredTopics[0].id;
            setSelectedTopicId(firstTopicId);
        } else if (filteredTopics.length === 0) {
            // Clear selections if there are no filtered topics
            setSelectedTopicId(null);
            setSelectedTipId(null);
        } else if (selectedTopicId && selectedTopicId.startsWith('temp-')) {
            // Keep the temp topic selected
        }
    }, [filteredTopics, selectedTopicId, appData.topics]);


    // Track previous conversation length to detect actual new messages
    const prevConversationLengthRef = useRef(0);
    useEffect(() => {
        // Only auto-scroll when there's a new message, not on every poll update
        if (conversation.length > prevConversationLengthRef.current) {
            conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        prevConversationLengthRef.current = conversation.length;
    }, [conversation]);

    // Restore scroll position when tip content changes
    const prevSelectedTipIdRef = useRef();
    useEffect(() => {
        if (selectedTipId && prevSelectedTipIdRef.current !== selectedTipId) {
            // Only reset scroll if selectedTipId actually changed
            if (conceptContentAreaRef.current) {
                conceptContentAreaRef.current.scrollTop = 0;
            }
            if (conceptContentAreaRef2.current) {
                conceptContentAreaRef2.current.scrollTop = 0;
            }
            if (conversationAreaRef.current) {
                conversationAreaRef.current.scrollTop = 0;
            }
        }
        prevSelectedTipIdRef.current = selectedTipId;
    }, [selectedTipId, selectedTopicId]);

    // Check for overflow when content changes
    useEffect(() => {
        if (selectedTip) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
                // Check both concept content areas
                [conceptContentAreaRef, conceptContentAreaRef2].forEach(ref => {
                    if (ref.current) {
                        const container = ref.current.closest('.concept-content-container');
                        if (container) {
                            const hasOverflow = ref.current.scrollHeight > ref.current.clientHeight;
                            container.classList.toggle('has-overflow', hasOverflow);
                        }
                    }
                });
            }, 100);
        }
    }, [selectedTip, selectedTopicId]);

    useEffect(() => {
        const handleScroll = () => {
        };

        // Store current refs to ensure proper cleanup
        const conceptArea1 = conceptContentAreaRef.current;
        const conceptArea2 = conceptContentAreaRef2.current;
        const conversationArea = conversationAreaRef.current;

        if (conceptArea1) {
            conceptArea1.addEventListener('scroll', handleScroll);
        }
        if (conceptArea2) {
            conceptArea2.addEventListener('scroll', handleScroll);
        }
        if (conversationArea) {
            conversationArea.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (conceptArea1) {
                conceptArea1.removeEventListener('scroll', handleScroll);
            }
            if (conceptArea2) {
                conceptArea2.removeEventListener('scroll', handleScroll);
            }
            if (conversationArea) {
                conversationArea.removeEventListener('scroll', handleScroll);
            }
        };
    }, [selectedTip, selectedTopicId]);

    useEffect(() => {
        
        if (prevTopicIdRef.current !== selectedTopicId) {
            
            // Only set to first tip if we don't have a newly generated concept AND the topic is not null
            if (!newlyGeneratedConcept && selectedTopicId) {
                const firstTipId = tipsForSelectedTopic[0]?.id || null;
                setSelectedTipId(firstTipId);
            } else if (!selectedTopicId) {
                setSelectedTipId(null);
            } else {
            }
        } else {
        }
        prevTopicIdRef.current = selectedTopicId;
    }, [selectedTopicId, tipsForSelectedTopic, newlyGeneratedConcept]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) setIsFilterOpen(false);
            if (conceptDropdownRef.current && !conceptDropdownRef.current.contains(event.target)) {
                setConceptDropdownOpen(null);
            }
            if (topicDropdownRef.current && !topicDropdownRef.current.contains(event.target)) {
                setTopicDropdownOpen(null);
            }
            if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
                setGroupDropdownOpen(null);
            }
            // Check if click is outside the topic filter dropdown and not on the filter button
            if (topicFilterRef.current && !topicFilterRef.current.contains(event.target) && 
                topicFilterButtonRef.current && !topicFilterButtonRef.current.contains(event.target)) {
                setIsTopicFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [filterRef, conceptDropdownRef, topicDropdownRef, groupDropdownRef, topicFilterRef, topicFilterButtonRef]);

    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                // When page is restored from bfcache, state can be stale.
                // Force a refetch of the data.
                setForceRefetch(f => f + 1);
            }
        };
    
        window.addEventListener('pageshow', handlePageShow);
        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []);
    
    useEffect(() => {
        if (isNewConceptModalOpen && newlyGeneratedConcept) {
            setSelectedTipId(newlyGeneratedConcept.id);
        }
    }, [isNewConceptModalOpen, newlyGeneratedConcept]);

    useEffect(() => {
        selectedTopicIdRef.current = selectedTopicId;
    }, [selectedTopicId]);

    // Clear selections when selected topic is filtered out
    useEffect(() => {
        // Skip if no topic is selected
        if (!selectedTopicId) {
            return;
        }
        
        if (filteredTopics.length > 0) {
            // Check if the currently selected topic is still in the filtered list
            const isSelectedTopicVisible = filteredTopics.some(topic => topic.id === selectedTopicId);
            
            if (!isSelectedTopicVisible) {
                // Clear both topic and tip selections
                setSelectedTopicId(null);
                setSelectedTipId(null);
            }
        } else {
            // Clear selections whenever no topics are visible (hierarchical filtering)
            // This ensures that if no parent (topic) is visible, no child (tip) should be selected
            setSelectedTopicId(null);
            setSelectedTipId(null);
        }
    }, [filteredTopics, selectedTopicId]);

    // Clear tip selection when selected tip is filtered out by status filters
    useEffect(() => {
        
        // Skip if no tip is selected or no topic is selected
        if (!selectedTipId || !selectedTopicId) {
            return;
        }
        
        if (tipsForSelectedTopic.length > 0) {
            // Check if the currently selected tip is still in the filtered list
            const isSelectedTipVisible = tipsForSelectedTopic.some(tip => tip.id === selectedTipId);
            
            if (!isSelectedTipVisible) {
                
                // Clear only tip selection, keep topic selection
                setSelectedTipId(null);
            } else {
            }
        } else {
            // No tips are visible after filtering, clear tip selection
            setSelectedTipId(null);
        }
    }, [tipsForSelectedTopic, selectedTipId, statusFilters, selectedTopicId]);

    const handleLoginSuccess = async (loggedInUserId, googleData = null) => {
        // With multiple sessions allowed, skip session takeover check
        // const hasExistingSessions = await sessionManager.checkForExistingSessions();
        
        // console.log('[App] hasExistingSessions:', hasExistingSessions);
        // if (hasExistingSessions) {
        //     // Show takeover modal
        //     console.log('[App] Showing session takeover modal');
        //     setPendingSession({ userId: loggedInUserId, googleData });
        //     setShowSessionTakeoverModal(true);
        //     return;
        // }
        
        // Proceed with normal login
        proceedWithLogin(loggedInUserId, googleData);
    };
    
    const proceedWithLogin = (loggedInUserId, googleData = null) => {
        setUserId(loggedInUserId);
        setGoogleUserData(googleData);
        
        // Get username from session (it was saved during login)
        const session = getUserSession();
        if (session) {
            setUsername(session.username);
        }
        
        // Initialize session manager (tab takeover disabled for multi-session support)
        // sessionManager.init(() => {
        //     // This callback is called when another tab takes over
        //     console.log('[App] Session taken over by another tab, logging out');
        //     handleLogout(true); // Preserve cookies for auto-login
        // });
        
        // SSE connection will be set up by useEffect when userId changes
        
        // Check if user has a profile
        const checkUserProfile = async () => {
            try {
                const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
                    headers: {
                        'X-User-ID': loggedInUserId,
                        'Content-Type': 'application/json',
                    }
                });
                
                
                if (response.ok) {
                    const data = await response.json();
                    
                    // Check if profile exists by looking for the user_id field (database field)
                    if (data && data.user_id) {
                        setUserProfile(data);
                        setUILanguage(data.uiLanguage || 'en');
                    } else {
                        // No profile exists at all (empty object {}), show profile modal
                        setIsUserProfileModalOpen(true);
                    }
                } else {
                }
            } catch (error) {
            }
        };
        
        checkUserProfile();
    };

    const handleSaveProfile = async (profileData) => {
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.USER_PROFILE, {
                method: 'POST',
                body: JSON.stringify(profileData)
            });
            
            if (response.ok) {
                const isFirstTimeUser = !userProfile || Object.keys(userProfile).length === 0;
                setUserProfile(profileData);
                // Update UI language immediately if it changed
                if (profileData.uiLanguage && profileData.uiLanguage !== uiLanguage) {
                    setUILanguage(profileData.uiLanguage);
                }
                setIsUserProfileModalOpen(false);
                
                // Show guided tutorial for first-time users after profile setup
                if (isFirstTimeUser && topics.length === 0) {
                    setTimeout(() => setShowGuidedTutorial(true), 500);
                }
            } else {
            }
        } catch (error) {
        }
    };

    const setupSSEConnection = () => {
        // Try to get sessionId from localStorage first, then from cookies
        let sessionId = localStorage.getItem('sessionId');
        if (!sessionId) {
            const session = getUserSession();
            sessionId = session?.sessionId;
        }
        
        if (!sessionId || !userId) {
            return;
        }
        
        // Handle SSE messages
        const handleSSEMessage = (data) => {
            if (data.type === 'session_invalidated') {
                const currentSessionId = localStorage.getItem('sessionId');
                if (currentSessionId === data.sessionId) {
                    // Clear session and redirect to login
                    clearUserSession();
                    setUserId(null);
                    setUsername(null);
                    setUserProfile(null);
                    setUILanguage('en');
                    setAppData({ topics: [], tips: {}, conversations: {}, topicGroups: [] });
                    setSelectedTopicId(null);
                    setSelectedTipId(null);
                    setIsUserProfileModalOpen(false);
                    // Close SSE connection
                    if (sseConnectionRef.current) {
                        sseConnectionRef.current.close();
                        sseConnectionRef.current = null;
                    }
                    // Show alert to user
                    alert(t('sessionInvalidatedByAnotherLogin'));
                }
            } else if (data.type === 'tip_update') {
                handleTipUpdateEvent(data);
            } else if (data.type === 'topic_update') {
                handleTopicUpdateEvent(data);
            } else if (data.type === 'conversation_update') {
                handleConversationUpdateEvent(data);
            } else if (data.type === 'review_chat_update') {
                handleReviewChatUpdateEvent(data);
            }
        };
        
        // Handle connection status changes
        const handleStatusChange = (status, data) => {
            setConnectionStatus(status);
            setConnectionStatusData(data);
            
            // Auto-refresh data when reconnected after server restart
            if (status === 'connected' && sseConnectionRef.current?.isShuttingDown) {
                console.log('[SSE] Reconnected after server restart, refreshing data...');
                // Refresh app data
                fetchData();
                sseConnectionRef.current.isShuttingDown = false;
            }
        };
        
        // Create enhanced SSE connection manager
        const connectionManager = new SSEConnectionManager(
            userId,
            sessionId,
            handleSSEMessage,
            handleStatusChange
        );
        
        // Store the manager instead of raw EventSource
        sseConnectionRef.current = connectionManager;
        
        // Connect
        connectionManager.connect();
    };
    
    // Set up SSE connection when userId changes
    useEffect(() => {
        if (userId) {
            setupSSEConnection();
        } else if (sseConnectionRef.current) {
            sseConnectionRef.current.close();
            sseConnectionRef.current = null;
        }
        
        // Cleanup on unmount
        return () => {
            if (sseConnectionRef.current) {
                sseConnectionRef.current.close();
                sseConnectionRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]); // Re-run when userId changes
    
    // Handle real-time tip updates from SSE
    const handleTipUpdateEvent = (data) => {
        switch (data.eventType) {
            case 'generating':
                // Update topic to show it's generating a tip
                setAppData(prev => {
                    const updatedTopics = prev.topics.map(t => 
                        t.id === data.topicId 
                            ? { ...t, isGeneratingTip: true }
                            : t
                    );
                    return { ...prev, topics: updatedTopics };
                });
                break;
                
            case 'generated':
                // Add the new tip to the state
                if (data.data.tip) {
                    setAppData(prev => {
                        const updatedTips = { ...prev.tips };
                        if (!updatedTips[data.topicId]) {
                            updatedTips[data.topicId] = [];
                        }
                        // Check if tip already exists (avoid duplicates)
                        const tipExists = updatedTips[data.topicId].some(t => t.id === data.tipId);
                        
                        if (!tipExists) {
                            updatedTips[data.topicId] = [...updatedTips[data.topicId], data.data.tip];
                            // Mark as unread
                            setUnreadConcepts(prev => new Set([...prev, data.tipId]));
                            setUnreadTopics(prev => new Set([...prev, data.topicId]));
                            // Play notification sound
                            playSound('newConcept');
                        }
                        return {
                            ...prev,
                            tips: updatedTips,
                            topics: prev.topics.map(t => 
                                t.id === data.topicId 
                                    ? { ...t, isGeneratingTip: false }
                                    : t
                            )
                        };
                    });
                    // Update lastTipUpdate to force tips panel re-render
                    setLastTipUpdate(prev => prev + 1);
                } else {
                }
                break;
                
            case 'generation_error':
                // Clear generating flag on error
                setAppData(prev => ({
                    ...prev,
                    topics: prev.topics.map(t => 
                        t.id === data.topicId 
                            ? { ...t, isGeneratingTip: false }
                            : t
                    )
                }));
                break;
                
            case 'deleted':
                // Remove tip from state
                setAppData(prev => {
                    const updatedTips = { ...prev.tips };
                    if (updatedTips[data.topicId]) {
                        updatedTips[data.topicId] = updatedTips[data.topicId].filter(t => t.id !== data.tipId);
                    }
                    return { ...prev, tips: updatedTips };
                });
                break;
                
            case 'status_changed':
                // Update tip status
                if (data.data.status !== undefined) {
                    setAppData(prev => {
                        const updatedTips = { ...prev.tips };
                        if (updatedTips[data.topicId]) {
                            updatedTips[data.topicId] = updatedTips[data.topicId].map(t => 
                                t.id === data.tipId 
                                    ? { 
                                        ...t, 
                                        knowledgeStatus: data.data.status,
                                        dateLearned: data.data.dateLearned
                                    }
                                    : t
                            );
                        }
                        return { ...prev, tips: updatedTips };
                    });
                    
                    // Update unread marks
                    if (READ_KNOWLEDGE_STATUSES.has(data.data.status)) {
                        // Remove from unread sets
                        setUnreadConcepts(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(data.tipId);
                            return newSet;
                        });
                        
                        // Check if topic still has unread tips
                        const topicTips = appData.tips[data.topicId] || [];
                        const hasUnreadTips = topicTips.some(t => 
                            t.id !== data.tipId && !READ_KNOWLEDGE_STATUSES.has(t.knowledgeStatus)
                        );
                        
                        if (!hasUnreadTips) {
                            setUnreadTopics(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(data.topicId);
                                return newSet;
                            });
                        }
                    }
                    
                    // Force re-render of tips panel
                    setLastTipUpdate(prev => prev + 1);
                }
                break;
                
            default:
                // Unknown event type, log for debugging
                break;
        }
    };
    
    // Handle real-time topic updates from SSE
    const handleTopicUpdateEvent = (data) => {
        
        switch (data.eventType) {
            case 'created':
                // Add new topic to state
                if (data.data.topic) {
                    setAppData(prev => {
                        // Check if topic already exists by ID or name
                        // This handles cases where we've already added the topic via handleAddTopic
                        const topicExists = prev.topics.some(t => 
                            t.id === data.data.topic.id || 
                            (t.name === data.data.topic.name && !t.id.startsWith('temp-'))
                        );
                        
                        // Also check if we have a temp topic with the same name
                        const tempTopicIndex = prev.topics.findIndex(t => 
                            t.name === data.data.topic.name && t.id.startsWith('temp-')
                        );
                        
                        if (tempTopicIndex !== -1) {
                            // Replace the temp topic with the real one
                            const newTopics = [...prev.topics];
                            newTopics[tempTopicIndex] = data.data.topic;
                            return {
                                ...prev,
                                topics: newTopics
                            };
                        } else if (topicExists) {
                            // Topic already exists, update it instead of adding duplicate
                            return {
                                ...prev,
                                topics: prev.topics.map(t => 
                                    t.name === data.data.topic.name ? data.data.topic : t
                                )
                            };
                        }
                        return {
                            ...prev,
                            topics: [...prev.topics, data.data.topic]
                        };
                    });
                }
                break;
                
            case 'generation_complete':
                // Clear generating flag
                setAppData(prev => ({
                    ...prev,
                    topics: prev.topics.map(t => 
                        t.id === data.topicId 
                            ? { ...t, isGeneratingTopic: false }
                            : t
                    )
                }));
                break;
                
            case 'reordered':
                // Update all topics with new order
                if (data.data.topics) {
                    setAppData(prev => ({
                        ...prev,
                        topics: data.data.topics
                    }));
                }
                break;
                
            case 'topicGroupChanged':
                // Update topic's group assignment
                if (data.data.topic) {
                    setAppData(prev => ({
                        ...prev,
                        topics: prev.topics.map(t => 
                            t.id === data.topicId ? data.data.topic : t
                        )
                    }));
                }
                break;
                
            case 'deleted':
                // Remove topic from state
                setAppData(prev => {
                    const updatedTips = { ...prev.tips };
                    delete updatedTips[data.topicId];
                    return {
                        ...prev,
                        topics: prev.topics.filter(t => t.id !== data.topicId),
                        tips: updatedTips
                    };
                });
                break;
                
            case 'updated':
                // Update topic data
                if (data.data.topic) {
                    setAppData(prev => ({
                        ...prev,
                        topics: prev.topics.map(t => 
                            t.id === data.topicId 
                                ? data.data.topic
                                : t
                        )
                    }));
                }
                break;
                
            case 'groupCreated':
                // Add new group to state
                if (data.data.group) {
                    setAppData(prev => {
                        // Check if group already exists to prevent duplicates
                        const groupExists = prev.topicGroups.some(g => g.id === data.data.group.id);
                        if (groupExists) {
                            return prev;
                        }
                        return {
                            ...prev,
                            topicGroups: [...prev.topicGroups, data.data.group]
                        };
                    });
                }
                break;
                
            case 'groupUpdated':
                // Update group in state
                if (data.data.group) {
                    setAppData(prev => ({
                        ...prev,
                        topicGroups: prev.topicGroups.map(g => 
                            g.id === data.data.group.id ? data.data.group : g
                        )
                    }));
                }
                break;
                
            case 'groupDeleted':
                // Remove group from state
                if (data.data.groupId) {
                    setAppData(prev => ({
                        ...prev,
                        topicGroups: prev.topicGroups.filter(g => g.id !== data.data.groupId)
                    }));
                }
                break;
                
            case 'groupsReordered':
                // Update topic groups order
                if (data.data.groups) {
                    setAppData(prev => ({
                        ...prev,
                        topicGroups: data.data.groups
                    }));
                }
                break;
                
            default:
                // Unknown event type, log for debugging
                break;
        }
    };
    
    // Handle real-time conversation updates from SSE
    const handleConversationUpdateEvent = (data) => {
        
        switch (data.eventType) {
            case 'generating_response':
                // Update topic to show it's generating a response
                setAppData(prev => ({
                    ...prev,
                    topics: prev.topics.map(t => 
                        t.id === data.topicId 
                            ? { ...t, isGeneratingResponse: true }
                            : t
                    )
                }));
                // Set chatting tip ID
                setChattingTipId(data.tipId);
                break;
                
            case 'message_added':
            case 'response_generated':
            case 'response_failed':
                // For response_generated or response_failed, immediately clear the chatting state and generating flag
                if (data.eventType === 'response_generated' || data.eventType === 'response_failed') {
                    setChattingTipId(prev => prev === data.tipId ? null : prev);
                    
                    // Always clear the generating response flag for response_generated or response_failed event
                    setAppData(prev => ({
                        ...prev,
                        topics: prev.topics.map(t => 
                            t.id === data.topicId 
                                ? { ...t, isGeneratingResponse: false }
                                : t
                        )
                    }));
                }
                
                // Handle error case - add error message to conversation
                if (data.eventType === 'response_failed' && data.data.error) {
                    setAppData(prev => {
                        const updatedConversations = { ...prev.conversations };
                        if (!updatedConversations[data.tipId]) {
                            updatedConversations[data.tipId] = [];
                        }
                        
                        // Create error message
                        const errorMessage = {
                            role: 'bot',
                            text: t('networkError') || 'Sorry, I had trouble connecting to the AI.',
                            timestamp: Date.now(),
                            isError: true,
                            retryable: data.data.error.retryable,
                            originalUserMessage: data.data.error.originalUserMessage,
                            messageId: `error_${Date.now()}`
                        };
                        
                        updatedConversations[data.tipId] = [
                            ...updatedConversations[data.tipId],
                            errorMessage
                        ];
                        
                        return {
                            ...prev,
                            conversations: updatedConversations
                        };
                    });
                }
                // Add message to conversation if present (for normal messages)
                else if (data.data.message) {
                    setAppData(prev => {
                        const updatedConversations = { ...prev.conversations };
                        if (!updatedConversations[data.tipId]) {
                            updatedConversations[data.tipId] = [];
                        }
                        
                        // Check if message already exists (avoid duplicates from optimistic updates)
                        const existingMessages = updatedConversations[data.tipId];
                        const isDuplicate = existingMessages.some(msg => 
                            msg.role === data.data.message.role && 
                            msg.text === data.data.message.text &&
                            // Check if timestamps are within 5 seconds (to handle slight differences)
                            Math.abs(new Date(msg.timestamp || 0).getTime() - new Date(data.data.message.timestamp || 0).getTime()) < 5000
                        );
                        
                        if (!isDuplicate) {
                            updatedConversations[data.tipId] = [
                                ...existingMessages,
                                data.data.message
                            ];
                        }
                        
                        return {
                            ...prev,
                            conversations: updatedConversations
                        };
                    });
                    
                    // Play sound for bot messages
                    if (data.data.message.role === 'bot') {
                        playSound('newMessage');
                    }
                }
                break;
                
            case 'conversation_cleared':
                // Clear the conversation for the specified tip
                setAppData(prev => ({
                    ...prev,
                    conversations: {
                        ...prev.conversations,
                        [data.tipId]: []
                    }
                }));
                break;
                
            default:
                // Unknown event type, log for debugging
                break;
        }
    };
    
    const handleReviewChatUpdateEvent = (data) => {
        // Notify any open review question modal about the update
        if (reviewChatUpdateCallbackRef.current) {
            reviewChatUpdateCallbackRef.current(data);
        }
    };
    
    const handleLogout = async (preserveCookies = false) => {
        // Call logout endpoint to invalidate session on server
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
            try {
                await fetch('/api/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionId}`,
                        'X-User-ID': userId
                    }
                });
            } catch (error) {
            }
        }
        
        // Always clear session data completely
        clearUserSession();
        // Also clear from localStorage to be sure
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionExpiresAt');
        
        setUserId(null);
        setUsername(null);
        setUserProfile(null);
        setUILanguage('en');
        setAppData({ topics: [], tips: {}, conversations: {}, topicGroups: [] });
        setSelectedTopicId(null);
        setSelectedTipId(null);
        setIsUserProfileModalOpen(false);
        sessionManager.destroy();
        
        // Close SSE connection
        if (sseConnectionRef.current) {
            sseConnectionRef.current.close();
            sseConnectionRef.current = null;
        }
    };

    // Data Handling
    const handleFilterChange = (status) => {
        const newFilters = statusFilters.includes(status) ? statusFilters.filter(s => s !== status) : [...statusFilters, status];
        setStatusFilters(newFilters);
    };

    const handleEditTopicFromTip = async () => {
        if (!selectedTip) return;
        const currentTopic = topics.find(t => t.id === selectedTip.topicId);
        if (currentTopic) {
            // Mark current tip as "I'll decide later"
            await handleUpdateTipStatus('skip');
            // Close the new concept modal if it's open
            setIsNewConceptModalOpen(false);
            // Open edit modal for the topic
            setTopicToEdit(currentTopic);
            setIsEditTopicModalOpen(true);
        }
    };

    const handleDeleteTip = async (tipId, tipTitle) => {
        setDeleteConfirmModalState({
            isOpen: true,
            tipId,
            tipTitle,
            onConfirm: async () => {
                setDeleteConfirmModalState({ isOpen: false, tipId: null, tipTitle: '', onConfirm: () => {} });
                setDeletingTipId(tipId); // Set deleting state
                try {
                    const response = await authenticatedFetch(`${API_ENDPOINTS.TIPS}/${tipId}`, {
                        method: 'DELETE'
                    });
                    if (!response.ok) throw new Error(t('failedToDeleteTip'));
                    
                    // Remove from local state
                    setAppData(prev => {
                        const updatedTips = { ...prev.tips };
                        // Find the topicId for this tip
                        const topicId = Object.keys(updatedTips).find(tid => 
                            updatedTips[tid].some(tip => tip.id === tipId)
                        );
                        if (topicId) {
                            updatedTips[topicId] = updatedTips[topicId].filter(tip => tip.id !== tipId);
                            // If no tips left for this topic, remove the topic's tips array
                            if (updatedTips[topicId].length === 0) {
                                delete updatedTips[topicId];
                            }
                        }
                        return { ...prev, tips: updatedTips };
                    });
                    
                    // Clear conversations for this tip
                    setAppData(prev => {
                        const updatedConversations = { ...prev.conversations };
                        delete updatedConversations[tipId];
                        return { ...prev, conversations: updatedConversations };
                    });
                    
                    // If this was the selected tip, select the first available tip
                    if (selectedTipId === tipId) {
                        const remainingTips = tipsForSelectedTopic.filter(tip => tip.id !== tipId);
                        setSelectedTipId(remainingTips[0]?.id || null);
                    }

                    // --- Update unreadConcepts and unreadTopics ---
                    setUnreadConcepts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(tipId);
                        return newSet;
                    });
                    setUnreadTopics(prev => {
                        // Find the topicId for this tip
                        let topicId = null;
                        // Try to get topicId from tipsForSelectedTopic if possible
                        const tip = tipsForSelectedTopic.find(t => t.id === tipId);
                        if (tip) {
                            topicId = tip.topicId;
                        } else {
                            // Fallback: search in appData.tips
                            topicId = Object.keys(appData.tips).find(tid =>
                                (appData.tips[tid] || []).some(tip => tip.id === tipId)
                            );
                        }
                        if (!topicId) return prev;
                        // After deletion, check if any unread tips remain for this topic
                        // Get remaining tips for this topic (excluding the deleted one)
                        const remainingTips = (appData.tips[topicId] || []).filter(tip => tip.id !== tipId);
                        // Check if any remaining tips are unread based on their knowledge status
                        const hasUnread = remainingTips.some(tip => 
                            !READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus)
                        );
                        const newSet = new Set(prev);
                        if (!hasUnread) {
                            newSet.delete(topicId);
                        }
                        return newSet;
                    });
                    // --- End update unreadConcepts/unreadTopics ---
                } catch (error) {
                    alert(`${t('errorDeletingTip')} ${error.message}`);
                } finally {
                    setDeletingTipId(null); // Clear deleting state
                }
            }
        });
    };

    // Helper to calculate if dropdown should appear above or below
    const calculateDropdownPosition = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        // More accurate dropdown height estimate based on typical menu items
        const dropdownHeight = 200; // Reduced from 250 to be more accurate
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const minimumSpace = 50; // Minimum space needed from viewport edge
        
        // Debug logging
        
        // If not enough space below (including minimum padding), show above
        if (spaceBelow < dropdownHeight + minimumSpace && spaceAbove > dropdownHeight + minimumSpace) {
            // Position above: use bottom positioning relative to parent
            return { top: 'auto', bottom: '32px' };
        }
        // Default: show below
        return { top: '32px', bottom: 'auto' }; // 32px is roughly the height of the element
    };

    const toggleConceptDropdown = (tipId, event) => {
        event.stopPropagation();
        if (conceptDropdownOpen === tipId) {
            setConceptDropdownOpen(null);
        } else {
            const position = calculateDropdownPosition(event);
            setDropdownPositions(prev => ({
                ...prev,
                [`concept-${tipId}`]: position
            }));
            setConceptDropdownOpen(tipId);
        }
    };

    const toggleTopicDropdown = (topicId, event) => {
        event.stopPropagation();
        if (topicDropdownOpen === topicId) {
            setTopicDropdownOpen(null);
        } else {
            const position = calculateDropdownPosition(event);
            setDropdownPositions(prev => ({
                ...prev,
                [`topic-${topicId}`]: position
            }));
            setTopicDropdownOpen(topicId);
        }
    };

    const toggleGroupDropdown = (groupId, event) => {
        event.stopPropagation();
        if (groupDropdownOpen === groupId) {
            setGroupDropdownOpen(null);
        } else {
            const position = calculateDropdownPosition(event);
            setDropdownPositions(prev => ({
                ...prev,
                [`group-${groupId}`]: position
            }));
            setGroupDropdownOpen(groupId);
        }
    };

    const handleNewConcept = useCallback((concept, topicData = null) => {
        
        // Check if the topic still exists before adding the concept
        // Use topicData if provided, otherwise check appData.topics
        const topicExists = topicData ? true : appData.topics.some(topic => topic.id === concept.topicId);
        if (!topicExists) {
            
            // No need to clear generating state since we're using database flag
            
            return;
        }
        
        // Add the new concept to appData.tips for the correct topic
        setAppData(prev => {
            const tipsForTopic = prev.tips[concept.topicId] || [];
            
            // Only add if not already present
            if (!tipsForTopic.some(tip => tip.id === concept.id)) {
                const newTipsForTopic = [...tipsForTopic, concept];
                
                const updatedTips = {
                    ...prev.tips,
                    [concept.topicId]: newTipsForTopic
                };
                
                return {
                    ...prev,
                    tips: updatedTips
                };
            } else {
                return prev;
            }
        });
        
        // Add unread mark to the new concept
        setUnreadConcepts(prev => {
            const newSet = new Set([...prev, concept.id]);
            return newSet;
        });
        
        setUnreadTopics(prev => {
            const newSet = new Set([...prev, concept.topicId]);
            return newSet;
        });
        
        setLastTipUpdate(prev => prev + 1);
        
        playSound('newConcept');
    }, [appData.topics, setAppData]);

    // Topic Groups Functions
    const handleCreateTopicGroup = async () => {
        if (!newGroupName.trim()) return;
        
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_GROUPS, {
                method: 'POST',
                body: JSON.stringify({ name: newGroupName })
            });
            
            if (response.ok) {
                const newGroup = await response.json();
                
                // Wait a bit to let WebSocket message be processed first
                setTimeout(() => {
                    setAppData(prev => {
                        // Check if WebSocket already added this group
                        const groupExists = prev.topicGroups.some(g => g.id === newGroup.id);
                        if (groupExists) {
                            return prev;
                        }
                        
                        return {
                            ...prev,
                            topicGroups: [...prev.topicGroups, newGroup]
                        };
                    });
                }, 100);
                
                setNewGroupName('');
                setIsCreatingGroup(false);
            }
        } catch (error) {
            alert(t('failedToCreateGroup'));
        }
    };
    
    const handleUpdateTopicGroup = async (groupId, newName) => {
        // If name is empty, cancel the edit instead of proceeding
        if (!newName.trim()) {
            setEditingGroupId(null);
            setEditingGroupName('');
            return;
        }
        
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_GROUP(groupId), {
                method: 'PUT',
                body: JSON.stringify({ name: newName })
            });
            
            if (response.ok) {
                const updatedGroup = await response.json();
                setAppData(prev => ({
                    ...prev,
                    topicGroups: prev.topicGroups.map(g => 
                        g.id === groupId ? updatedGroup : g
                    )
                }));
                setEditingGroupId(null);
                setEditingGroupName('');
            }
        } catch (error) {
            alert(t('failedToUpdateGroup'));
        }
    };
    
    const handleDeleteTopicGroup = async (groupId) => {
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_GROUP(groupId), {
                method: 'DELETE'
            });
            
            if (response.ok) {
                setAppData(prev => ({
                    ...prev,
                    topicGroups: prev.topicGroups.filter(g => g.id !== groupId),
                    topics: prev.topics.map(t => 
                        t.groupId === groupId ? { ...t, groupId: null } : t
                    )
                }));
            }
        } catch (error) {
            alert(t('failedToDeleteGroup'));
        }
    };
    
    // const handleReorderTopicGroups = async (groupOrders) => {
    //     try {
    //         const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_GROUPS_REORDER, {
    //             method: 'PUT',
    //             body: JSON.stringify({ groupOrders })
    //         });
    //         
    //         if (response.ok) {
    //             const { groups } = await response.json();
    //             setAppData(prev => ({
    //                 ...prev,
    //                 topicGroups: groups
    //             }));
    //         }
    //     } catch (error) {
    //         console.error('Error reordering topic groups:', error);
    //     }
    // };
    
    const handleAssignTopicToGroup = async (topicId, groupId) => {
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPIC_ASSIGN_GROUP(topicId), {
                method: 'PUT',
                body: JSON.stringify({ groupId })
            });
            
            if (response.ok) {
                const updatedTopic = await response.json();
                setAppData(prev => ({
                    ...prev,
                    topics: prev.topics.map(t => 
                        t.id === topicId ? updatedTopic : t
                    )
                }));
            }
        } catch (error) {
        }
    };

    const handleAddNewTip = async () => {
        if (!selectedTopicId) return;
        
        // Check if the topic still exists before proceeding
        if (!topicExists(selectedTopicId)) {
            alert(t('errorGeneratingTip') + ' Topic no longer exists.');
            return;
        }
        
        const requestId = generateRequestId('new-tip', selectedTopicId);
        
        // Check if there's already a pending request for this topic
        if (isRequestPending(requestId)) {
            return;
        }
        
        addPendingRequest(requestId);
        
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TIPS, { 
                method: 'POST', body: JSON.stringify({ topicId: selectedTopicId })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to generate tip');
            
            // Double-check that the topic still exists before adding the concept
            if (!topicExists(selectedTopicId)) {
                return;
            }
            
            handleNewConcept(data);
        } catch (error) {
            alert(`${t('errorGeneratingTip')} ${error.message}`);
        } 
        finally { 
            removePendingRequest(requestId);
        }
    };

    const handleAddTopic = async (topicName, userScale, purpose, language, description, isScheduled = false, scheduleTimeHour = 4, scheduleTimePeriod = 1, scheduleFrequencyValue = 1, scheduleFrequencyUnit = 1, scheduledSkipIfUnread = false, userTimezone = null, groupId = null) => {
        
        const requestId = generateRequestId('new-topic', topicName);
        
        // Check if there's already a pending topic creation request
        if (isRequestPending(requestId)) {
            return;
        }
        
        addPendingRequest(requestId);
        setIsAddTopicModalOpen(false);
        setLoadingMessage(t('generatingConcept'));
        
        // Generate a temporary topic for optimistic UI
        const tempId = 'temp-' + uuidv4();
        const tempTopic = {
            id: tempId,
            name: topicName,
            knowledgeLevel: userScale,
            learningPurpose: purpose,
            contentLanguage: language,
            description,
            isScheduled,
            scheduleTimeHour,
            scheduleTimePeriod,
            scheduleFrequencyValue,
            scheduleFrequencyUnit,
            scheduledSkipIfUnread,
            groupId,
            isTemp: true,
            isGeneratingTopic: true  // Mark as generating for UI display
        };
        
        
        // No need to add to generating topics set since we're using database flag

        setAppData(prev => {
            const newTopics = [...prev.topics, tempTopic];
            return { ...prev, topics: newTopics };
        });
        
        // Do not auto-select the topic upon creation. Selection will happen
        // when the user interacts with the NewConceptModal for the first concept.
        
        let newTip;
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TOPICS, { 
                method: 'POST', 
                body: JSON.stringify({ 
                    name: topicName, 
                    knowledgeLevel: userScale, 
                    learningPurpose: purpose, 
                    contentLanguage: language, 
                    description,
                    isScheduled,
                    scheduleTimeHour,
                    scheduleTimePeriod,
                    scheduleFrequencyValue,
                    scheduleFrequencyUnit,
                    scheduledSkipIfUnread,
                    userTimezone,
                    groupId
                })
            });
            
            const data = await response.json();
            
            if (!response.ok) throw new Error(data.error || 'Failed to add topic');
            
            const { newTopic } = data;
            newTip = data.newTip; // May be undefined in async implementation
            
            
            // No need to swap IDs in generating set since we're using database flag

            // Replace temp topic with real topic
            setAppData(prev => {
                const updatedTopics = prev.topics.map(t => t.id === tempId ? newTopic : t);
                const updatedData = { ...prev, topics: updatedTopics };
                
                // If a tip was returned (synchronous mode), add it
                if (newTip) {
                    updatedData.tips = {
                        ...prev.tips,
                        [newTopic.id]: [newTip]
                    };
                }
                
                return updatedData;
            });

            // The topic is not auto-selected, so no need to update selectedTopicId here.
            
            // Only handle new concept if we got a tip (synchronous mode)
            if (newTip) {
                // Call handleNewConcept to handle unread marks and modal display
                // The concept is already added to appData.tips above, so this won't duplicate it
                handleNewConcept(newTip, newTopic);
                
                // Force tipsForSelectedTopic to recalculate after the topic ID change
                // This ensures the new concept appears in the list immediately
                setTimeout(() => {
                    setLastTipUpdate(prev => prev + 1);
                }, 0);
            }
            // In async mode, the polling mechanism will detect the new tip when it arrives
            
        } catch (error) {
            
            
            // Remove temp topic on error
            setAppData(prev => {
                const filteredTopics = prev.topics.filter(t => t.id !== tempId);
                return { ...prev, topics: filteredTopics };
            });
            
            // Do not change selected topic on error, user should stay where they are.
            
            alert(`${t('errorAddingTopic')} ${error.message}`);
        } finally {
            // No need to remove from generating set since we're using database flag
            removePendingRequest(requestId);
        }
    };

    const handleUpdateTopic = async (topicId, updatedData) => {
        setIsEditTopicModalOpen(false);
        setLoadingMessage(t('updatingTopic'));
        try {
            const response = await authenticatedFetch(`${API_ENDPOINTS.TOPICS}/${topicId}`, {
                method: 'PUT',
                body: JSON.stringify(updatedData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update topic');
            setAppData(prev => ({ ...prev, topics: prev.topics.map(t => t.id === topicId ? data : t) }));
        } catch (error) { 
            alert(`${t('errorUpdatingTopic')} ${error.message}`); 
        } 
        finally { 
            setTopicToEdit(null);
        }
    };

    const handleDeleteTopic = async (topicId) => {
        
        setIsEditTopicModalOpen(false);
        setLoadingMessage(t('deletingTopic'));
        setDeletingTopicId(topicId); // Set deleting state
        
        // Cancel any pending concept generation requests for this topic
        const requestsToCancel = Array.from(pendingRequests).filter(requestId => 
            requestId.startsWith(`new-tip-${topicId}-`) || 
            requestId.startsWith(`new-topic-${topicId}-`)
        );
        
        requestsToCancel.forEach(requestId => {
            removePendingRequest(requestId);
        });
        
        // No need to clear generating state since we're using database flag
        
        // Close any modals that might be showing concepts for this topic
        if (newlyGeneratedConcept && newlyGeneratedConcept.topicId === topicId) {
            setIsNewConceptModalOpen(false);
            setNewlyGeneratedConcept(null);
        }
        
        // Check if this is a temp topic (no need to make API call)
        const isTempTopic = topicId.startsWith('temp-');
        if (isTempTopic) {
            
            // Remove temp topic from local state
            setAppData(prev => {
                const updatedTopics = prev.topics.filter(t => t.id !== topicId);
                const updatedTips = { ...prev.tips };
                delete updatedTips[topicId]; // Remove all tips for this topic
                
                return {
                    ...prev,
                    topics: updatedTopics,
                    tips: updatedTips
                };
            });
            
            // Clear selections if the deleted topic was selected
            if (selectedTopicId === topicId) {
                const remainingTopics = appData.topics.filter(t => t.id !== topicId);
                setSelectedTopicId(remainingTopics.length > 0 ? remainingTopics[0].id : null);
                setSelectedTipId(null);
            }
            
            return;
        }
        
        try {
            const response = await authenticatedFetch(`${API_ENDPOINTS.TOPICS}/${topicId}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error(t('failedToDeleteTopic'));
            
            // Remove topic and its tips from local state
            setAppData(prev => {
                const updatedTopics = prev.topics.filter(t => t.id !== topicId);
                const updatedTips = { ...prev.tips };
                delete updatedTips[topicId]; // Remove all tips for this topic
                
                // Remove conversations for all tips of this topic
                const updatedConversations = { ...prev.conversations };
                Object.keys(updatedConversations).forEach(tipId => {
                    // Find if this tip belongs to the deleted topic
                    const tipBelongsToDeletedTopic = Object.values(prev.tips).some(tipsForTopic =>
                        tipsForTopic.some(tip => tip.id === tipId && tip.topicId === topicId)
                    );
                    if (tipBelongsToDeletedTopic) {
                        delete updatedConversations[tipId];
                    }
                });
                
                return {
                    ...prev,
                    topics: updatedTopics,
                    tips: updatedTips,
                    conversations: updatedConversations
                };
            });
            
            // Clear selections if the deleted topic was selected
            if (selectedTopicId === topicId) {
                const remainingTopics = appData.topics.filter(t => t.id !== topicId);
                setSelectedTopicId(remainingTopics.length > 0 ? remainingTopics[0].id : null);
                setSelectedTipId(null);
            }
            
            // Clean up unread states for the deleted topic
            setUnreadTopics(prev => {
                const newSet = new Set(prev);
                newSet.delete(topicId);
                return newSet;
            });
            
            // Clean up unread concepts for tips of the deleted topic
            setUnreadConcepts(prev => {
                const newSet = new Set(prev);
                // Remove all unread concepts that belonged to the deleted topic
                const tipsForDeletedTopic = appData.tips[topicId] || [];
                tipsForDeletedTopic.forEach(tip => {
                    newSet.delete(tip.id);
                });
                return newSet;
            });
            
            
        } catch (error) { 
            alert(`${t('errorDeletingTopic')} ${error.message}`); 
        } 
        finally { 
            setTopicToEdit(null); 
            setDeletingTopicId(null); // Clear deleting state
        }
    };

    const handleOpenEditModal = (topic) => {
        setTopicToEdit(topic);
        setIsEditTopicModalOpen(true);
    };

    const handleUnhideTip = async () => {
        if (!selectedTip) return;
        await handleUpdateTipHidden(false);
    };

    // Helper to show the follow-up modal after a tip transitions from unread to a read state
    const showFollowUpModal = (status, concept) => {
        if (!concept) return;
        if (status === 'skip' || status === 'notWhatLookingFor') {
            setConfirmModalState({
                isOpen: true,
                title: t('notLookingForTitle'),
                message: t('notLookingForMessage'),
                confirmText: t('requestDifferentConcept'),
                cancelText: t('notNow'),
                onConfirm: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse).then(() => {
                                handleAddNewTip();
                            });
                        } else {
                            handleAddNewTip();
                        }
                        return { isOpen: false };
                    });
                },
                onCancel: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse);
                        }
                        return { isOpen: false };
                    });
                },
                showCheckbox: true,
                checkboxLabel: t('hideThisTip'),
                checkboxState: false,
                onCheckboxChange: (checked) => {
                    setConfirmModalState(prev => ({
                        ...prev,
                        checkboxState: checked
                    }));
                }
            });
        } else if (status === 'known' || status === 'easy') {
            const promptMessage = {
                known: t('knownMessage'),
                easy: t('easyMessage')
            }[status];
            setConfirmModalState({
                isOpen: true,
                title: t('continueLearning'),
                message: promptMessage,
                confirmText: t('requestNewConcept'),
                cancelText: t('notNow'),
                onConfirm: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse).then(() => {
                                handleAddNewTip();
                            });
                        } else {
                            handleAddNewTip();
                        }
                        return { isOpen: false };
                    });
                },
                onCancel: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse);
                        }
                        return { isOpen: false };
                    });
                },
                showCheckbox: true,
                checkboxLabel: t('hideThisTip'),
                checkboxState: false,
                onCheckboxChange: (checked) => {
                    setConfirmModalState(prev => ({
                        ...prev,
                        checkboxState: checked
                    }));
                }
            });
        } else if (status === 'hard') {
            setConfirmModalState({
                isOpen: true,
                title: t('conceptTooHard'),
                message: t('hardMessage'),
                confirmText: t('requestEasier'),
                cancelText: t('notNow'),
                onConfirm: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse).then(() => {
                                handleAddNewTip();
                            });
                        } else {
                            handleAddNewTip();
                        }
                        return { isOpen: false };
                    });
                },
                onCancel: () => {
                    setConfirmModalState(prev => {
                        const shouldHide = prev.checkboxState;
                        if (shouldHide) {
                            // Get the current tip state from appData.tips instead of using the stale concept object
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            handleUpdateTipHidden(true, tipToUse);
                        }
                        return { isOpen: false };
                    });
                },
                showCheckbox: true,
                checkboxLabel: t('hideThisTip'),
                checkboxState: false,
                onCheckboxChange: (checked) => {
                    setConfirmModalState(prev => ({
                        ...prev,
                        checkboxState: checked
                    }));
                }
            });
        } else if (status === 'partial' || status === 'new') {
            setConfirmModalState({
                isOpen: true,
                title: t('letsLearnMore'),
                message: t('learnMoreMessage'),
                confirmText: t('startConversation'),
                cancelText: t('skipConversation'),
                onConfirm: () => {
                    // Use state updater pattern to get current checkbox state
                    setConfirmModalState(prev => {
                        const shouldEnableReview = prev.checkboxState;
                        
                        // Handle review schedule enable/disable
                        if (status === 'new' && concept && shouldEnableReview) {
                            // Get the current tip state from appData.tips to ensure we have the latest data
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            
                            // First update state directly for immediate UI feedback
                            setAppData(prevData => {
                                const updatedTips = { ...prevData.tips };
                                updatedTips[tipToUse.topicId] = updatedTips[tipToUse.topicId].map(t => 
                                    t.id === tipToUse.id ? { ...t, isScheduledForReview: true } : t
                                );
                                return { ...prevData, tips: updatedTips };
                            });
                            
                            // Then call the API in the background
                            authenticatedFetch(API_ENDPOINTS.TIP_REVIEW_SCHEDULE(tipToUse.id), {
                                method: 'PUT',
                                body: JSON.stringify({ isScheduledForReview: true })
                            }).then(response => {
                                if (response.ok) {
                                    // Optionally refresh data to get server state
                                    authenticatedFetch(API_ENDPOINTS.DATA).then(dataResponse => {
                                        if (dataResponse.ok) {
                                            dataResponse.json().then(data => setAppData(data));
                                        }
                                    });
                                }
                            }).catch(error => {
                                console.error('Failed to enable review schedule:', error);
                                // Revert the optimistic update on error
                                setAppData(prevData => {
                                    const updatedTips = { ...prevData.tips };
                                    updatedTips[tipToUse.topicId] = updatedTips[tipToUse.topicId].map(t => 
                                        t.id === tipToUse.id ? { ...t, isScheduledForReview: false } : t
                                    );
                                    return { ...prevData, tips: updatedTips };
                                });
                            });
                        }
                        
                        // Start conversation after a delay
                        setTimeout(() => {
                            startAutomatedConversation(concept.id, status, true);
                        }, 0);
                        
                        // Close the modal
                        return { isOpen: false };
                    });
                },
                onCancel: () => {
                    // Use state updater pattern to get current checkbox state
                    setConfirmModalState(prev => {
                        const shouldEnableReview = prev.checkboxState;
                        
                        // Handle review schedule enable/disable even when not starting conversation
                        if (status === 'new' && concept && shouldEnableReview) {
                            // Get the current tip state from appData.tips to ensure we have the latest data
                            const currentTipState = appData.tips[concept.topicId]?.find(t => t.id === concept.id);
                            const tipToUse = currentTipState || concept;
                            
                            // First update state directly for immediate UI feedback
                            setAppData(prevData => {
                                const updatedTips = { ...prevData.tips };
                                updatedTips[tipToUse.topicId] = updatedTips[tipToUse.topicId].map(t => 
                                    t.id === tipToUse.id ? { ...t, isScheduledForReview: true } : t
                                );
                                return { ...prevData, tips: updatedTips };
                            });
                            
                            // Then call the API in the background
                            authenticatedFetch(API_ENDPOINTS.TIP_REVIEW_SCHEDULE(tipToUse.id), {
                                method: 'PUT',
                                body: JSON.stringify({ isScheduledForReview: true })
                            }).then(response => {
                                if (response.ok) {
                                    // Optionally refresh data to get server state
                                    authenticatedFetch(API_ENDPOINTS.DATA).then(dataResponse => {
                                        if (dataResponse.ok) {
                                            dataResponse.json().then(data => setAppData(data));
                                        }
                                    });
                                }
                            }).catch(error => {
                                console.error('Failed to enable review schedule:', error);
                                // Revert the optimistic update on error
                                setAppData(prevData => {
                                    const updatedTips = { ...prevData.tips };
                                    updatedTips[tipToUse.topicId] = updatedTips[tipToUse.topicId].map(t => 
                                        t.id === tipToUse.id ? { ...t, isScheduledForReview: false } : t
                                    );
                                    return { ...prevData, tips: updatedTips };
                                });
                            });
                        }
                        
                        // Close the modal
                        return { isOpen: false };
                    });
                },
                showCheckbox: status === 'new', // Only show checkbox for "this is new to me"
                checkboxLabel: t('enableReviewSchedule') || 'Enable review schedule for this concept',
                checkboxState: true, // Default to checked for better UX
                onCheckboxChange: (checked) => {
                    setConfirmModalState(prev => ({
                        ...prev,
                        checkboxState: checked
                    }));
                }
            });
        }
    };

    const handleUpdateTipStatus = async (newStatus, conceptOverride = null, skipFollowUpModal = false) => {
        const tip = conceptOverride || selectedTip;
        if (!tip) return;
        const originalStatus = tip.knowledgeStatus;
        
        // Check if this is a transition from unread to read state
        const isTransitioningToRead = !READ_KNOWLEDGE_STATUSES.has(originalStatus) && READ_KNOWLEDGE_STATUSES.has(newStatus);
        
        // Update optimistically with both knowledgeStatus and dateLearned if transitioning to read
        const optimisticState = { 
            ...appData.tips, 
            [tip.topicId]: appData.tips[tip.topicId].map(t => 
                t.id === tip.id 
                    ? { 
                        ...t, 
                        knowledgeStatus: newStatus,
                        ...(isTransitioningToRead && { dateLearned: new Date().toISOString() })
                    } 
                    : t
            )
        };
        setAppData(prev => ({...prev, tips: optimisticState }));
        
        // Only remove unread mark if the new status is one of the 6
        if (READ_KNOWLEDGE_STATUSES.has(newStatus)) {
            setUnreadConcepts(prev => {
                const newSet = new Set(prev);
                newSet.delete(tip.id);
                
                // Check if topic still has unread concepts after removing this one
                // We need to check the actual knowledge status of remaining tips, not the old unread set
                const topicTips = optimisticState[tip.topicId] || [];
                const hasUnreadConcepts = topicTips.some(tip2 =>
                    tip2.id !== tip.id && !READ_KNOWLEDGE_STATUSES.has(tip2.knowledgeStatus)
                );
                
                if (!hasUnreadConcepts) {
                    setUnreadTopics(prevTopics => {
                        const newTopicsSet = new Set(prevTopics);
                        newTopicsSet.delete(tip.topicId);
                        return newTopicsSet;
                    });
                }
                return newSet;
            });
            
            // Show follow-up modal only if the tip was previously unread AND skipFollowUpModal is false
            // AND we haven't shown it for this tip yet
            if (!READ_KNOWLEDGE_STATUSES.has(originalStatus) && !skipFollowUpModal) {
                // Check if we've already shown the follow-up modal for this tip
                if (!shownFollowUpModalFor.has(tip.id)) {
                    showFollowUpModal(newStatus, tip);
                    setShownFollowUpModalFor(prev => {
                        const newSet = new Set(prev);
                        newSet.add(tip.id);
                        return newSet;
                    });
                }
            }
        }
        
        try {
            await authenticatedFetch(`${API_ENDPOINTS.TIPS}/${tip.id}/status`, {
                method: 'PUT', body: JSON.stringify({ status: newStatus })
            });
        } catch (error) {
            alert(t('failedToUpdateStatus'));
            const revertedState = { ...appData.tips, [tip.topicId]: appData.tips[tip.topicId].map(t => t.id === tip.id ? { ...t, knowledgeStatus: originalStatus, dateLearned: tip.dateLearned } : t)};
            setAppData(prev => ({...prev, tips: revertedState}));
            return;
        }
    };

    const proceedWithNewConversation = async (tipId, userStatus) => {
        // Use a stable request ID for duplicate prevention
        const stableRequestId = `chat-start-${tipId}`;
        
        // Check if there's already a pending chat request for this tip
        if (isRequestPending(stableRequestId)) {
            return;
        }
        
        addPendingRequest(stableRequestId);
        setChattingTipId(tipId);
        
        // Auto-transition to full mode when bot starts typing and conversation is empty
        const currentConversation = appData.conversations[tipId] || [];
        if (currentConversation.length === 0 && mainAreaState === 'tip-only') {
            setMainAreaState('full'); // Direct state change for auto-transition
            // Also persist this change
            if (selectedTip) {
                updateTristateInDatabase(selectedTip, 'full');
            }
        }
        
        setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [tipId]: [] }}));
        
        try {
            // Get the topic language for this tip
            const tip = tipsForSelectedTopic.find(t => t.id === tipId);
            const topic = appData.topics.find(t => t.id === tip?.topicId);
            const contentLanguage = topic?.contentLanguage || 'en';
            
            const response = await authenticatedFetch(API_ENDPOINTS.CHAT_START, { 
                method: 'POST', 
                body: JSON.stringify({ 
                    tipId, 
                    userStatus,
                    contentLanguage // Pass the topic language code to the backend
                }) 
            });
            const botMessage = await response.json();
            playSound('newMessage');
            setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [tipId]: [botMessage] }}));
        } catch (error) {
        } 
        finally { 
            setChattingTipId(null);
            removePendingRequest(stableRequestId);
        }
    };
    
    const clearConversationHistory = async (tipId) => {
        try {
            const response = await authenticatedFetch(`${API_ENDPOINTS.TIPS}/${tipId}/conversation`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                throw new Error('Failed to clear conversation history');
            }
            
            // Clear the local state immediately
            setAppData(prev => ({
                ...prev,
                conversations: {
                    ...prev.conversations,
                    [tipId]: []
                }
            }));
            
            return true;
        } catch (error) {
            console.error('Error clearing conversation history:', error);
            return false;
        }
    };

    const startAutomatedConversation = async (tipId, userStatus, skipKnowledgeLevelModal = false) => {
        // If skipKnowledgeLevelModal is true, skip the modal and proceed
        if (!skipKnowledgeLevelModal) {
            // Check if the concept is in unread state
            const tip = tipsForSelectedTopic.find(t => t.id === tipId);
            if (tip && !READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus)) {
                setConceptForKnowledgeLevel(tip);
                setIsSelectKnowledgeLevelModalOpen(true);
                return;
            }
        }

        if (userStatus === 'skip') {
            setIsRateConceptModalOpen(true);
            return;
        }
        
        const existingConversation = appData.conversations[tipId];
        if (existingConversation && existingConversation.length > 0) {
            // Show confirmation modal before clearing conversation
            setConfirmModalState({
                isOpen: true, 
                title: t('overwriteConversation'),
                message: t('overwriteMessage'),
                confirmText: t('eraseAndStartNew'),
                cancelText: t('cancel'),
                onConfirm: async () => { 
                    setConfirmModalState({ isOpen: false });
                    // Clear the existing conversation history first
                    const cleared = await clearConversationHistory(tipId);
                    if (cleared) {
                        // Proceed with new conversation after clearing history
                        proceedWithNewConversation(tipId, userStatus);
                    } else {
                        // If clearing failed, show error notification
                        showNotification(t('failedToClearConversation') || 'Failed to clear conversation history', 'error');
                    }
                },
                onCancel: () => setConfirmModalState({ isOpen: false })
            });
        } else {
            proceedWithNewConversation(tipId, userStatus);
        }
    };

    const handleRateThenChat = async (newStatus) => {
        if (!selectedTip || !newStatus) return;
        await handleUpdateTipStatus(newStatus);
        proceedWithNewConversation(selectedTip.id, newStatus);
    };
    
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userInput.trim() || isTipChatting(selectedTip?.id) || !selectedTip) return;
        
        const requestId = generateRequestId('chat-message', selectedTip.id);
        
        // Check if there's already a pending chat message request for this tip
        if (isRequestPending(requestId)) {
            return;
        }
        
        addPendingRequest(requestId);
        const userMessage = { role: 'user', text: userInput, timestamp: Date.now() };
        const currentConvo = appData.conversations[selectedTip.id] || [];
        setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage] }}));
        
        // Check if using premium model and start timer for slow chat notification
        if (userProfile?.preferredModel === 'gemini-2.5-pro' && !userProfile?.hideSlowChatNotification) {
            // Show notification after 10 seconds of waiting
            clearTimeout(slowChatTimeoutRef.current);
            slowChatTimeoutRef.current = setTimeout(() => {
                setShowSlowChatNotification(true);
            }, 10000);
        }
        
        setChattingTipId(selectedTip.id);
        setUserInput('');
        try {
            // Get the topic language for this tip
            const topic = appData.topics.find(t => t.id === selectedTip.topicId);
            const contentLanguage = topic?.contentLanguage || 'en';
            
            const response = await authenticatedFetch(API_ENDPOINTS.CHAT, {
                method: 'POST', 
                body: JSON.stringify({ 
                    tipId: selectedTip.id, 
                    message: userInput, 
                    conversationHistory: currentConvo,
                    contentLanguage // Pass the topic language code to the backend
                })
            });
            if (!response.ok) throw new Error(t('networkResponseNotOk'));
            const botMessage = await response.json();
            playSound('newMessage');
            setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage, botMessage] }}));
            
            // Clear slow chat notification timer since response was received
            clearTimeout(slowChatTimeoutRef.current);
            setShowSlowChatNotification(false);
        } catch (error) {
            // Clear slow chat notification timer on error too
            clearTimeout(slowChatTimeoutRef.current);
            setShowSlowChatNotification(false);
            
            let errorText = t('networkError');
            
            // Provide more specific error messages
            if (error.name === 'AbortError') {
                errorText = t('requestTimeout') || 'Request timed out. The AI is taking longer than expected. Please try again.';
            } else if (error.message?.includes('Network response was not ok')) {
                errorText = t('serverError') || 'Server error. Please try again in a moment.';
            }
            
            const errorMessage = { 
                role: 'bot', 
                text: errorText, 
                timestamp: Date.now(),
                isError: true,
                retryable: true,
                originalUserMessage: userInput,
                messageId: `error_${Date.now()}`
            };
            setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage, errorMessage] }}));
        } finally { 
            setChattingTipId(null);
            removePendingRequest(requestId);
        }
    };

    // Quick response handler
    const handleQuickResponse = async (message) => {
        if (isTipChatting(selectedTip?.id) || !selectedTip) return;
        
        const requestId = generateRequestId('chat-message', selectedTip.id);
        
        // Check if there's already a pending chat message request for this tip
        if (isRequestPending(requestId)) {
            return;
        }
        
        addPendingRequest(requestId);
        const userMessage = { role: 'user', text: message, timestamp: Date.now() };
        const currentConvo = appData.conversations[selectedTip.id] || [];
        setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage] }}));
        

        
        setChattingTipId(selectedTip.id);
        try {
            // Get the topic language for this tip
            const topic = appData.topics.find(t => t.id === selectedTip.topicId);
            const contentLanguage = topic?.contentLanguage || 'en';
            
            const response = await authenticatedFetch(API_ENDPOINTS.CHAT, {
                method: 'POST', 
                body: JSON.stringify({ 
                    tipId: selectedTip.id, 
                    message: message, 
                    conversationHistory: currentConvo,
                    contentLanguage // Pass the topic language code to the backend
                })
            });
            if (!response.ok) throw new Error(t('networkResponseNotOk'));
            const botMessage = await response.json();
            playSound('newMessage');
            setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage, botMessage] }}));
        } catch (error) {
            let errorText = t('networkError');
            
            // Provide more specific error messages
            if (error.name === 'AbortError') {
                errorText = t('requestTimeout') || 'Request timed out. The AI is taking longer than expected. Please try again.';
            } else if (error.message?.includes('Network response was not ok')) {
                errorText = t('serverError') || 'Server error. Please try again in a moment.';
            }
            
            const errorMessage = { 
                role: 'bot', 
                text: errorText, 
                timestamp: Date.now(),
                isError: true,
                retryable: true,
                originalUserMessage: message,
                messageId: `error_${Date.now()}`
            };
            setAppData(prev => ({ ...prev, conversations: { ...prev.conversations, [selectedTip.id]: [...currentConvo, userMessage, errorMessage] }}));
        } finally { 
            setChattingTipId(null);
            removePendingRequest(requestId);
        }
    };
    
    // Handler for retrying failed messages
    const handleRetryMessage = async (errorMsg, conversationIndex) => {
        if (!selectedTip || isTipChatting(selectedTip?.id)) return;
        
        const requestId = generateRequestId('chat-message-retry', selectedTip.id);
        
        // Check if there's already a pending request
        if (isRequestPending(requestId)) {
            return;
        }
        
        addPendingRequest(requestId);
        
        // Update the error message to show "Retrying..."
        setAppData(prev => {
            const updatedConversations = { ...prev.conversations };
            const tipConvo = [...(updatedConversations[selectedTip.id] || [])];
            tipConvo[conversationIndex] = {
                ...errorMsg,
                text: t('retrying') || 'Retrying...',
                isRetrying: true
            };
            updatedConversations[selectedTip.id] = tipConvo;
            return { ...prev, conversations: updatedConversations };
        });
        
        setChattingTipId(selectedTip.id);
        
        try {
            // Get the topic language for this tip
            const topic = appData.topics.find(t => t.id === selectedTip.topicId);
            const contentLanguage = topic?.contentLanguage || 'en';
            
            // Get conversation history up to the error message (excluding the error itself)
            const conversationHistory = (appData.conversations[selectedTip.id] || []).slice(0, conversationIndex - 1);
            
            const response = await authenticatedFetch(API_ENDPOINTS.CHAT, {
                method: 'POST',
                body: JSON.stringify({
                    tipId: selectedTip.id,
                    message: errorMsg.originalUserMessage,
                    conversationHistory,
                    contentLanguage
                })
            });
            
            if (!response.ok) throw new Error(t('networkResponseNotOk'));
            const botMessage = await response.json();
            playSound('newMessage');
            
            // Replace the error message with the successful response
            setAppData(prev => {
                const updatedConversations = { ...prev.conversations };
                const tipConvo = [...(updatedConversations[selectedTip.id] || [])];
                tipConvo[conversationIndex] = botMessage;
                updatedConversations[selectedTip.id] = tipConvo;
                return { ...prev, conversations: updatedConversations };
            });
        } catch (error) {
            let errorText = t('networkError');
            
            // Provide more specific error messages
            if (error.name === 'AbortError') {
                errorText = t('requestTimeout') || 'Request timed out. The AI is taking longer than expected. Please try again.';
            } else if (error.message?.includes('Network response was not ok')) {
                errorText = t('serverError') || 'Server error. Please try again in a moment.';
            }
            
            // Update the error message with new error text
            setAppData(prev => {
                const updatedConversations = { ...prev.conversations };
                const tipConvo = [...(updatedConversations[selectedTip.id] || [])];
                tipConvo[conversationIndex] = {
                    ...errorMsg,
                    text: errorText,
                    isRetrying: false,
                    retryCount: (errorMsg.retryCount || 0) + 1
                };
                updatedConversations[selectedTip.id] = tipConvo;
                return { ...prev, conversations: updatedConversations };
            });
        } finally {
            setChattingTipId(null);
            removePendingRequest(requestId);
        }
    };
    
    const handleConceptSelection = (tipId) => {
        
        // Clear chatting state when switching to a different concept
        if (chattingTipId && chattingTipId !== tipId) {
            setChattingTipId(null);
        }
        
        setSelectedTipId(tipId);
        
        // Find the selected tip
        const concept = tipsForSelectedTopic.find(tip => tip.id === tipId);
        if (concept) {
            
            // Don't clear review-due state here anymore - it will be cleared when user submits review answer
            
            // Always set the tristate to the tip's saved state (or default to 'tip-only')
            const savedTristateState = concept.tristateState || 'tip-only';
            setMainAreaState(savedTristateState);
            
            // Check if this tip has conversation history
            const tipConversation = appData.conversations[tipId] || [];
            const hasHistory = tipConversation.length > 0;
            setHasConversationHistory(hasHistory);
            

            
            // Only show modal if this tip is unread
            if (unreadConcepts.has(tipId)) {
                setNewlyGeneratedConcept(concept);
                setIsNewConceptModalOpen(true);
            } else {
            }
        } else {
        }
    };
    
    const handleUpdateTipHidden = async (isHidden, conceptOverride = null) => {
        const tip = conceptOverride || selectedTip;
        if (!tip) return;
        
        // Always get the current tip state from appData.tips to ensure we have the latest knowledgeStatus
        const currentTipState = appData.tips[tip.topicId]?.find(t => t.id === tip.id);
        const tipToUse = currentTipState || tip;
        
        const optimisticState = { ...appData.tips, [tipToUse.topicId]: appData.tips[tipToUse.topicId].map(t => t.id === tipToUse.id ? { ...t, isHidden } : t)};
        setAppData(prev => ({...prev, tips: optimisticState }));
        
        try {
            await authenticatedFetch(`${API_ENDPOINTS.TIPS}/${tipToUse.id}/hide`, {
                method: 'PUT', body: JSON.stringify({ isHidden })
            });
            
            // Refresh data from backend to ensure we have the latest state
            const response = await authenticatedFetch(API_ENDPOINTS.DATA);
            const data = await response.json();
            
            // Update appData with refreshed data
            setAppData(data);
            
            // Re-initialize unread concepts from refreshed data
            const unreadConceptIds = new Set();
            const unreadTopicIds = new Set();
            
            // Check all tips across all topics for unread status
            Object.values(data.tips || {}).forEach(tipsForTopic => {
                tipsForTopic.forEach(tip => {
                    // A tip is unread if its knowledgeStatus is not one of the 6 read statuses
                    if (!READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus)) {
                        unreadConceptIds.add(tip.id);
                        unreadTopicIds.add(tip.topicId);
                    }
                });
            });
            
            setUnreadConcepts(unreadConceptIds);
            setUnreadTopics(unreadTopicIds);
            
        } catch (error) {
            alert(t('failedToUpdateStatus'));
            const revertedState = { ...appData.tips, [tipToUse.topicId]: appData.tips[tipToUse.topicId].map(t => t.id === tipToUse.id ? { ...t, isHidden: !isHidden } : t)};
            setAppData(prev => ({...prev, tips: revertedState}));
            return;
        }
    };

    const handleUpdateTipFavorite = async (isFavorite, conceptOverride = null) => {
        const tip = conceptOverride || selectedTip;
        if (!tip) return;
        const optimisticState = { ...appData.tips, [tip.topicId]: appData.tips[tip.topicId].map(t => t.id === tip.id ? { ...t, isFavorite } : t)};
        setAppData(prev => ({...prev, tips: optimisticState }));
        try {
            await authenticatedFetch(API_ENDPOINTS.TIP_FAVORITE(tip.id), {
                method: 'PUT', body: JSON.stringify({ isFavorite })
            });
        } catch (error) {
            alert(t('failedToUpdateStatus'));
            const revertedState = { ...appData.tips, [tip.topicId]: appData.tips[tip.topicId].map(t => t.id === tip.id ? { ...t, isFavorite: !isFavorite } : t)};
            setAppData(prev => ({...prev, tips: revertedState}));
            return;
        }
    };

    const handleUpdateTopicFavorite = async (topicId, isFavorite) => {
        const optimisticState = appData.topics.map(t => t.id === topicId ? { ...t, isFavorite } : t);
        setAppData(prev => ({...prev, topics: optimisticState }));
        try {
            await authenticatedFetch(`${API_ENDPOINTS.TOPICS}/${topicId}/favorite`, {
                method: 'PUT', body: JSON.stringify({ isFavorite })
            });
        } catch (error) {
            alert(t('failedToUpdateStatus'));
            const revertedState = appData.topics.map(t => t.id === topicId ? { ...t, isFavorite: !isFavorite } : t);
            setAppData(prev => ({...prev, topics: revertedState}));
            return;
        }
    };

    // Handler to generate review question (development only)
    const handleGenerateReviewQuestion = (tip) => {
        if (!tip) return;
        setSelectedTipId(tip.id);
        setIsGenerateReviewModalOpen(true);
    };

    // Handler to open review modal with pre-calculated initial slide
    const handleOpenReviewModal = async () => {
        if (!selectedTip?.id) return;
        
        try {
            // Pre-fetch review questions to calculate initial slide
            const response = await authenticatedFetch(`/api/review-questions/${selectedTip.id}`);
            
            if (response.ok) {
                const data = await response.json();
                const reviewQuestions = data.reviewQuestions || [];
                
                // Find the latest unlocked review
                let latestUnlockedIndex = 0;
                for (let i = reviewQuestions.length - 1; i >= 0; i--) {
                    if (reviewQuestions[i]?.unlocked) {
                        latestUnlockedIndex = REVIEW_SCHEDULE.findIndex(day => day === reviewQuestions[i].reviewDay);
                        break;
                    }
                }
                
                setReviewModalInitialSlide(latestUnlockedIndex);
            }
        } catch (error) {
            setReviewModalInitialSlide(0); // Default to first slide on error
        }
        
        // Open the modal
        setIsReviewQuestionModalOpen(true);
    };

    const handleConfirmGenerateReviewQuestion = async () => {
        if (!selectedTip || isGeneratingReviewQuestion) return;
        
        setIsGeneratingReviewQuestion(true);
        
        try {
            const response = await authenticatedFetch('/api/review-questions/generate-dev', {
                method: 'POST',
                body: JSON.stringify({ 
                    tipId: selectedTip.id, 
                    reviewDayNumber: selectedReviewDay 
                })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate review question');
            }
            
            await response.json(); // Consume the response
            alert(`Review question generated successfully for Day ${selectedReviewDay}!`);
            setIsGenerateReviewModalOpen(false);
            
        } catch (error) {
            alert(`Failed to generate review question: ${error.message}`);
        } finally {
            setIsGeneratingReviewQuestion(false);
        }
    };

    // Handler to toggle review reminder for a tip
    const handleToggleReviewReminder = async (tip, forceValue = null, skipOptimisticUpdate = false) => {
        // For force enabling from the follow-up modal, we may need to check the updated tip state
        // since the tip's knowledge status was just updated
        if (!tip) return;
        
        // If forceValue is true, we're enabling from the modal after setting status to 'new'
        // In this case, the tip should already have a read status from the previous update
        if (forceValue === true && !isTipRead(tip)) {
            // Get the latest tip state that should have the updated knowledge status
            const latestTip = appData.tips[tip.topicId]?.find(t => t.id === tip.id);
            if (latestTip && isTipRead(latestTip)) {
                tip = latestTip;
            } else if (!isTipRead(tip)) {
                // If still not read, skip since review requires read status
                return;
            }
        } else if (!isTipRead(tip)) {
            return;
        }
        
        const newValue = forceValue !== null ? forceValue : !tip.isScheduledForReview;
        
        // Skip optimistic update if already done (e.g., from the modal)
        if (!skipOptimisticUpdate) {
            // Optimistically update state
            setAppData(prev => {
                const updatedTips = { ...prev.tips };
                updatedTips[tip.topicId] = updatedTips[tip.topicId].map(t => 
                    t.id === tip.id ? { ...t, isScheduledForReview: newValue } : t
                );
                return { ...prev, tips: updatedTips };
            });
        }
        
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TIP_REVIEW_SCHEDULE(tip.id), {
                method: 'PUT',
                body: JSON.stringify({ isScheduledForReview: newValue })
            });
            
            if (response.ok) {
                // Get the updated tip data from the backend to ensure we have the correct next_review_due_date and review_count
                const dataResponse = await authenticatedFetch(API_ENDPOINTS.DATA);
                if (dataResponse.ok) {
                    const data = await dataResponse.json();
                    setAppData(data);
                }
            }
        } catch (error) {
            // Revert on error
            setAppData(prev => {
                const updatedTips = { ...prev.tips };
                updatedTips[tip.topicId] = updatedTips[tip.topicId].map(t => 
                    t.id === tip.id ? { ...t, isScheduledForReview: !newValue } : t
                );
                return { ...prev, tips: updatedTips };
            });
            alert(t('failedToUpdateStatus'));
        }
    };

    // Handler to clear review-due state when user actively engages with a tip
    const handleClearReviewDue = async (tipId) => {
        try {
            const response = await authenticatedFetch(API_ENDPOINTS.TIP_CLEAR_REVIEW_DUE(tipId), {
                method: 'PUT'
            });
            
            if (response.ok) {
                const result = await response.json();
                
                // Update the specific tip in our local state
                if (result.tip) {
                    setAppData(prevData => {
                        const newData = { ...prevData };
                        // Find the topic that contains this tip
                        for (const topicId in newData.tips) {
                            const tipIndex = newData.tips[topicId].findIndex(t => t.id === tipId);
                            if (tipIndex !== -1) {
                                newData.tips[topicId][tipIndex] = result.tip;
                                break;
                            }
                        }
                        return newData;
                    });
                    
                    // If this is the currently selected tip, force update of lastTipUpdate
                    // This ensures components dependent on tip data will re-render
                    if (selectedTipId === tipId) {
                        setLastTipUpdate(Date.now());
                    }
                } else {
                    // Fallback to fetching all data if tip not returned
                    const dataResponse = await authenticatedFetch(API_ENDPOINTS.DATA);
                    if (dataResponse.ok) {
                        const data = await dataResponse.json();
                        setAppData(data);
                    }
                }
            } else {
                // Error details available if needed
                await response.text();
            }
        } catch (error) {
            // Don't show error to user as this is a background operation
        }
    };

    // Handler to reset review progress for a tip
    const handleResetReviewProgress = async (tipId) => {
        try {
            await authenticatedFetch(API_ENDPOINTS.TIP_RESET_REVIEW(tipId), {
                method: 'PUT'
            });
            
            // Fetch fresh data to get the correct updated values from backend
            const dataResponse = await authenticatedFetch(API_ENDPOINTS.DATA);
            if (dataResponse.ok) {
                const data = await dataResponse.json();
                setAppData(data);
            }
        } catch (error) {
            alert(t('failedToUpdateStatus'));
        }
    };

    // Helper to calculate days until next review
    const getDaysUntilNextReview = (nextReviewDueDate) => {
        if (!nextReviewDueDate) return null;
        
        const now = new Date();
        const reviewDate = new Date(nextReviewDueDate);
        
        // Calculate the difference in days by comparing UTC dates (not local dates)
        // This ensures consistent calculation regardless of user's timezone
        const nowUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const reviewDateUTC = new Date(Date.UTC(reviewDate.getUTCFullYear(), reviewDate.getUTCMonth(), reviewDate.getUTCDate()));
        const diffTime = reviewDateUTC.getTime() - nowUTC.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        
        
        return diffDays;
    };

    // Helper to calculate hours until next review (for when it's less than 24 hours)
    const getHoursUntilNextReview = (nextReviewDueDate) => {
        if (!nextReviewDueDate) return null;
        
        const now = new Date();
        const reviewDate = new Date(nextReviewDueDate);
        
        // Calculate the difference in milliseconds
        const diffTime = reviewDate.getTime() - now.getTime();
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
        
        return diffHours;
    };

    // Helper to get review status text
    const getReviewStatusText = (tip) => {
        if (!tip.isScheduledForReview) return null;
        
        // If review is due, show "Review due"
        if (tip.reviewIsDue) {
            return t('reviewDue');
        }
        
        // There are 6 reviews in the schedule (days 1, 3, 7, 14, 30, 60).
        // reviewCount tracks how many have been completed (0-6).
        // If reviewCount is 6, all reviews are complete.
        if (tip.reviewCount >= 6) {
            return t('reviewSequenceCompleted');
        }
        
        // If no next review date, it means the review is being scheduled.
        // Returning null prevents a misleading message during the optimistic update flicker.
        if (!tip.nextReviewDueDate) {
            return null;
        }
        
        // Calculate days until next review
        const daysUntil = getDaysUntilNextReview(tip.nextReviewDueDate);
        
        
        // If past due date (negative days), show "Overdue"
        if (daysUntil < 0) {
            return t('reviewOverdue');
        }
        
        // If it's less than 24 hours (daysUntil === 0), calculate and show hours
        if (daysUntil === 0) {
            const hoursUntil = getHoursUntilNextReview(tip.nextReviewDueDate);
            
            // If review has already passed, show "Review due"
            if (hoursUntil <= 0) {
                return t('reviewDue');
            }
            
            // Show hours until next review
            if (hoursUntil === 1) return t('nextReviewIn') + ' ' + hoursUntil + ' ' + t('hour');
            return t('nextReviewIn') + ' ' + hoursUntil + ' ' + t('hours');
        }
        
        // Show days until next review
        if (daysUntil === 1) return t('nextReviewIn') + ' ' + daysUntil + ' ' + t('day');
        return t('nextReviewIn') + ' ' + daysUntil + ' ' + t('reviewDays');
    };


    // Add state to track deleting topic and concept
    const [deletingTopicId, setDeletingTopicId] = useState(null);
    const [deletingTipId, setDeletingTipId] = useState(null);
    
    
    // Extract generating topic IDs for dependency array
    // Include topics that have generation flags OR have pending requests
    const currentGeneratingTopicIds = useMemo(() => {
        const generatingFromFlags = appData?.topics?.filter(topic => 
            topic.isGeneratingTip || topic.isGeneratingResponse || topic.isGeneratingTopic || topic.isGeneratingSummary
        ).map(t => t.id) || [];
        
        // Extract topic IDs from pending requests (e.g., "learning-summary-topic_123" -> "topic_123")
        const generatingFromRequests = Array.from(pendingRequests).map(requestId => {
            const match = requestId.match(/learning-summary-(topic_\d+)/);
            return match ? match[1] : null;
        }).filter(Boolean);
        
        // Combine and deduplicate
        const allGeneratingIds = [...new Set([...generatingFromFlags, ...generatingFromRequests])];
        
        
        return allGeneratingIds;
    }, [appData?.topics, pendingRequests]);
    
    // Memoize the generating topics fingerprint to avoid re-running effect unnecessarily
    const generatingTopicsFingerprint = useMemo(() => {
        return currentGeneratingTopicIds.join(',');
    }, [currentGeneratingTopicIds]);
    
    // Polling effect to check for completed tip generation
    useEffect(() => {
        if (!userId || currentGeneratingTopicIds.length === 0) {
            return;
        }
        
        // Get current generating topics (tip generation, response generation, topic generation, and summary generation)
        const generatingTopics = appData.topics.filter(topic => topic.isGeneratingTip || topic.isGeneratingResponse || topic.isGeneratingTopic || topic.isGeneratingSummary);
        
        
        
        // Store the current appData.tips and conversations to compare against
        const currentTips = appData.tips;
        const currentConversations = appData.conversations;
        
        const pollInterval = setInterval(async () => {
            try {
                const response = await authenticatedFetch(API_ENDPOINTS.DATA);
                if (response.ok) {
                    const newData = await response.json();
                    
                    // Check for completed tip generations and collect all new tips
                    let hasNewTips = false;
                    const allNewTips = [];
                    
                    
                    for (const topic of generatingTopics) {
                        const updatedTopic = newData.topics.find(t => t.id === topic.id);
                        
                        // If generation completed (flag cleared) and we have new tips
                        if (updatedTopic && !updatedTopic.isGeneratingTip && !updatedTopic.isGeneratingTopic) {
                            const newTips = newData.tips[topic.id] || [];
                            const oldTipIds = new Set((currentTips[topic.id] || []).map(tip => tip.id));
                            
                            // Find newly added tips
                            const newlyAddedTips = newTips.filter(newTip => !oldTipIds.has(newTip.id));
                            
                            if (newlyAddedTips.length > 0) {
                                hasNewTips = true;
                                allNewTips.push(...newlyAddedTips.map(tip => ({ tip, topic: updatedTopic })));
                            }
                        }
                    }
                    
                    // Check for new conversation messages
                    let hasNewConversations = false;
                    const newConversationMessages = [];
                    
                    for (const tipId in newData.conversations) {
                        const newMessages = newData.conversations[tipId] || [];
                        const oldMessages = currentConversations[tipId] || [];
                        
                        if (newMessages.length > oldMessages.length) {
                            hasNewConversations = true;
                            const newlyAddedMessages = newMessages.slice(oldMessages.length);
                            newConversationMessages.push(...newlyAddedMessages.filter(msg => msg.role === 'bot'));
                        }
                    }
                    
                    // Check if we actually need to update the state
                    // This prevents unnecessary re-renders that would clear text selection
                    const shouldUpdate = hasNewTips || hasNewConversations || 
                        generatingTopics.some(topic => {
                            const updatedTopic = newData.topics.find(t => t.id === topic.id);
                            return updatedTopic && (
                                updatedTopic.isGeneratingTip !== topic.isGeneratingTip ||
                                updatedTopic.isGeneratingResponse !== topic.isGeneratingResponse ||
                                updatedTopic.isGeneratingTopic !== topic.isGeneratingTopic ||
                                updatedTopic.isGeneratingSummary !== topic.isGeneratingSummary ||
                                updatedTopic.learningSummary !== topic.learningSummary
                            );
                        });
                    
                    if (shouldUpdate) {
                        // Save current selection before updating
                        const selection = window.getSelection();
                        const savedSelection = selection && selection.rangeCount > 0 ? {
                            range: selection.getRangeAt(0),
                            anchorNode: selection.anchorNode,
                            anchorOffset: selection.anchorOffset,
                            focusNode: selection.focusNode,
                            focusOffset: selection.focusOffset
                        } : null;
                        
                        // Update app data to get the latest state (including summaries)
                        // Preserve temporary topics that exist locally but not on server
                        setAppData(prev => {
                            const tempTopics = prev.topics.filter(t => t.isTemp && t.id.startsWith('temp-'));
                            const serverTopics = newData.topics || [];
                            const mergedTopics = [...tempTopics, ...serverTopics];
                            
                            // Preserve local error messages in conversations unless server has newer messages
                            const mergedConversations = { ...newData.conversations };
                            for (const tipId in prev.conversations) {
                                const localMessages = prev.conversations[tipId] || [];
                                const serverMessages = newData.conversations[tipId] || [];
                                
                                // Check if local has error messages that might be temporary
                                if (localMessages.length > serverMessages.length) {
                                    const lastLocalMessage = localMessages[localMessages.length - 1];
                                    // Only preserve if the last message is an error message
                                    // Check for common error message patterns in multiple languages
                                    if (lastLocalMessage && lastLocalMessage.role === 'bot' && 
                                        (lastLocalMessage.text.includes('Sorry, I had trouble connecting to the AI') || 
                                         lastLocalMessage.text.includes('抱歉，我无法连接到AI') || 
                                         lastLocalMessage.text.includes('申し訳ございませんが、AIに接続できませんでした') ||
                                         lastLocalMessage.text.toLowerCase().includes('error') ||
                                         lastLocalMessage.text.toLowerCase().includes('failed'))) {
                                        // Keep local conversation with error message
                                        mergedConversations[tipId] = localMessages;
                                    }
                                }
                            }
                            
                            return { ...newData, topics: mergedTopics, conversations: mergedConversations };
                        });
                        
                        // Restore selection after state update
                        if (savedSelection) {
                            // Use requestAnimationFrame to ensure DOM has updated
                            requestAnimationFrame(() => {
                                try {
                                    const selection = window.getSelection();
                                    selection.removeAllRanges();
                                    
                                    // Check if the saved nodes still exist in the DOM
                                    if (document.contains(savedSelection.anchorNode) && 
                                        document.contains(savedSelection.focusNode)) {
                                        selection.setBaseAndExtent(
                                            savedSelection.anchorNode,
                                            savedSelection.anchorOffset,
                                            savedSelection.focusNode,
                                            savedSelection.focusOffset
                                        );
                                    }
                                } catch (error) {
                                    // Silently fail if we can't restore selection
                                    // This can happen if the selected elements were replaced
                                }
                            });
                        }
                    }
                    
                    // If we found any new tips or conversations, process them
                    if (hasNewTips || hasNewConversations) {
                        if (hasNewTips) {
                        }
                        if (hasNewConversations) {
                        }
                        
                        // Trigger notifications for each new tip
                        for (const { tip, topic } of allNewTips) {
                            handleNewConcept(tip, topic);
                        }
                        
                        // Play sound for new conversation messages
                        if (newConversationMessages.length > 0) {
                            playSound('newMessage');
                        }
                    }
                    
                    // Check if any topics are still generating (tips, responses, topics, or summaries)
                    const stillGenerating = newData.topics.filter(topic => topic.isGeneratingTip || topic.isGeneratingResponse || topic.isGeneratingTopic || topic.isGeneratingSummary);
                    
                    // Only stop polling if no topics are generating anymore
                    if (stillGenerating.length === 0) {
                        clearInterval(pollInterval);
                        return;
                    }
                }
            } catch (error) {
            }
        }, 3000); // Poll every 3 seconds
        
        // Clean up polling after 5 minutes to prevent infinite polling
        const timeout = setTimeout(() => {
            clearInterval(pollInterval);
        }, 5 * 60 * 1000);
        
        return () => {
            clearInterval(pollInterval);
            clearTimeout(timeout);
        };
    }, [userId, currentGeneratingTopicIds, currentGeneratingTopicIds.length, generatingTopicsFingerprint, appData.topics, appData.tips, appData.conversations, authenticatedFetch, handleNewConcept, selectedTipId, setAppData]);

    // Update topicToEdit when appData.topics changes to ensure EditTopicModal gets latest data
    useEffect(() => {
        if (topicToEdit && appData.topics) {
            const updatedTopic = appData.topics.find(t => t.id === topicToEdit.id);
            if (updatedTopic) {
                // Only update if there are actual changes to relevant fields
                const hasRelevantChanges = (
                    updatedTopic.learningSummary !== topicToEdit.learningSummary ||
                    updatedTopic.isGeneratingSummary !== topicToEdit.isGeneratingSummary ||
                    updatedTopic.name !== topicToEdit.name ||
                    updatedTopic.knowledgeLevel !== topicToEdit.knowledgeLevel ||
                    updatedTopic.learningPurpose !== topicToEdit.learningPurpose ||
                    updatedTopic.contentLanguage !== topicToEdit.contentLanguage ||
                    updatedTopic.description !== topicToEdit.description ||
                    updatedTopic.includeSummaryInContext !== topicToEdit.includeSummaryInContext ||
                    updatedTopic.isScheduled !== topicToEdit.isScheduled ||
                    updatedTopic.scheduleTimeHour !== topicToEdit.scheduleTimeHour ||
                    updatedTopic.scheduleTimePeriod !== topicToEdit.scheduleTimePeriod ||
                    updatedTopic.scheduleFrequencyValue !== topicToEdit.scheduleFrequencyValue ||
                    updatedTopic.scheduleFrequencyUnit !== topicToEdit.scheduleFrequencyUnit ||
                    updatedTopic.scheduledSkipIfUnread !== topicToEdit.scheduledSkipIfUnread ||
                    updatedTopic.userTimezone !== topicToEdit.userTimezone
                );
                
                if (hasRelevantChanges) {
                    setTopicToEdit(updatedTopic);
                }
            }
        }
    }, [appData.topics, topicToEdit]);

    // Auto-resize textarea helper
    const autoResizeTextarea = useCallback((element) => {
        if (element) {
            element.style.height = 'auto';
            element.style.height = Math.min(element.scrollHeight, 120) + 'px';
        }
    }, []);

    // Three-State Feature Helper Functions
    
    // Function to update tristate state in database (define first to avoid circular dependency)
    const updateTristateInDatabase = useCallback(async (tipToUpdate, newTristateState) => {
        if (!tipToUpdate) return;
        const { id: tipId, topicId } = tipToUpdate;

        // Optimistically update local state
        setAppData(prev => {
            const updatedTipsForTopic = (prev.tips[topicId] || []).map(t =>
                t.id === tipId ? { ...t, tristateState: newTristateState } : t
            );
            return {
                ...prev,
                tips: {
                    ...prev.tips,
                    [topicId]: updatedTipsForTopic,
                },
            };
        });
        
        try {
            const response = await authenticatedFetch(`${API_ENDPOINTS.TIPS}/${tipId}/tristate`, {
                method: 'PUT',
                body: JSON.stringify({ tristateState: newTristateState })
            });
            
            if (!response.ok) {
                // Revert on failure
                setAppData(prev => {
                    const revertedTipsForTopic = (prev.tips[topicId] || []).map(t =>
                        t.id === tipId ? tipToUpdate : t // Revert to the original tip object
                    );
                    return {
                        ...prev,
                        tips: {
                            ...prev.tips,
                            [topicId]: revertedTipsForTopic,
                        },
                    };
                });
            } else {
            }
        } catch (error) {
            // Revert on error
            setAppData(prev => {
                const revertedTipsForTopic = (prev.tips[topicId] || []).map(t =>
                    t.id === tipId ? tipToUpdate : t // Revert to the original tip object
                );
                return {
                    ...prev,
                    tips: {
                        ...prev.tips,
                        [topicId]: revertedTipsForTopic,
                    },
                };
            });
        }
    }, [authenticatedFetch, setAppData]);

    const changeMainAreaState = useCallback((newState) => {
        
        
        if (newState === mainAreaState) {
            return;
        }
        
        setMainAreaState(newState);
        
        // Persist the tristate change to database
        if (selectedTip) {
            updateTristateInDatabase(selectedTip, newState);
        }
        
        // Scroll to bottom when switching to chat-only mode
        if (newState === 'chat-only' && conversation.length > 0) {
            // Use setTimeout to ensure DOM is updated after state change
            setTimeout(() => {
                conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
        
        // Reset textarea height when switching away from chat-only mode
        if (mainAreaState === 'chat-only' && newState !== 'chat-only' && chatInputRef.current) {
            chatInputRef.current.style.height = 'auto';
        }
    }, [mainAreaState, selectedTip, updateTristateInDatabase, conversation, setMainAreaState]);

    const updateConversationHistoryState = useCallback((conversationArray) => {
        // Consider both conversation history and chatting state for the selected tip
        const isSelectedTipChatting = chattingTipId === selectedTipId;
        const hasHistory = (conversationArray && conversationArray.length > 0) || isSelectedTipChatting;
        
        if (hasHistory !== hasConversationHistory) {
            setHasConversationHistory(hasHistory);
        }
    }, [hasConversationHistory, chattingTipId, selectedTipId]);

    const getMainAreaLayout = useCallback(() => {
        
        switch (mainAreaState) {
            case 'tip-only':
                return {
                    showTipContent: true,
                    showConversation: false,
                    showResponseArea: true,
                    tipContentHeight: 'flex-1 min-h-0', // Add min-h-0 to allow proper flex shrinking
                    conversationHeight: 'hidden',
                    responseAreaHeight: 'min-h-40 flex-shrink-0' // Flexible height with minimum
                };
            case 'full':
                return {
                    showTipContent: true,
                    showConversation: true,
                    showResponseArea: true,
                    tipContentHeight: 'flex-1 min-h-0', // Add min-h-0 to allow proper flex shrinking
                    conversationHeight: 'flex-1 min-h-0', // Add min-h-0 to allow proper flex shrinking
                    responseAreaHeight: 'min-h-40 flex-shrink-0' // Flexible height with minimum
                };
            case 'chat-only':
                return {
                    showTipContent: false,
                    showConversation: true,
                    showResponseArea: true,
                    tipContentHeight: 'hidden',
                    conversationHeight: 'flex-1 min-h-0', // Add min-h-0 to allow proper flex shrinking
                    responseAreaHeight: 'min-h-40 flex-shrink-0' // Flexible height with minimum
                };
            default:
                return {
                    showTipContent: true,
                    showConversation: true,
                    showResponseArea: true,
                    tipContentHeight: 'h-64', // Fixed height for tip content in full state
                    conversationHeight: 'flex-1 min-h-0', // Add min-h-0 to allow proper flex shrinking
                    responseAreaHeight: 'min-h-40 flex-shrink-0' // Flexible height with minimum
                };
        }
    }, [mainAreaState]);

    // Effect to monitor conversation changes
    useEffect(() => {
        updateConversationHistoryState(conversation);
    }, [conversation, chattingTipId, updateConversationHistoryState]);



    // Effect to set tristate state from selected tip's persisted state
    useEffect(() => {
        
        // Only set the initial state when the tip changes, not when conversation updates
        if (prevSelectedTipIdRef.current !== selectedTipId) {
            
            if (selectedTip && selectedTip.tristateState) {
                setMainAreaState(selectedTip.tristateState);
                
                // Check if this tip has conversation history
                const hasHistory = conversation && conversation.length > 0;
                setHasConversationHistory(hasHistory);
            } else if (selectedTipId) {
                // Tip exists but no tristate state (legacy data), default to tip-only
                setMainAreaState('tip-only');
                
                // Check if this tip has conversation history
                const hasHistory = conversation && conversation.length > 0;
                setHasConversationHistory(hasHistory);
            } else {
                // No tip selected
                setMainAreaState('tip-only');
                setHasConversationHistory(false);
            }
        } else {
            // Tip hasn't changed, just update conversation history state
            const isSelectedTipChatting = chattingTipId === selectedTipId;
            const hasHistory = (conversation && conversation.length > 0) || isSelectedTipChatting;
            setHasConversationHistory(hasHistory);
        }
    }, [selectedTipId, selectedTip, conversation, chattingTipId, setMainAreaState]);

    // Effect to handle conversation history state updates
    useEffect(() => {
    }, [hasConversationHistory, mainAreaState]);

    // Effect to handle case when no tip is selected
    useEffect(() => {
        if (!selectedTipId) {
            setMainAreaState('tip-only');
            setHasConversationHistory(false);
        }
    }, [selectedTipId, setMainAreaState]);

    // Effect to auto-resize textarea in chat-only mode
    useEffect(() => {
        if (mainAreaState === 'chat-only' && chatInputRef.current && chatInputRef.current.tagName === 'TEXTAREA') {
            if (userInput.trim() === '') {
                // Reset height when input is empty
                chatInputRef.current.style.height = 'auto';
            } else {
                autoResizeTextarea(chatInputRef.current);
            }
        }
    }, [userInput, mainAreaState, autoResizeTextarea]);

    // Show email verification message if present
    if (verificationMessage || verificationError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full mx-auto">
                    <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden">
                        <div className="px-8 pt-8 pb-6">
                            <div className="text-center mb-8">
                                <div className={`mx-auto w-20 h-20 ${verificationMessage ? 'bg-gradient-to-br from-green-500 to-teal-600' : 'bg-gradient-to-br from-red-500 to-pink-600'} rounded-full flex items-center justify-center mb-4 shadow-lg`}>
                                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {verificationMessage ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                    </svg>
                                </div>
                                <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
                                    {verificationMessage ? 'Email Verified!' : 'Verification Failed'}
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {verificationMessage || verificationError}
                                </p>
                                <button
                                    onClick={() => {
                                        setVerificationMessage('');
                                        setVerificationError('');
                                    }}
                                    className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg shadow-md transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    {getTranslation('goToLogin', uiLanguage) || 'Go to Login'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    if (!userId && !showSessionTakeoverModal) return <Login onLoginSuccess={handleLoginSuccess} uiLanguage={uiLanguage} />;
    
    return (
        <>
            <ConnectionStatus status={connectionStatus} statusData={connectionStatusData} uiLanguage={uiLanguage} />
            {showSessionTakeoverModal && (
                <SessionTakeoverModal
                    onConfirm={() => {
                        setShowSessionTakeoverModal(false);
                        if (pendingSession) {
                            proceedWithLogin(pendingSession.userId, pendingSession.googleData);
                            setPendingSession(null);
                        }
                    }}
                    onCancel={() => {
                        setShowSessionTakeoverModal(false);
                        setPendingSession(null);
                        // Clear any pending session data
                        clearUserSession();
                    }}
                    uiLanguage={uiLanguage}
                />
            )}
            <GuidedTutorial 
                isActive={showGuidedTutorial}
                onDismiss={() => setShowGuidedTutorial(false)}
                onComplete={() => setShowGuidedTutorial(false)}
                uiLanguage={uiLanguage}
            />
            <TutorialMode uiLanguage={uiLanguage} onAddTopicClick={() => setIsAddTopicModalOpen(true)} />
            <AddTopicModal isOpen={isAddTopicModalOpen} onClose={() => setIsAddTopicModalOpen(false)} onAddTopic={handleAddTopic} uiLanguage={uiLanguage} topicGroups={appData.topicGroups || []} />
            <EditTopicModal 
                isOpen={isEditTopicModalOpen} 
                onClose={() => setIsEditTopicModalOpen(false)} 
                topic={topicToEdit} 
                onUpdateTopic={handleUpdateTopic} 
                onDeleteTopic={handleDeleteTopic} 
                uiLanguage={uiLanguage} 
                authenticatedFetch={authenticatedFetch} 
                hasPendingSummaryRequest={hasPendingSummaryRequestForTopic} 
                addPendingRequest={addPendingRequest} 
                removePendingRequest={removePendingRequest} 
                generateRequestId={generateRequestId}
                tutorialStepId={isTutorialMode ? tutorialContext?.getCurrentStepData?.()?.id : null}
            />
            <ConfirmModal 
                isOpen={confirmModalState.isOpen} 
                onClose={() => setConfirmModalState({ ...confirmModalState, isOpen: false })} 
                onConfirm={confirmModalState.onConfirm} 
                onCancel={confirmModalState.onCancel}
                title={confirmModalState.title} 
                message={confirmModalState.message} 
                confirmText={confirmModalState.confirmText} 
                cancelText={confirmModalState.cancelText}
                showCheckbox={confirmModalState.showCheckbox}
                checkboxLabel={confirmModalState.checkboxLabel}
                checkboxState={confirmModalState.checkboxState}
                onCheckboxChange={(checked) => {
                    setConfirmModalState(prev => ({
                        ...prev,
                        checkboxState: checked
                    }));
                }}
            />
            <NewConceptModal 
                isOpen={isNewConceptModalOpen} 
                concept={newlyGeneratedConcept} 
                onStatusSelect={(status, concept) => {
                    // Don't close the modal if we're in tutorial mode on the new-concept-modal step
                    if (isTutorialMode && tutorialContext?.getCurrentStepData?.()?.id === 'new-concept-modal') {
                        // Do nothing during tutorial
                        return;
                    }
                    setIsNewConceptModalOpen(false);
                    if (status && concept && READ_KNOWLEDGE_STATUSES.has(status)) {
                        // Select the new topic when user confirms a status
                        setSelectedTopicId(concept.topicId);
                        handleUpdateTipStatus(status, concept);
                    }
                    // If status is null (cancelled), keep the unread mark and don't switch topics
                }}
                onEditTopic={handleEditTopicFromTip}
                uiLanguage={uiLanguage}
                isTutorialMode={isTutorialMode}
            />
            <SelectKnowledgeLevelModal 
                isOpen={isSelectKnowledgeLevelModalOpen} 
                concept={conceptForKnowledgeLevel} 
                onStatusSelect={(status, concept) => {
                    setIsSelectKnowledgeLevelModalOpen(false);
                    if (status && concept && READ_KNOWLEDGE_STATUSES.has(status)) {
                        handleUpdateTipStatus(status, concept, true).then(() => {
                            // After updating status, start the conversation
                            proceedWithNewConversation(concept.id, status);
                        });
                    }
                    // If status is null (cancelled), don't start conversation
                }}
                onEditTopic={handleEditTopicFromTip}
                uiLanguage={uiLanguage}
            />
            <RateConceptModal isOpen={isRateConceptModalOpen} onClose={() => setIsRateConceptModalOpen(false)} onStatusSelect={handleRateThenChat} conceptTitle={selectedTip?.title || ''} uiLanguage={uiLanguage} />
            <ReviewQuestionModal 
                isOpen={isReviewQuestionModalOpen} 
                onClose={() => setIsReviewQuestionModalOpen(false)} 
                tip={selectedTip} 
                language={uiLanguage} 
                userId={userId} 
                initialSlide={reviewModalInitialSlide}
                onClearReviewDue={handleClearReviewDue}
                onSSEUpdate={reviewChatUpdateCallbackRef}
                isTutorialMode={isTutorialMode}
                dummyReviewQuestions={isTutorialMode ? dummyData?.reviewQuestions : null}
            />
            
            {/* Development-only modal for generating review questions */}
            {process.env.NODE_ENV !== 'production' && isGenerateReviewModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-purple-600" />
                                [DEV] Generate Review Question
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                Select which review day to generate a question for:
                            </p>
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                {[1, 3, 7, 14, 30, 60].map(day => (
                                    <button
                                        key={day}
                                        onClick={() => setSelectedReviewDay(day)}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            selectedReviewDay === day
                                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600'
                                        }`}
                                    >
                                        Day {day}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleConfirmGenerateReviewQuestion}
                                    disabled={isGeneratingReviewQuestion}
                                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isGeneratingReviewQuestion ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            Generate Question
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => setIsGenerateReviewModalOpen(false)}
                                    disabled={isGeneratingReviewQuestion}
                                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal 
                isOpen={deleteConfirmModalState.isOpen} 
                onClose={() => setDeleteConfirmModalState({ isOpen: false, tipId: null, tipTitle: '', onConfirm: () => {} })} 
                onConfirm={deleteConfirmModalState.onConfirm} 
                title={t('deleteConcept')} 
                message={
                    uiLanguage === 'zh-CN' ? (
                        <>
                            确定要删除概念 <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{deleteConfirmModalState.tipTitle}</span> 吗？此操作无法撤销。
                        </>
                    ) : uiLanguage === 'ja' ? (
                        <>
                            概念 <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{deleteConfirmModalState.tipTitle}</span> を削除してもよろしいですか？この操作は元に戻せません。
                        </>
                    ) : (
                        <>
                            Are you sure you want to delete the concept <span className="bg-gray-100 px-2 py-1 rounded font-semibold">{deleteConfirmModalState.tipTitle}</span>? This action cannot be undone.
                        </>
                    )
                }
                confirmText={t('delete')} 
                cancelText={t('cancel')} 
            />
            <UserProfileModal 
                isOpen={isUserProfileModalOpen} 
                onClose={() => setIsUserProfileModalOpen(false)} 
                onSaveProfile={handleSaveProfile}
                currentLanguage={uiLanguage}
                googleUserData={googleUserData}
                onLanguageChange={setUILanguage}
            />
            <SettingsModal 
                isOpen={isSettingsModalOpen} 
                onClose={() => setIsSettingsModalOpen(false)}
                userProfile={userProfile}
                onSaveProfile={handleSaveProfile}
                currentLanguage={uiLanguage}
                authenticatedFetch={authenticatedFetch}
            />
            <ResponsiveWarningModal
                isOpen={showResponsiveWarning}
                onClose={() => setShowResponsiveWarning(false)}
                t={t}
            />
            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                t={t}
                authenticatedFetch={authenticatedFetch}
            />
            
            <div className="flex flex-col h-screen font-sans bg-gray-50 text-gray-800">
                {/* Notification Banner */}
                {notification && (
                    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
                        notification.type === 'error' ? 'bg-red-500 text-white' :
                        notification.type === 'success' ? 'bg-green-500 text-white' :
                        'bg-blue-500 text-white'
                    }`}>
                        {notification.message}
                    </div>
                )}
                
                {/* Slow Chat Notification Bubble */}
                {showSlowChatNotification && (
                    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-in-down">
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow-lg px-5 py-4 max-w-sm">
                            <div className="flex items-start gap-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600 mt-0.5"></div>
                                <div className="flex-1">
                                    <p className="text-sm text-amber-800 mb-3">
                                        {t('slowChatNotification')}
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => {
                                                // Open settings modal directly to AI Model tab
                                                setIsSettingsModalOpen(true);
                                                setShowSlowChatNotification(false);
                                                clearTimeout(slowChatTimeoutRef.current);
                                                // Set a flag to open AI Model tab
                                                setTimeout(() => {
                                                    const event = new CustomEvent('openAIModelTab');
                                                    window.dispatchEvent(event);
                                                }, 100);
                                            }}
                                            className="text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            {t('changeModel')}
                                        </button>
                                        <button
                                            onClick={handleDismissSlowChatNotification}
                                            className="text-xs text-amber-600 hover:text-amber-800 underline"
                                        >
                                            {t('dontShowAgain')}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowSlowChatNotification(false);
                                        clearTimeout(slowChatTimeoutRef.current);
                                    }}
                                    className="text-amber-600 hover:text-amber-800 -mr-1 -mt-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Header Banner */}
                {userId && (
                    <div className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <BookOpen size={20} className="text-sky-600"/>
                            <h1 className="text-lg font-semibold text-gray-800">{t('appTitle')}</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">{t('welcome')}, {userProfile?.name || username}</span>
                            {process.env.NODE_ENV === 'development' && hasPendingRequests() && (
                                <div className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                    {pendingRequests.size} {t('requestsPending')}
                                </div>
                            )}
                            <TutorialButton uiLanguage={uiLanguage} />
                            <button 
                                onClick={() => {
                                    setIsSettingsModalOpen(true);
                                }}
                                className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-colors"
                                data-tutorial-target="settings-button"
                            >
                                <Settings size={16} />
                                {t('settings')}
                            </button>
                            {isTestDeployment && (
                                <button
                                    onClick={() => setIsFeedbackModalOpen(true)}
                                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                                >
                                    <MessageSquarePlus size={16} />
                                    {t('feedbackBugReport')}
                                </button>
                            )}
                            <button 
                                onClick={handleLogout}
                                className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                                <LogOut size={16} />
                                {t('logout')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Topics Panel (should not be covered by spinner) */}
                    <div className="w-1/6 bg-white border-r border-gray-200 flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center h-16">
                            <h2 className="text-lg font-semibold text-gray-700 whitespace-nowrap">{t('topics')}</h2>
                            <div className="flex items-center gap-1" data-tutorial-target="topic-filters">
                                <div className="relative">
                                    <button 
                                        onClick={() => topics.length === 0 ? null : setTopicFilters(prev => prev.includes('favorites') ? prev.filter(f => f !== 'favorites') : [...prev, 'favorites'])} 
                                        className="p-2 rounded-md hover:bg-gray-200 text-gray-500 hover:text-red-600 relative disabled:opacity-50 disabled:cursor-not-allowed" 
                                        title={topics.length === 0 ? t('noTopicsToFilter') : t('filterFavoriteTopicsTooltip')}
                                        disabled={topics.length === 0}
                                    >
                                        <Heart size={20} className={topicFilters.includes('favorites') ? "fill-current text-red-500" : ""} />
                                        {topicFilters.includes('favorites') && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-red-500 ring-2 ring-white"></span>}
                                    </button>
                                </div>
                                <div className="relative">
                                    <button 
                                        onClick={() => topics.length === 0 ? null : setTopicReviewDueFilter(prev => !prev)} 
                                        className="p-2 rounded-md hover:bg-gray-200 text-gray-500 hover:text-yellow-600 relative disabled:opacity-50 disabled:cursor-not-allowed" 
                                        title={topics.length === 0 ? t('noTopicsToFilter') : t('filterReviewDueTopicsTooltip')}
                                        disabled={topics.length === 0}
                                    >
                                        <Clock size={20} className={topicReviewDueFilter ? "text-yellow-500" : ""} />
                                        {topicReviewDueFilter && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-yellow-500 ring-2 ring-white"></span>}
                                    </button>
                                </div>
                                <div className="relative">
                                    <button 
                                        ref={topicFilterButtonRef} 
                                        onClick={() => topics.length === 0 ? null : setIsTopicFilterOpen(prev => !prev)} 
                                        className="p-2 rounded-md hover:bg-gray-200 text-gray-500 hover:text-purple-600 relative disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={topics.length === 0 ? t('noTopicsToFilter') : t('filterTopicsTooltip')}
                                        disabled={topics.length === 0}
                                    >
                                        <Filter size={20} className={topicScheduledFilter ? "text-purple-500" : ""} />
                                        {topicScheduledFilter && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-purple-500 ring-2 ring-white"></span>}
                                    </button>
                                    {isTopicFilterOpen && (
                                        <div ref={topicFilterRef} className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-20 border border-gray-200">
                                            <div className="p-2">
                                                <h3 className="text-xs font-semibold text-gray-500 px-2 mb-2">{t('filterByStatus')}</h3>
                                                <label className={`flex items-center w-full px-3 py-2 text-sm rounded-md hover:bg-gray-100 cursor-pointer ${topicScheduledFilter === 'scheduled' ? 'bg-purple-50' : ''}`}>
                                                    <input 
                                                        type="radio" 
                                                        name="scheduledFilter" 
                                                        checked={topicScheduledFilter === 'scheduled'} 
                                                        onChange={() => {
                                                            setTopicScheduledFilter('scheduled');
                                                            setIsTopicFilterOpen(false);
                                                        }} 
                                                        className="h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500" 
                                                    />
                                                    <span className={`ml-3 ${topicScheduledFilter === 'scheduled' ? 'text-purple-700 font-medium' : 'text-gray-700'}`}>{t('showScheduledTopicsOnly')}</span>
                                                </label>
                                                <label className={`flex items-center w-full px-3 py-2 text-sm rounded-md hover:bg-gray-100 cursor-pointer ${topicScheduledFilter === 'unscheduled' ? 'bg-purple-50' : ''}`}>
                                                    <input 
                                                        type="radio" 
                                                        name="scheduledFilter" 
                                                        checked={topicScheduledFilter === 'unscheduled'} 
                                                        onChange={() => {
                                                            setTopicScheduledFilter('unscheduled');
                                                            setIsTopicFilterOpen(false);
                                                        }} 
                                                        className="h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-500" 
                                                    />
                                                    <span className={`ml-3 ${topicScheduledFilter === 'unscheduled' ? 'text-purple-700 font-medium' : 'text-gray-700'}`}>{t('showUnscheduledTopicsOnly')}</span>
                                                </label>
                                                <div className="border-t border-gray-200 my-2"></div>
                                                <button 
                                                    onClick={() => {
                                                        setTopicScheduledFilter(null);
                                                        setIsTopicFilterOpen(false);
                                                    }}
                                                    className={`w-full px-3 py-2 text-left text-sm rounded-md hover:bg-gray-50 ${topicScheduledFilter === null ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-600'}`}
                                                >
                                                    {t('showAllTopics')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button 
                                    onClick={() => {
                                        setShowGuidedTutorial(false);
                                        setIsAddTopicModalOpen(true);
                                    }} 
                                    className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-sky-600"
                                    data-tutorial-target="add-topic-button"
                                    title={t('addTopicTooltip')}
                                >
                                    <Plus size={20} />
                                </button>
                            </div>
                        </div>
                        {topicFilters.includes('favorites') && filteredTopics.length === 0 && (
                            <div className="px-4 py-2 bg-sky-50 border-b border-sky-200">
                                <p className="text-xs text-sky-700 font-medium">{t('showFavoriteTopicsOnly')}</p>
                            </div>
                        )}
                        {topicReviewDueFilter && filteredTopics.length === 0 && (
                            <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
                                <p className="text-xs text-yellow-700 font-medium">{t('showReviewDueTopicsOnly')}</p>
                            </div>
                        )}
                        {topicScheduledFilter === 'scheduled' && filteredTopics.length === 0 && (
                            <div className="px-4 py-2 bg-purple-50 border-b border-purple-200">
                                <p className="text-xs text-purple-700 font-medium">{t('showScheduledTopicsOnly')}</p>
                            </div>
                        )}
                        {topicScheduledFilter === 'unscheduled' && filteredTopics.length === 0 && (
                            <div className="px-4 py-2 bg-purple-50 border-b border-purple-200">
                                <p className="text-xs text-purple-700 font-medium">{t('showUnscheduledTopicsOnly')}</p>
                            </div>
                        )}
                        {topicReviewDueFilter && filteredTopics.length === 0 && (
                            <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
                                <p className="text-xs text-yellow-700 font-medium">{t('noTopicsHaveReviewDue')}</p>
                            </div>
                        )}
                        <div className="overflow-y-auto flex-grow">
                            {/* Create Group Button - only when no scheduling filter and custom groups are shown */}
                            {topicScheduledFilter === null && (
                                <div className="p-4 border-b border-gray-200" data-tutorial-target="topic-groups-area">
                                    {isCreatingGroup ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={newGroupName}
                                                onChange={(e) => setNewGroupName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleCreateTopicGroup();
                                                    } else if (e.key === 'Escape') {
                                                        setIsCreatingGroup(false);
                                                        setNewGroupName('');
                                                    }
                                                }}
                                                placeholder={t('groupName') || 'Group name'}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400"
                                                autoFocus
                                            />
                                            <button
                                                onClick={handleCreateTopicGroup}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                            >
                                                <Check size={18} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsCreatingGroup(false);
                                                    setNewGroupName('');
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setIsCreatingGroup(true)}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <Plus size={16} />
                                            {t('createGroup') || 'Create Group'}
                                        </button>
                                    )}
                                </div>
                            )}
                            
                            {topicScheduledFilter === 'scheduled' && groupedTopics ? (
                                // Grouped view for scheduled topics
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={customCollisionDetection}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    modifiers={[restrictToVerticalAxis]}
                                >
                                    {groupedTopics.map((group, groupIndex) => (
                                    <div key={`group-${group.frequencyValue}-${group.frequencyUnit}`} className={groupIndex > 0 ? 'mt-4' : ''}>
                                        {/* Group header */}
                                        <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                                            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                <Clock size={16} className="text-sky-500" />
                                                {getFrequencyLabel(group.frequencyValue, group.frequencyUnit)}
                                                <span className="text-xs font-normal text-gray-500">({group.topics.length} {group.topics.length === 1 ? t('topicSingular') : t('topicPlural')})</span>
                                            </h3>
                                        </div>
                                        {/* Topics in this group */}
                                        <DroppableGroup groupId={`group-${group.frequencyValue}-${group.frequencyUnit}`} customGroupedTopics={groupedTopics} t={t}>
                                            <SortableContext
                                                items={group.topics.map(t => t.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {group.topics.map(topic => {
                                                    const isDeleting = deletingTopicId === topic.id;
                                                    const isGenerating = topic.isGeneratingTopic;
                                                    const isDisabled = isDeleting || isGenerating;
                                                    const hasUnreadMark = unreadTopics.has(topic.id);
                                                    
                                                    return (
                                                        <SortableTopicItem
                                                            key={topic.id}
                                                            topic={topic}
                                                            isDeleting={isDeleting}
                                                            isGenerating={isGenerating}
                                                            isDisabled={isDisabled}
                                                            hasUnreadMark={hasUnreadMark}
                                                            topicHasReviewDue={topicHasReviewDue}
                                                            selectedTopicId={selectedTopicId}
                                                            setSelectedTopicId={setSelectedTopicId}
                                                            toggleTopicDropdown={toggleTopicDropdown}
                                                            topicDropdownOpen={topicDropdownOpen}
                                                            topicDropdownRef={topicDropdownRef}
                                                            handleUpdateTopicFavorite={handleUpdateTopicFavorite}
                                                            handleOpenEditModal={handleOpenEditModal}
                                                            t={t}
                                                            setConfirmModalState={setConfirmModalState}
                                                            handleDeleteTopic={handleDeleteTopic}
                                                            uiLanguage={uiLanguage}
                                                            setTopicDropdownOpen={setTopicDropdownOpen}
                                                            dropdownPositions={dropdownPositions}
                                                        />
                                                    );
                                                })}
                                            </SortableContext>
                                            {/* Drop zone for empty groups */}
                                            {group.topics.length === 0 && (
                                                <div className="p-4 text-center text-gray-400 text-sm">
                                                    {t('dropTopicsHere') || 'Drop topics here'}
                                                </div>
                                            )}
                                        </DroppableGroup>
                                    </div>
                                    ))}
                                </DndContext>
                            ) : customGroupedTopics ? (
                                // Custom groups view when no scheduling filter is active
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={customCollisionDetection}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    modifiers={[restrictToVerticalAxis]}
                                >
                                    <SortableContext
                                        items={customGroupedTopics.map(g => `group-sort-${g.id}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {customGroupedTopics.map((group, groupIndex) => {
                                            if (group.id === 'ungrouped') {
                                                // Ungrouped section is not sortable
                                                return (
                                                    <div key={group.id} className={groupIndex > 0 ? 'mt-4' : ''}>
                                                        <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                                                            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                                <Folder size={16} className="text-sky-500" />
                                                                {group.name}
                                                                <span className="text-xs font-normal text-gray-500">({group.topics.length} {group.topics.length === 1 ? t('topicSingular') : t('topicPlural')})</span>
                                                            </h3>
                                                        </div>
                                                        <DroppableGroup groupId={group.id} customGroupedTopics={customGroupedTopics} t={t}>
                                                            <SortableContext
                                                                items={group.topics.map(t => t.id)}
                                                                strategy={verticalListSortingStrategy}
                                                            >
                                                                {group.topics.map(topic => {
                                                                    const isDeleting = deletingTopicId === topic.id;
                                                                    const isGenerating = topic.isGeneratingTopic;
                                                                    const isDisabled = isDeleting || isGenerating;
                                                                    const hasUnreadMark = unreadTopics.has(topic.id);
                                                                    
                                                                    return (
                                                                        <SortableTopicItem
                                                                            key={topic.id}
                                                                            topic={topic}
                                                                            isDeleting={isDeleting}
                                                                            isGenerating={isGenerating}
                                                                            isDisabled={isDisabled}
                                                                            hasUnreadMark={hasUnreadMark}
                                                                            topicHasReviewDue={topicHasReviewDue}
                                                                            selectedTopicId={selectedTopicId}
                                                                            setSelectedTopicId={setSelectedTopicId}
                                                                            toggleTopicDropdown={toggleTopicDropdown}
                                                                            topicDropdownOpen={topicDropdownOpen}
                                                                            topicDropdownRef={topicDropdownRef}
                                                                            handleUpdateTopicFavorite={handleUpdateTopicFavorite}
                                                                            handleOpenEditModal={handleOpenEditModal}
                                                                            t={t}
                                                                            setConfirmModalState={setConfirmModalState}
                                                                            handleDeleteTopic={handleDeleteTopic}
                                                                            uiLanguage={uiLanguage}
                                                                            setTopicDropdownOpen={setTopicDropdownOpen}
                                                                            dropdownPositions={dropdownPositions}
                                                                        />
                                                                    );
                                                                })}
                                                            </SortableContext>
                                                            {group.topics.length === 0 && (
                                                                <div className="p-4 text-center text-gray-400 text-sm">
                                                                    {t('dropTopicsHere') || 'Drop topics here'}
                                                                </div>
                                                            )}
                                                        </DroppableGroup>
                                                    </div>
                                                );
                                            }
                                            
                                            // Sortable custom groups
                                            return (
                                                <SortableGroupContainer 
                                                        key={group.id}
                                                        tutorialTarget="topic-groups-area" 
                                                        group={group} 
                                                        groupIndex={groupIndex}
                                                        editingGroupId={editingGroupId}
                                                        editingGroupName={editingGroupName}
                                                        setEditingGroupName={setEditingGroupName}
                                                        handleUpdateTopicGroup={handleUpdateTopicGroup}
                                                        setEditingGroupId={setEditingGroupId}
                                                        groupDropdownOpen={groupDropdownOpen}
                                                        setGroupDropdownOpen={setGroupDropdownOpen}
                                                        groupDropdownRef={groupDropdownRef}
                                                        t={t}
                                                        setConfirmModalState={setConfirmModalState}
                                                        handleDeleteTopicGroup={handleDeleteTopicGroup}
                                                        toggleGroupDropdown={toggleGroupDropdown}
                                                        dropdownPositions={dropdownPositions}
                                                    >
                                        {/* Topics in this group */}
                                        <DroppableGroup groupId={group.id} customGroupedTopics={customGroupedTopics} t={t}>
                                            <SortableContext
                                                items={group.topics.map(t => t.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {group.topics.map(topic => {
                                                    const isDeleting = deletingTopicId === topic.id;
                                                    const isGenerating = topic.isGeneratingTopic;
                                                    const isDisabled = isDeleting || isGenerating;
                                                    const hasUnreadMark = unreadTopics.has(topic.id);
                                                    
                                                    return (
                                                        <SortableTopicItem
                                                            key={topic.id}
                                                            topic={topic}
                                                            isDeleting={isDeleting}
                                                            isGenerating={isGenerating}
                                                            isDisabled={isDisabled}
                                                            hasUnreadMark={hasUnreadMark}
                                                            topicHasReviewDue={topicHasReviewDue}
                                                            selectedTopicId={selectedTopicId}
                                                            setSelectedTopicId={setSelectedTopicId}
                                                            toggleTopicDropdown={toggleTopicDropdown}
                                                            topicDropdownOpen={topicDropdownOpen}
                                                            topicDropdownRef={topicDropdownRef}
                                                            handleUpdateTopicFavorite={handleUpdateTopicFavorite}
                                                            handleOpenEditModal={handleOpenEditModal}
                                                            t={t}
                                                            setConfirmModalState={setConfirmModalState}
                                                            handleDeleteTopic={handleDeleteTopic}
                                                            uiLanguage={uiLanguage}
                                                            setTopicDropdownOpen={setTopicDropdownOpen}
                                                            dropdownPositions={dropdownPositions}
                                                        />
                                                    );
                                                })}
                                            </SortableContext>
                                            {/* Drop zone for empty groups */}
                                            {group.topics.length === 0 && (
                                                <div className="p-4 text-center text-gray-400 text-sm">
                                                    {t('dropTopicsHere') || 'Drop topics here'}
                                                </div>
                                            )}
                                        </DroppableGroup>
                                </SortableGroupContainer>
                            );
                        })}
                                    </SortableContext>
                                    <DragOverlay>
                                        {activeItem && activeItem.type === 'group' && activeItem.data && (
                                            <div className="bg-white rounded-lg shadow-2xl border-2 border-sky-400 opacity-95">
                                                <div className="px-4 py-2.5 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-200">
                                                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                        <Folder size={16} className="text-sky-500" />
                                                        {activeItem.data.name}
                                                    </h3>
                                                </div>
                                                {/* Show topics in the group */}
                                                <div className="max-h-96 overflow-hidden">
                                                    {customGroupedTopics?.find(g => g.id === activeItem.data.id)?.topics.slice(0, 5).map((topic, idx) => (
                                                        <div key={topic.id} className={`px-4 py-3 border-b border-gray-100 ${idx >= 4 ? 'opacity-50' : ''}`}>
                                                            <div className="flex items-center gap-2">
                                                                <BookOpen size={14} className="text-sky-400" />
                                                                <span className="text-sm">{topic.name}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {customGroupedTopics?.find(g => g.id === activeItem.data.id)?.topics.length > 5 && (
                                                        <div className="px-4 py-2 text-center text-xs text-gray-500">
                                                            +{customGroupedTopics.find(g => g.id === activeItem.data.id).topics.length - 5} more topics
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {activeItem && activeItem.type === 'topic' && activeItem.data && (
                                            <div className="bg-white rounded-lg shadow-2xl border-2 border-sky-400 p-4 opacity-95 transform scale-105">
                                                <div className="flex items-center gap-2">
                                                    <BookOpen size={16} className="text-sky-500" />
                                                    <span className="font-medium">{activeItem.data.name}</span>
                                                </div>
                                            </div>
                                        )}
                                    </DragOverlay>
                                </DndContext>
                            ) : (
                                // Regular view for unscheduled topics
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={customCollisionDetection}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    modifiers={[restrictToVerticalAxis]}
                                >
                                    <SortableContext
                                        items={filteredTopics.map(t => t.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {filteredTopics.map(topic => {
                                            const isDeleting = deletingTopicId === topic.id;
                                            const isGenerating = topic.isGeneratingTopic;
                                            const isDisabled = isDeleting || isGenerating;
                                            const hasUnreadMark = unreadTopics.has(topic.id);
                                            
                                            return (
                                                <SortableTopicItem
                                                    key={topic.id}
                                                    topic={topic}
                                                    isDeleting={isDeleting}
                                                    isGenerating={isGenerating}
                                                    isDisabled={isDisabled}
                                                    hasUnreadMark={hasUnreadMark}
                                                    topicHasReviewDue={topicHasReviewDue}
                                                    selectedTopicId={selectedTopicId}
                                                    setSelectedTopicId={setSelectedTopicId}
                                                    toggleTopicDropdown={toggleTopicDropdown}
                                                    topicDropdownOpen={topicDropdownOpen}
                                                    topicDropdownRef={topicDropdownRef}
                                                    handleUpdateTopicFavorite={handleUpdateTopicFavorite}
                                                    handleOpenEditModal={handleOpenEditModal}
                                                    t={t}
                                                    setConfirmModalState={setConfirmModalState}
                                                    handleDeleteTopic={handleDeleteTopic}
                                                    uiLanguage={uiLanguage}
                                                    setTopicDropdownOpen={setTopicDropdownOpen}
                                                    dropdownPositions={dropdownPositions}
                                                />
                                            );
                                        })}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    </div>

                    {/* Panels to be covered by spinner */}
                    <div className="flex-1 flex overflow-hidden relative">
                        {/* Single Spinner for First Concept Generation */}
                        {(selectedTopicId && appData.topics.find(t => t.id === selectedTopicId)?.isGeneratingTopic) && (
                            <div className="absolute inset-0 bg-white bg-opacity-80 backdrop-blur-sm z-40 flex flex-col justify-center items-center">
                                <div className="w-16 h-16 border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin"></div>
                                <p className="mt-4 text-lg text-gray-700 font-semibold tracking-wide">{loadingMessage}</p>
                            </div>
                        )}

                        {/* Concepts Panel */}
                        <div className="w-1/4 bg-gray-50 border-r border-gray-200 flex flex-col">
                            <div className="p-4 border-b border-gray-200 flex justify-between items-center h-16">
                                <h2 className="text-lg font-semibold text-gray-700 whitespace-nowrap">{t('concepts')}</h2>
                                <div className="flex items-center gap-1" data-tutorial-target="tip-filters">
                                    <div className="relative">
                                        <button 
                                            onClick={() => !selectedTopicId ? null : setStatusFilters(prev => prev.includes('favorites') ? prev.filter(f => f !== 'favorites') : [...prev, 'favorites'])} 
                                            className="p-2 rounded-md relative hover:bg-gray-200 text-gray-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed" 
                                            title={!selectedTopicId ? t('selectTopicToFilterConcepts') : t('filterFavoriteConceptsTooltip')}
                                            disabled={!selectedTopicId}
                                        >
                                            <Heart size={20} className={statusFilters.includes('favorites') ? "fill-current text-red-500" : ""} />
                                            {statusFilters.includes('favorites') && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-red-500 ring-2 ring-white"></span>}
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <button 
                                            onClick={() => !selectedTopicId ? null : setReviewDueFilter(prev => !prev)} 
                                            className="p-2 rounded-md relative hover:bg-gray-200 text-gray-500 hover:text-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed" 
                                            title={!selectedTopicId ? t('selectTopicToFilterConcepts') : t('filterReviewDueConceptsTooltip')}
                                            disabled={!selectedTopicId}
                                        >
                                            <Clock size={20} className={reviewDueFilter ? "text-yellow-500" : ""} />
                                            {reviewDueFilter && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-yellow-500 ring-2 ring-white"></span>}
                                        </button>
                                    </div>
                                    <div className="relative" ref={filterRef}>
                                        <button 
                                            onClick={() => !selectedTopicId ? null : setIsFilterOpen(prev => !prev)} 
                                            className="p-2 rounded-md relative hover:bg-gray-200 text-gray-500 hover:text-sky-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={!selectedTopicId ? t('selectTopicToFilterConcepts') : t('filterConceptsTooltip')}
                                            disabled={!selectedTopicId}
                                        >
                                            <Filter size={20} />
                                            {statusFilters.filter(f => f !== 'favorites').length > 0 && <span className="absolute top-0 right-0 block h-2 w-2 transform -translate-y-1/2 translate-x-1/2 rounded-full bg-sky-500 ring-2 ring-white"></span>}
                                        </button>
                                        {isFilterOpen && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-20 border border-gray-200">
                                                <div className="p-2">
                                                    <h3 className="text-xs font-semibold text-gray-500 px-2 mb-2">{t('filterByStatus')}</h3>
                                                    {statusOptions.map(option => (
                                                        option.type === 'separator' ? (
                                                            <div key="separator" className="border-t border-gray-200 my-2"></div>
                                                        ) : (
                                                            <label key={option.value} className="flex items-center w-full px-3 py-2 text-sm rounded-md hover:bg-gray-100 cursor-pointer">
                                                                <input type="checkbox" checked={statusFilters.includes(option.value)} onChange={() => handleFilterChange(option.value)} className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
                                                                <span className="ml-3 text-gray-700">{t(option.label)}</span>
                                                            </label>
                                                        )
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={handleAddNewTip} disabled={!selectedTopicId || hasPendingTipRequestForTopic(selectedTopicId)} className="p-2 rounded-md hover:bg-gray-200 text-gray-500 hover:text-sky-600 disabled:opacity-50 disabled:cursor-not-allowed" title={t('addConceptTooltip')} data-tutorial-target="add-tip-button">
                                        {hasPendingTipRequestForTopic(selectedTopicId) ? <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div> : <Plus size={20} />}
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-y-auto flex-grow p-2" key={`tips-${selectedTopicId}-${lastTipUpdate}`}>
                                {tipsForSelectedTopic.map(tip => {
                                    const isDeleting = deletingTipId === tip.id;
                                    // In tutorial mode, check unreadConcepts set; otherwise check knowledgeStatus
                                    const hasUnreadMark = isTutorialMode 
                                        ? unreadConcepts.has(tip.id)
                                        : !READ_KNOWLEDGE_STATUSES.has(tip.knowledgeStatus);
                                    return (
                                    <div key={tip.id} className="group relative">
                                        {/* Unread mark in upper left */}
                                        {hasUnreadMark && (
                                            <div className="absolute top-1 left-3 w-2 h-2 bg-sky-500 rounded-full z-[5]"></div>
                                        )}
                                        {/* Review due orange dot */}
                                        {tip.reviewIsDue && (
                                            <div className="absolute top-1 left-6 w-2 h-2 bg-orange-500 rounded-full z-[5]"></div>
                                        )}
                                        <div 
                                            onClick={() => !isDeleting && handleConceptSelection(tip.id)}
                                            onContextMenu={(e) => {
                                                if (!isDeleting) {
                                                    e.preventDefault();
                                                    toggleConceptDropdown(tip.id, e);
                                                }
                                            }}
                                            className={`p-3 rounded-lg cursor-pointer border-l-4 transition-colors duration-150 mb-2 flex justify-between items-start ${selectedTipId === tip.id ? 'bg-white shadow-sm border-sky-500' : 'border-transparent hover:bg-white'} ${isDeleting ? 'opacity-60 pointer-events-none cursor-not-allowed grayscale' : ''}`}
                                            style={isDeleting ? { position: 'relative' } : {}}
                                            data-tutorial-target="tip-card"
                                        >
                                            <div className="flex items-start flex-grow min-w-0">
                                                <p className="font-medium text-sm text-gray-700 break-words flex-grow">{tip.title}</p>
                                                {/* Show favorite heart icon if tip is favorited */}
                                                {tip.isFavorite && (
                                                    <Heart size={14} className="fill-current text-red-500 ml-2 flex-shrink-0 mt-0.5" />
                                                )}
                                            </div>
                                            {isDeleting ? (
                                                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin ml-2"></div>
                                            ) : (
                                                <button 
                                                    onClick={(e) => toggleConceptDropdown(tip.id, e)} 
                                                    className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-gray-200 text-gray-500 ml-2 flex-shrink-0"
                                                >
                                                    <MoreVertical size={16} />
                                                </button>
                                            )}
                                        </div>
                                        {conceptDropdownOpen === tip.id && (
                                            <div 
                                                ref={conceptDropdownRef}
                                                className="absolute right-0 z-30 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
                                                style={{ ...(dropdownPositions[`concept-${tip.id}`] || { top: '32px', bottom: 'auto' }) }}
                                                onMouseEnter={() => {
                                                }}
                                            >
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateTipHidden(!tip.isHidden, tip);
                                                        setConceptDropdownOpen(null);
                                                    }}
                                                    className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                                                >
                                                    {tip.isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                                                    {tip.isHidden ? t('showThisTipAgain') : t('hideThisTip')}
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateTipFavorite(!tip.isFavorite, tip);
                                                        setConceptDropdownOpen(null);
                                                    }}
                                                    className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                                                >
                                                    <Heart size={14} className={tip.isFavorite ? "fill-current text-red-500" : ""} />
                                                    {tip.isFavorite ? t('unfavoriteThisTip') : t('favoriteThisTip')}
                                                </button>
                                                {isTipRead(tip) && (
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleReviewReminder(tip);
                                                            setConceptDropdownOpen(null);
                                                        }}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                                                    >
                                                        <Clock size={14} className={tip.isScheduledForReview ? "text-yellow-500" : ""} />
                                                        {tip.isScheduledForReview ? t('unscheduleReview') : t('scheduleReview')}
                                                    </button>
                                                )}
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteTip(tip.id, tip.title);
                                                        setConceptDropdownOpen(null);
                                                    }}
                                                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                >
                                                    <Trash2 size={14} />
                                                    {t('delete')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )})}
                                {statusFilters.length > 0 && tipsForSelectedTopic.length === 0 && (
                                    <div className="px-4 py-2 bg-sky-50 border border-sky-200 rounded-lg">
                                        <p className="text-xs text-sky-700 font-medium">{t('noConceptsMatchFilter')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Main Content Panels */}
                        <div className="w-2/4 flex flex-col bg-white">
                            {(() => {
                                const layout = getMainAreaLayout();
                                
                                return (
                                    <>
                                        {/* State Toggle Buttons */}
                                        <div className="p-2 border-b border-gray-200 bg-gray-50 flex justify-center gap-2">
                                            <div className="flex gap-2" data-tutorial-target="tristate-buttons-area">
                                                <button 
                                                    onClick={() => changeMainAreaState('tip-only')}
                                                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                                        mainAreaState === 'tip-only' 
                                                            ? 'bg-sky-500 text-white' 
                                                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                                    }`}
                                                >
                                                    {t('tipOnly')}
                                                </button>
                                                <button 
                                                    onClick={() => changeMainAreaState('full')}
                                                    disabled={!hasConversationHistory}
                                                    title={!hasConversationHistory ? t('startConversationFirst') : ''}
                                                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                                        mainAreaState === 'full' 
                                                            ? 'bg-sky-500 text-white' 
                                                            : hasConversationHistory
                                                                ? 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                    }`}
                                                >
                                                    {t('fullView')}
                                                </button>
                                                <button 
                                                    onClick={() => changeMainAreaState('chat-only')}
                                                    disabled={!hasConversationHistory}
                                                    title={!hasConversationHistory ? t('startConversationFirst') : ''}
                                                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                                        mainAreaState === 'chat-only' 
                                                            ? 'bg-sky-500 text-white' 
                                                            : hasConversationHistory
                                                                ? 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                    }`}
                                                >
                                                    {t('chatOnly')}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Main content area with proper flex container */}
                                        <div className="flex-1 flex flex-col min-h-0">
                                            {/* Tip Content and Conversation Container */}
                                            {layout.showTipContent && layout.showConversation && mainAreaState === 'full' ? (
                                                // For full view, wrap both in a flex container to split space
                                                <div className="flex-1 flex flex-col min-h-0">
                                                    {/* Tip Content Section */}
                                                    <div className="flex-1 p-4 border-b border-gray-200 flex flex-col min-h-0" data-tutorial-target="tip-content-area">
                                                        <div className="flex items-center gap-3 text-gray-700 h-12 flex-shrink-0">
                                                            <BookOpen size={20} className="text-sky-600"/>
                                                            <h2 className="text-lg font-semibold">{t('currentLearningConcept')}</h2>
                                                        </div>
                                                        {selectedTip ? (
                                                            <div className="mt-2 text-gray-700 concept-content-container flex-1 overflow-hidden min-h-0">
                                                                <div className="prose prose-sm max-w-none overflow-y-auto h-full" ref={conceptContentAreaRef}>
                                                                    <h3 className="font-bold text-base text-gray-800">
                                                                        {selectedTip.title}
                                                                        {process.env.NODE_ENV === 'development' && (
                                                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                                                (Tip ID: {selectedTip.id}, Topic ID: {selectedTip.topicId})
                                                                            </span>
                                                                        )}
                                                                    </h3>
                                                                    {(() => {
                                                                        return renderMarkdownWrapper(selectedTip.content);
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="mt-2 text-gray-500 flex-1">{t('selectTopicConcept')}</div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Conversation Section */}
                                                    <div className="flex-1 flex flex-col min-h-0" data-tutorial-target="ai-tutor-area">
                                                        <div className="flex-1 p-4 overflow-y-auto bg-gray-50/50 min-h-0">
                                                            {selectedTip ? (
                                                                <div className="space-y-4">
                                                                {conversation.map((msg, index) => (
                                                                    <div key={index} className={`flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                                                        {msg.role === 'bot' && (
                                                                            <div className={`w-8 h-8 rounded-full ${msg.isError ? 'bg-red-100' : 'bg-gray-200'} flex items-center justify-center flex-shrink-0`}>
                                                                                <Bot size={20} className={msg.isError ? 'text-red-500' : 'text-sky-700'}/>
                                                                            </div>
                                                                        )}
                                                                        <div className={`max-w-xl p-3 rounded-lg shadow-sm ${
                                                                            msg.role === 'user' ? 'bg-sky-500 text-white' : 
                                                                            msg.isError ? 'bg-red-50 text-gray-800 border border-red-200' : 
                                                                            'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                            {msg.role === 'user' ? (
                                                                                <div className="whitespace-pre-line">{msg.text || '[Message not available]'}</div>
                                                                            ) : (
                                                                                <>
                                                                                    {msg.isRetrying ? (
                                                                                        <div className="flex items-center space-x-2">
                                                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                                                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                                                                                            <span className="text-gray-600 ml-2">{msg.text}</span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        renderMarkdownWrapper(msg.text || '[Message not available]')
                                                                                    )}
                                                                                    {msg.isError && msg.retryable && !msg.isRetrying && (
                                                                                        <button
                                                                                            onClick={() => handleRetryMessage(msg, index)}
                                                                                            className="mt-3 px-3 py-1.5 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors flex items-center gap-1.5"
                                                                                            disabled={isTipChatting(selectedTip?.id)}
                                                                                        >
                                                                                            <RefreshCw size={14} />
                                                                                            {t('retry') || 'Retry'}
                                                                                        </button>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        {msg.role === 'user' && <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"><User size={20} className="text-gray-600"/></div>}
                                                                    </div>
                                                                ))}
                                                                {isTipChatting(selectedTip?.id) && (
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                                            <Bot size={20} className="text-sky-700"/>
                                                                        </div>
                                                                        <div className="p-3 bg-gray-100 rounded-lg shadow-sm">
                                                                            <div className="flex items-center space-x-2">
                                                                                <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                                                                                <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
                                                                                <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div ref={conversationEndRef} />
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center h-full">
                                                                <div className="text-center text-gray-500">
                                                                    <MessageSquarePlus size={48} className="mx-auto mb-4 text-gray-300" />
                                                                    <p className="text-lg font-medium">{t('selectConcept')}</p>
                                                                    <p className="text-sm">{t('selectTopicConcept')}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // For tip-only or chat-only views, render normally
                                                <>
                                                    {layout.showTipContent && (
                                                        <div className={`p-4 border-b border-gray-200 flex-shrink-0 ${layout.tipContentHeight} flex flex-col min-h-0`}>
                                                            <div className="flex items-center gap-3 text-gray-700 h-12 flex-shrink-0">
                                    <BookOpen size={20} className="text-sky-600"/>
                                    <h2 className="text-lg font-semibold ">{t('currentLearningConcept')}</h2>
                                </div>
                                {selectedTip ? (
                                                                <div className="mt-2 text-gray-700 concept-content-container flex-1 overflow-hidden min-h-0">
                                                                    <div className="prose prose-sm max-w-none overflow-y-auto h-full" ref={conceptContentAreaRef2}>
                                            <h3 className="font-bold text-base text-gray-800">
                                                {selectedTip.title}
                                                {process.env.NODE_ENV === 'development' && (
                                                    <span className="ml-2 text-xs font-normal text-gray-500">
                                                        (Tip ID: {selectedTip.id}, Topic ID: {selectedTip.topicId})
                                                    </span>
                                                )}
                                            </h3>
                                            {(() => {
                                                return renderMarkdownWrapper(selectedTip.content);
                                            })()}
                                        </div>
                                    </div>
                                ) : (
                                                                <div className="mt-2 text-gray-500 flex-1">{t('selectTopicConcept')}</div>
                                )}
                            </div>
                                                    )}
                                                    
                                                    {layout.showConversation && (
                                                        <div className={`flex-grow flex flex-col min-h-0 ${layout.conversationHeight}`}>
                                <div className="flex-grow p-4 overflow-y-auto bg-gray-50/50 min-h-0" ref={conversationAreaRef}>
                                    {selectedTip ? (
                                        <div className="space-y-4">
                                            {conversation.map((msg, index) => (
                                                <div key={index} className={`flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                                    {msg.role === 'bot' && (
                                                        <div className={`w-8 h-8 rounded-full ${msg.isError ? 'bg-red-100' : 'bg-gray-200'} flex items-center justify-center flex-shrink-0`}>
                                                            <Bot size={20} className={msg.isError ? 'text-red-500' : 'text-sky-700'}/>
                                                        </div>
                                                    )}
                                                    <div className={`max-w-xl p-3 rounded-lg shadow-sm ${
                                                        msg.role === 'user' ? 'bg-sky-500 text-white' : 
                                                        msg.isError ? 'bg-red-50 text-gray-800 border border-red-200' : 
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {msg.role === 'user' ? (
                                                            <div className="whitespace-pre-line">{msg.text || '[Message not available]'}</div>
                                                        ) : (
                                                            <>
                                                                {msg.isRetrying ? (
                                                                    <div className="flex items-center space-x-2">
                                                                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                                                                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                                                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                                                                        <span className="text-gray-600 ml-2">{msg.text}</span>
                                                                    </div>
                                                                ) : (
                                                                    renderMarkdownWrapper(msg.text || '[Message not available]')
                                                                )}
                                                                {msg.isError && msg.retryable && !msg.isRetrying && (
                                                                    <button
                                                                        onClick={() => handleRetryMessage(msg, index)}
                                                                        className="mt-3 px-3 py-1.5 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors flex items-center gap-1.5"
                                                                        disabled={isTipChatting(selectedTip?.id)}
                                                                    >
                                                                        <RefreshCw size={14} />
                                                                        {t('retry') || 'Retry'}
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    {msg.role === 'user' && <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"><User size={20} className="text-gray-600"/></div>}
                                                </div>
                                            ))}
                                            {isTipChatting(selectedTip?.id) && (
                                                <div className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                        <Bot size={20} className="text-sky-700"/>
                                                    </div>
                                                    <div className="p-3 bg-gray-100 rounded-lg shadow-sm">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
                                                            <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div ref={conversationEndRef} />
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-center text-gray-500">
                                                <MessageSquarePlus size={48} className="mx-auto mb-4 text-gray-300" />
                                                <p className="text-lg font-medium">{t('selectConcept')}</p>
                                                <p className="text-sm">{t('selectTopicConcept')}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            
                                            {/* Response Area: always visible at the bottom */}
                                            {layout.showResponseArea && (
                                                <div className={`p-4 border-t border-gray-200 flex-shrink-0 ${layout.responseAreaHeight} flex flex-col justify-end`} data-tutorial-target={mainAreaState === 'full' ? 'ai-tutor-area' : undefined}>
                                    {selectedTip ? (
                                        conversation.length > 0 ? (
                                            <>
                                                {/* Quick Response Section */}
                                                <div className="mb-3" data-tutorial-target="quick-actions-area">
                                                    {/* Section Label */}
                                                    <div className="flex items-center gap-1.5 mb-2.5">
                                                        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                                            {t('quickActions')}
                                                        </span>
                                                    </div>
                                                    {/* Quick Response Buttons */}
                                                    <div className="grid grid-cols-2 gap-2">
                                                    <button 
                                                        onClick={() => {
                                                            // Get the topic language for the message
                                                            const topic = appData.topics.find(t => t.id === selectedTip?.topicId);
                                                            const topicLanguage = topic?.contentLanguage || 'en';
                                                            // Send message in topic language, display button in UI language
                                                            handleQuickResponse(getTranslation('stillDontUnderstandMessage', topicLanguage));
                                                        }}
                                                        disabled={isTipChatting(selectedTip?.id)}
                                                        className="px-3 py-2 text-xs font-semibold bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-red-200 flex items-center justify-center gap-1"
                                                    >
                                                        <span className="hidden sm:inline">{t('stillDontUnderstand')}</span>
                                                        <span className="sm:hidden">{t('confused')}</span>
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            // Get the topic language for the message
                                                            const topic = appData.topics.find(t => t.id === selectedTip?.topicId);
                                                            const topicLanguage = topic?.contentLanguage || 'en';
                                                            // Send message in topic language, display button in UI language
                                                            handleQuickResponse(getTranslation('testMyKnowledgeMessage', topicLanguage));
                                                        }}
                                                        disabled={isTipChatting(selectedTip?.id)}
                                                        className="px-3 py-2 text-xs font-semibold bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-blue-200 flex items-center justify-center gap-1"
                                                    >
                                                        <span className="hidden sm:inline">{t('testMyKnowledge')}</span>
                                                        <span className="sm:hidden">{t('testMe')}</span>
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            // Get the topic language for the message
                                                            const topic = appData.topics.find(t => t.id === selectedTip?.topicId);
                                                            const topicLanguage = topic?.contentLanguage || 'en';
                                                            // Send message in topic language, display button in UI language
                                                            handleQuickResponse(getTranslation('giveAnExampleMessage', topicLanguage));
                                                        }}
                                                        disabled={isTipChatting(selectedTip?.id)}
                                                        className="px-3 py-2 text-xs font-semibold bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-green-200 flex items-center justify-center gap-1"
                                                    >
                                                        <span className="hidden sm:inline">{t('giveAnExample')}</span>
                                                        <span className="sm:hidden">{t('example')}</span>
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            // Get the topic language for the message
                                                            const topic = appData.topics.find(t => t.id === selectedTip?.topicId);
                                                            const topicLanguage = topic?.contentLanguage || 'en';
                                                            // Send message in topic language, display button in UI language
                                                            handleQuickResponse(getTranslation('explainDifferentlyMessage', topicLanguage));
                                                        }}
                                                        disabled={isTipChatting(selectedTip?.id)}
                                                        className="px-3 py-2 text-xs font-semibold bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-purple-200 flex items-center justify-center gap-1"
                                                    >
                                                        <span className="hidden sm:inline">{t('explainDifferently')}</span>
                                                        <span className="sm:hidden">{t('different')}</span>
                                                    </button>
                                                    </div>
                                                </div>
                                                <form onSubmit={handleSendMessage} className="flex gap-3 flex-shrink-0">
                                                    {mainAreaState === 'chat-only' ? (
                                                        <textarea 
                                                            ref={chatInputRef}
                                                            id="chat-input" 
                                                            value={userInput} 
                                                            onChange={(e) => setUserInput(e.target.value)} 
                                                            placeholder={t('askQuestion')} 
                                                            className="flex-grow bg-white rounded-lg p-3 border border-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 focus:shadow-md transition-all duration-200 resize-none" 
                                                            disabled={isTipChatting(selectedTip?.id)}
                                                            autoComplete="off"
                                                            rows={1}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    if (userInput.trim() && !isTipChatting(selectedTip?.id)) {
                                                                        handleSendMessage(e);
                                                                    }
                                                                }
                                                            }}
                                                            style={{
                                                                minHeight: '44px',
                                                                maxHeight: '120px',
                                                                overflowY: 'auto'
                                                            }}
                                                        />
                                                    ) : (
                                                        <input 
                                                            id="chat-input" 
                                                            type="text" 
                                                            value={userInput} 
                                                            onChange={(e) => setUserInput(e.target.value)} 
                                                            placeholder={t('askQuestion')} 
                                                            className="flex-grow bg-white rounded-lg p-3 border border-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 focus:shadow-md transition-all duration-200" 
                                                            disabled={isTipChatting(selectedTip?.id)}
                                                            autoComplete="off"
                                                        />
                                                    )}
                                                    <button type="submit" className="bg-sky-500 text-white p-3 rounded-lg flex items-center justify-center hover:bg-sky-600 disabled:bg-gray-300 disabled:cursor-not-allowed" disabled={isTipChatting(selectedTip?.id) || !userInput.trim()}>
                                                        <Send size={20} />
                                                    </button>
                                                </form>
                                            </>
                                        ) : (
                                            <div className="flex justify-center items-center p-3 h-full">
                                                <div className="text-center w-full">
                                                    <button 
                                                        onClick={() => startAutomatedConversation(selectedTip.id, selectedTip.knowledgeStatus)} 
                                                        disabled={isTipChatting(selectedTip?.id)}
                                                        title={isTipChatting(selectedTip?.id) ? t('botGeneratingMessage') : ''}
                                                        className={`w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors ${
                                                            isTipChatting(selectedTip?.id) 
                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                                                : 'bg-sky-500 text-white hover:bg-sky-600'
                                                        }`}
                                                    >
                                                        <MessageSquarePlus size={16} />
                                                        {t('startNewDeepDive')}
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        // Always show a disabled input if no tip is selected
                                        <div className="flex justify-center items-center h-full">
                                            <form className="flex gap-3 w-full">
                                                <input 
                                                    id="chat-input" 
                                                    type="text" 
                                                    value={''} 
                                                    placeholder={t('selectConcept')} 
                                                    className="flex-grow bg-gray-100 rounded-lg p-3 border border-gray-200 text-gray-400 cursor-not-allowed" 
                                                    disabled
                                                />
                                                <button type="button" className="bg-gray-300 text-white p-3 rounded-lg flex items-center justify-center cursor-not-allowed" disabled>
                                                    <Send size={20} />
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                        </div>
                                    )}
                                </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Concept Details Panel */}
                        <div className="w-1/4 bg-white border-l border-gray-200 flex flex-col">
                            <div className="p-4 border-b border-gray-200 flex items-center h-16">
                                <h2 className="text-lg font-semibold text-gray-700 whitespace-nowrap">{t('conceptDetails')}</h2>
                            </div>
                            {selectedTip ? (
                                <div className="flex-1 overflow-y-auto">
                                    <div className="p-4 space-y-6">
                                        <div data-tutorial-target="knowledge-level-selector">
                                            <h3 className="font-semibold mb-2 text-gray-600">{t('myKnowledgeLevel')}</h3>
                                            <div className="flex flex-col space-y-2">
                                                <button onClick={() => handleUpdateTipStatus('easy', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'easy' ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('tooEasy')}</button>
                                                <button onClick={() => handleUpdateTipStatus('known', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'known' ? 'bg-green-100 text-green-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('alreadyKnown')}</button>
                                                <button onClick={() => handleUpdateTipStatus('partial', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('partiallyKnow')}</button>
                                                <button onClick={() => handleUpdateTipStatus('new', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'new' ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('newToMe')}</button>
                                                <button onClick={() => handleUpdateTipStatus('hard', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'hard' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('tooHard')}</button>
                                                <div className="border-t my-2"></div>
                                                <button onClick={() => handleUpdateTipStatus('skip', selectedTip)} className={`p-2 rounded-md text-left text-sm font-medium transition-colors ${selectedTip.knowledgeStatus === 'skip' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 hover:bg-gray-200'}`}>{t('notWhatLookingFor')}</button>
                                            </div>
                                        </div>
                                        <div className="flex items-center min-h-[60px] gap-4" data-tutorial-target="review-tracking-area">
                                            <div className="flex-shrink-0" data-tutorial-target="review-tracking-area">
                                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('dateLearned')}</div>
                                                <div className={`text-xl font-bold ${
                                                    selectedTip.dateLearned 
                                                        ? 'text-gray-900 dark:text-gray-100' 
                                                        : 'text-gray-500 dark:text-gray-400'
                                                }`}>
                                                    {selectedTip.dateLearned
                                                        ? (() => {
                                                            const date = new Date(selectedTip.dateLearned);
                                                            const today = new Date();
                                                            const diffTime = Math.abs(today - date);
                                                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                                            
                                                            if (diffDays === 0) return t('today');
                                                            if (diffDays === 1) return t('yesterday');
                                                            if (diffDays < 7) return t('daysAgo', { count: diffDays });
                                                            if (diffDays < 30) {
                                                                const weeks = Math.floor(diffDays / 7);
                                                                return t('weeksAgo', { count: weeks });
                                                            }
                                                            if (diffDays < 365) {
                                                                const months = Math.floor(diffDays / 30);
                                                                return t('monthsAgo', { count: months });
                                                            }
                                                            
                                                            const years = Math.floor(diffDays / 365);
                                                            return t('yearsAgo', { count: years });
                                                        })()
                                                        : t('notLearnedYet')}
                                                </div>
                                            </div>
                                            {isTipRead(selectedTip) && (
                                                <div className="flex-1 flex items-center justify-center" data-tutorial-target="review-tracking-area">
                                                    <div className="flex items-center gap-3">
                                                        <button 
                                                            onClick={handleOpenReviewModal}
                                                            disabled={!selectedTip.isScheduledForReview || !selectedTip.hasReviewQuestions}
                                                            title={!selectedTip.isScheduledForReview || !selectedTip.hasReviewQuestions ? t('noReviewQuestionsYet') : ''}
                                                            data-tutorial-target="review-tracking-area"
                                                            className={`relative flex items-center gap-2 px-6 py-3 rounded-lg text-base font-semibold transition-all transform ${
                                                                !selectedTip.isScheduledForReview || !selectedTip.hasReviewQuestions
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : selectedTip.reviewIsDue
                                                                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700 hover:scale-105 animate-pulse-glow'
                                                                        : 'bg-orange-50 text-orange-700 hover:bg-orange-100 hover:scale-105 shadow-sm hover:shadow-md'
                                                            }`}
                                                        >
                                                            {t('reviewQuestions')}
                                                            {selectedTip.reviewIsDue && (
                                                                <span className="absolute -top-1 -right-1 flex h-4 w-4">
                                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                                                                </span>
                                                            )}
                                                        </button>
                                                        {selectedTip.isScheduledForReview && (
                                                            <div 
                                                                data-tutorial-target="review-tracking-area"
                                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                                                                selectedTip.reviewIsDue 
                                                                    ? 'bg-red-100 text-red-700 border border-red-200' 
                                                                    : selectedTip.reviewCount >= REVIEW_SCHEDULE.length
                                                                        ? 'bg-green-100 text-green-700 border border-green-200'
                                                                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                            }`}>
                                                                {selectedTip.reviewIsDue ? (
                                                                    <>
                                                                        <span className="relative flex h-2 w-2">
                                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                                                        </span>
                                                                        {t('reviewDue')}
                                                                    </>
                                                                ) : selectedTip.reviewCount >= REVIEW_SCHEDULE.length ? (
                                                                    <>
                                                                        <CheckCircle size={14} />
                                                                        {t('reviewSequenceCompleted')}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {getReviewStatusText(selectedTip)}
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {selectedTip.isHidden ? (
                                            <div className="border-t pt-6">
                                                <h3 className="font-semibold mb-2 text-gray-600">{t('hiddenTip')}</h3>
                                                <button 
                                                    onClick={handleUnhideTip}
                                                    className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                >
                                                    <Eye size={16} />
                                                    {t('showThisTipAgain')}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="border-t pt-6">
                                                <h3 className="font-semibold mb-2 text-gray-600" data-tutorial-target="tip-actions-area">{t('tipActions')}</h3>
                                                <div className="flex gap-2" data-tutorial-target="tip-actions-area">
                                                    <button 
                                                        onClick={() => handleUpdateTipHidden(true)}
                                                        className="flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    >
                                                        <EyeOff size={16} />
                                                        {t('hideThisTip')}
                                                    </button>
                                                    <button 
                                                        onClick={() => handleUpdateTipFavorite(!selectedTip.isFavorite)}
                                                        className="flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    >
                                                        <Heart size={16} className={selectedTip.isFavorite ? "fill-current text-red-500" : ""} />
                                                        {selectedTip.isFavorite ? t('unfavoriteThisTip') : t('favoriteThisTip')}
                                                    </button>
                                                </div>
                                                {/* Always render a placeholder div for tutorial spotlight */}
                                                <div className="mt-2" data-tutorial-target="tip-actions-area">
                                                {isTipRead(selectedTip) && (
                                                    <div className="flex flex-col gap-2">
                                                            <button 
                                                                onClick={() => handleToggleReviewReminder(selectedTip)}
                                                                className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                            >
                                                                <Clock size={16} className={selectedTip.isScheduledForReview ? "text-yellow-500" : ""} />
                                                                {selectedTip.isScheduledForReview ? t('unscheduleReview') : t('scheduleReview')}
                                                            </button>
                                                            {selectedTip.isScheduledForReview && (
                                                                <button 
                                                                    onClick={() => {
                                                                        setConfirmModalState({
                                                                            isOpen: true,
                                                                            title: t('reviewProgressReset'),
                                                                            message: t('reviewProgressResetMessage'),
                                                                            confirmText: t('resetReviewProgress'),
                                                                            cancelText: t('cancel'),
                                                                            onConfirm: () => {
                                                                                setConfirmModalState(prev => ({ ...prev, isOpen: false }));
                                                                                handleResetReviewProgress(selectedTip.id);
                                                                            },
                                                                            onCancel: () => setConfirmModalState(prev => ({ ...prev, isOpen: false }))
                                                                        });
                                                                    }}
                                                                    className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-red-50 text-red-600 hover:bg-red-100"
                                                                >
                                                                    <Trash2 size={14} />
                                                                    {t('resetReviewProgress')}
                                                                </button>
                                                            )}
                                                        </div>
                                                )}
                                                </div>
                                            </div>
                                        )}
                                        <div className="border-t pt-6">
                                            <h3 className="font-semibold mb-2 text-gray-600">{t('conversation')}</h3>
                                            <div className="space-y-2">
                                                <button 
                                                    onClick={() => startAutomatedConversation(selectedTip.id, selectedTip.knowledgeStatus)} 
                                                    disabled={isTipChatting(selectedTip?.id)}
                                                    className={`w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors ${
                                                        isTipChatting(selectedTip?.id) 
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    <MessageSquarePlus size={16} />
                                                    {t('startNewDeepDive')}
                                                </button>
                                                {/* Development only button for generating review questions */}
                                                {process.env.NODE_ENV !== 'production' && (
                                                    <button 
                                                        onClick={() => handleGenerateReviewQuestion(selectedTip)}
                                                        className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-purple-50 text-purple-700 hover:bg-purple-100 border-2 border-dashed border-purple-300"
                                                        title={t('devGenerateReview')}
                                                    >
                                                        <Sparkles size={16} />
                                                        [DEV] Generate Review Q
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="border-t pt-6">
                                            <button 
                                                onClick={() => handleDeleteTip(selectedTip.id, selectedTip.title)}
                                                className="w-full flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium transition-colors bg-red-50 text-red-600 hover:bg-red-100"
                                            >
                                                <Trash2 size={16} />
                                                {t('deleteConcept')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 text-sm text-gray-500">{t('selectConceptDetails')}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Create a wrapper component to provide tutorial context to App
function AppWrapper() {
    return <App />;
}

// Wrap the App component with TimezoneProvider and TutorialProvider
const AppWithProviders = () => (
    <TimezoneProvider>
        <TutorialProvider>
            <AppWrapper />
        </TutorialProvider>
    </TimezoneProvider>
);

export default AppWithProviders;